import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    TextField,
    Autocomplete,
    Chip,
    CircularProgress,
    Alert,
    Snackbar,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    LinearProgress,
    FormControlLabel,
    Switch,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Stack,
    Checkbox,
    alpha,
    useTheme,
    Stepper,
    Step,
    StepLabel,
    Fade,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Tooltip,
    Popover,
    Tabs,
    Tab,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Search as SearchIcon,
    AutoAwesome as AutoAwesomeIcon,
    CompareArrows as CompareArrowsIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    ArrowBack as ArrowBackIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Description as FileIcon,
    Add as AddIcon,
    FolderOpen as FolderOpenIcon,
    History as HistoryIcon,
    Delete as DeleteIcon,
    Folder as FolderIcon,
    NavigateNext as NextIcon,
    Check as AcceptIcon,
    Close as RejectIcon,
    Edit as CorrectIcon,
    Save as SaveIcon,
    Settings as SettingsIcon,
    FileDownload as FileDownloadIcon,
    StopCircle as StopIcon,
    InfoOutlined as InfoOutlinedIcon,
    ArrowUpward as MoveUpIcon,
    ArrowDownward as MoveDownIcon,
    Insights as InsightsIcon,
} from '@mui/icons-material';

// Components
import MatchingOptionsDialog from '../components/report-matching/MatchingOptionsDialog';
import {
    getLineageReports,
    getLineageReport,
    getTablesBySource,
    getColumnsBySource,
    getProjects,
    createWorkspace,
    getWorkspaces,
    getWorkspace,
    downloadWorkspaceLineage,
    updateWorkspace,
    getLineageFiles,
    getLineageFile,
    getLineageIndexDiagnostics,
    uploadToWorkspace,
    classifyWorkspace,
    getWorkspaceClassificationStatus,
    indexWorkspace,
    getWorkspaceIndexStatus,
    startReportMatchingJob,
    getReportMatchingJob,
    cancelReportMatchingJob,
    deleteReportMatchingJob,
    listReportMatchingJobs,
    saveMatchingResult,
    getWorkspaceMatchings,
    getSavedMatching,
    compareWorkspaceReports,
    deleteWorkspace,
    deleteMatchingResult,
    getSavedRecommendation,
    deleteSavedRecommendation,
    updateSavedRecommendationDeveloperUse,
    startRecommendationJob,
    getRecommendationJob,
    cancelRecommendationJob,
    listRecommendationJobs,
    analyzeGaps,
    getDeduplication,
    getThemesBySystem,
    getGlobalThemesBySystem,
    exportGapAnalysis,
    exportRecommendations,
    saveReportMatchingPreference,
    deleteReportMatchingPreference,
    saveSchemaMappingPreference,
    deleteSchemaMappingPreference,
    getSchemaMappingPreferences,
} from '../services/api';

function parseApiDate(value) {
    if (!value) return null;
    const text = String(value).trim();
    if (!text) return null;
    const normalized = /(?:Z|[+-]\d{2}:\d{2})$/i.test(text) ? text : `${text}Z`;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDisplayDate(value) {
    const parsed = parseApiDate(value);
    return parsed ? parsed.toLocaleString() : 'Unknown';
}

function getRecommendationGapIdentity(item) {
    if (!item) return '';
    const legacyKey = String(item.legacy_key || '').trim();
    if (legacyKey) return legacyKey;
    const legacyTable = String(item.legacy_table || item.legacy_table_physical || item.legacy_table_logical || '').trim().toLowerCase();
    const legacyPhysical = String(item.legacy_physical || '').trim().toLowerCase();
    const legacyColumn = String(item.legacy_column || '').trim().toLowerCase();
    if (legacyTable && legacyPhysical) return `${legacyTable}:${legacyPhysical}`;
    if (legacyTable && legacyColumn) return `${legacyTable}:${legacyColumn}`;
    return [legacyTable, legacyPhysical, legacyColumn, String(item.legacy_expression || '').trim().toLowerCase()].filter(Boolean).join('|');
}

const RECOMMENDATION_STAGE_ORDER = [
    {
        key: 'other_reports_same_theme',
        label: 'Other Reports',
        color: 'info',
        sourceKeys: ['selected_target_report', 'same_theme_reports', 'other_report_same_theme'],
        mode: 'report',
    },
    {
        key: 'schema_mapper',
        label: 'Schema Mapper',
        color: 'success',
        sourceKeys: ['schema_mapper', 'top_contributing_tables', 'direct_search'],
        mode: 'layered',
    },
];

const RECOMMENDATION_SUBSTAGE_ORDER = [
    { key: 'L2', layer: 'L2', label: 'L2' },
    { key: 'L3', layer: 'L3', label: 'L3' },
];

const RECOMMENDATION_JOB_STAGE_ORDER = [
    { key: 'selected_target_report', label: 'Selected Target Report' },
    { key: 'other_reports_same_theme', label: 'Other Reports' },
    { key: 'schema_mapper', label: 'Schema Mapper' },
];

const REPORT_MATCHING_ACTIVE_JOB_POLL_MS = 2500;
const REPORT_MATCHING_WORKSPACE_JOBS_POLL_MS = 5000;
const CATALYST_LAYER_SOURCE_SYSTEMS = {
    L2: 'IDDS_L2',
    L3: 'IDDS_L3',
};

/**
 * Report Matching Page
 */
function ReportMatchingPage() {
    const DEDUP_DEFAULTS = useMemo(() => ({
        legacy: {
            dedup_prefer_physical_by_data_item: true,
            dedup_by_key: false,
            include_table_logical_name: false,
            include_table_physical_name: false,
            include_sql_filter: true,
            include_expression: false,
        },
        catalyst: {
            dedup_prefer_physical_by_data_item: true,
            dedup_by_key: false,
            include_table_logical_name: false,
            include_table_physical_name: false,
            include_sql_filter: true,
            include_expression: false,
        },
    }), []);
    const theme = useTheme();
    const { workspaceId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const autoOpenedHistoryReportRef = useRef('');
    const completedMatchJobSignatureRef = useRef('');
    const autosaveLastFailedSnapshotRef = useRef('');
    const latestGapAnalysisRef = useRef(null);
    const latestMatchSelectionsRef = useRef({});

    // Global State
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [workspaceToDelete, setWorkspaceToDelete] = useState(null);
    const [newWorkspaceName, setNewWorkspaceName] = useState('');
    const [newWorkspaceDesc, setNewWorkspaceDesc] = useState('');
    const [createWorkspaceMode, setCreateWorkspaceMode] = useState('upload_later');
    const [existingIndexedLineageFiles, setExistingIndexedLineageFiles] = useState([]);
    const [selectedExistingLineageFile, setSelectedExistingLineageFile] = useState(null);
    const [workspaceNameDraft, setWorkspaceNameDraft] = useState('');
    const [workspaceDescDraft, setWorkspaceDescDraft] = useState('');
    const [schemaMapperProjects, setSchemaMapperProjects] = useState([]);
    const [selectedSchemaProject, setSelectedSchemaProject] = useState(null);
    const [workspaceSchemaProjectId, setWorkspaceSchemaProjectId] = useState('');
    const [savingWorkspaceSettings, setSavingWorkspaceSettings] = useState(false);

    // Workflow State
    const [activeStep, setActiveStep] = useState(0);

    // Data State
    const [legacyReports, setLegacyReports] = useState([]);
    const [selectedLegacyReport, setSelectedLegacyReport] = useState(null);
    const [queuedLegacyReports, setQueuedLegacyReports] = useState([]);
    const [matchResults, setMatchResults] = useState(null);
    const [activeMatchJob, setActiveMatchJob] = useState(null);
    const [workspaceMatchJobs, setWorkspaceMatchJobs] = useState([]);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [gapAnalysis, setGapAnalysis] = useState(null);
    const [savedMatchings, setSavedMatchings] = useState([]);
    const [recommendations, setRecommendations] = useState(null);
    const [activeRecommendationJob, setActiveRecommendationJob] = useState(null);
    const [savedRecommendationAvailable, setSavedRecommendationAvailable] = useState(false);
    const [selectedRecommendation, setSelectedRecommendation] = useState(null);
    const recommendationSelectionResetKeyRef = useRef('');
    const [manualOverrideDialog, setManualOverrideDialog] = useState({
        open: false,
        gap: null,
        candidate: null,
        layer: 'L2',
        table: null,
        column: null,
        tables: [],
        columns: [],
        loadingTables: false,
        loadingColumns: false,
        saving: false,
        error: '',
    });
    const [manualOverrideTableCache, setManualOverrideTableCache] = useState({});
    const [manualOverrideColumnCache, setManualOverrideColumnCache] = useState({});
    const [recommendationStageSelections, setRecommendationStageSelections] = useState({});
    const [recommendationSubstageSelections, setRecommendationSubstageSelections] = useState({});
    const [recommendationVisibleCounts, setRecommendationVisibleCounts] = useState({});
    const [recommendationTableFilter, setRecommendationTableFilter] = useState('');
    const [recommendationColumnFilter, setRecommendationColumnFilter] = useState('');
    const [recommendationStatusFilter, setRecommendationStatusFilter] = useState('');
    const [recommendationStageFilter, setRecommendationStageFilter] = useState('');
    const recommendationPreferenceBusyRef = useRef(new Set());
    const [recommendationPreferenceBusyKeys, setRecommendationPreferenceBusyKeys] = useState({});
    const [lineageMeta, setLineageMeta] = useState(null);
    const [lineageClassificationState, setLineageClassificationState] = useState(null);
    const [lineageIndexState, setLineageIndexState] = useState(null);
    const [lineageDiagnostics, setLineageDiagnostics] = useState(null);
    const [deduplicationConfig, setDeduplicationConfig] = useState(DEDUP_DEFAULTS);

    // Options
    const [topN, setTopN] = useState(7);
    const [customTopN, setCustomTopN] = useState(7);
    const [standardOtherThemeTopN, setStandardOtherThemeTopN] = useState(7);
    const [finalMatchThreshold, setFinalMatchThreshold] = useState(50);
    const [sameThemeOnly, setSameThemeOnly] = useState(false);
    const [globalThemeOnly, setGlobalThemeOnly] = useState(true);
    const [ignoreCommon, setIgnoreCommon] = useState(true);
    const [ignorePii, setIgnorePii] = useState(true);
    const [matchOnlyLayout, setMatchOnlyLayout] = useState(false);
    const [includeCustom, setIncludeCustom] = useState(true);
    const [isConfigOpen, setIsConfigOpen] = useState(false);
    const [indexing, setIndexing] = useState(false);
    const [classificationMode, setClassificationMode] = useState('local');
    const [classificationReports, setClassificationReports] = useState({ legacy: [], catalyst: [] });
    const [classificationLoading, setClassificationLoading] = useState(false);
    const [classificationDialogOpen, setClassificationDialogOpen] = useState(false);
    const [classificationDialogSystem, setClassificationDialogSystem] = useState('legacy');
    const [classificationDialogReport, setClassificationDialogReport] = useState('');
    const [classificationDialogColumns, setClassificationDialogColumns] = useState([]);
    const [classificationDialogLoading, setClassificationDialogLoading] = useState(false);
    const [localThemeKeywords, setLocalThemeKeywords] = useState({ legacy: null, catalyst: null });
    const [globalThemeKeywords, setGlobalThemeKeywords] = useState({ legacy: null, catalyst: null });
    const [selectedKeywordTheme, setSelectedKeywordTheme] = useState('');
    const [compareDialogOpen, setCompareDialogOpen] = useState(false);
    const [compareLegacyReport, setCompareLegacyReport] = useState(null);
    const [compareCatalystReport, setCompareCatalystReport] = useState(null);
    const [compareReportData, setCompareReportData] = useState(null);
    const [compareReportLoading, setCompareReportLoading] = useState(false);
    const [compareViewTab, setCompareViewTab] = useState('inventory');
    const [gapAnalysisInfoDialogOpen, setGapAnalysisInfoDialogOpen] = useState(false);
    const [matchSectionSearch, setMatchSectionSearch] = useState('');
    const [showLegacyNonLayoutFields, setShowLegacyNonLayoutFields] = useState(false);

    const resolvedThemeMode = matchResults?.theme_mode
        || (globalThemeOnly ? 'global' : (sameThemeOnly ? 'local' : 'none'));
    const legacyHasSelectedThemes = Boolean(
        matchResults && (
            resolvedThemeMode === 'global'
                ? (matchResults.legacy_global_themes || []).length
                : resolvedThemeMode === 'local'
                    ? (matchResults.legacy_themes || []).length
                    : false
        )
    );

    const classificationStats = useMemo(() => {
        const themeField = classificationMode === 'global' ? 'global_themes' : 'themes';
        const buildStats = (reports = []) => {
            const byTheme = {};
            const unclassified = [];
            reports.forEach((report) => {
                const themes = Array.isArray(report?.[themeField]) ? report[themeField].filter(Boolean) : [];
                if (!themes.length) {
                    unclassified.push(report?.report_name || 'Unknown');
                    return;
                }
                themes.forEach((themeName) => {
                    if (!themeName) return;
                    byTheme[themeName] = (byTheme[themeName] || 0) + 1;
                });
            });
            const themeEntries = Object.entries(byTheme)
                .map(([themeName, count]) => ({ themeName, count }))
                .sort((a, b) => (b.count - a.count) || a.themeName.localeCompare(b.themeName));
            return {
                total: reports.length,
                classifiedCount: reports.length - unclassified.length,
                unclassified,
                themeEntries
            };
        };

        return {
            legacy: buildStats(classificationReports.legacy),
            catalyst: buildStats(classificationReports.catalyst)
        };
    }, [classificationReports, classificationMode]);

    const canViewClassification = Boolean(selectedWorkspace?.is_classified);
    const canStartMatching = Boolean(selectedWorkspace?.is_classified && lineageMeta?.is_indexed);
    const hasWorkspaceSettingChanges = Boolean(
        selectedWorkspace
        && (
            (workspaceNameDraft.trim() || '') !== String(selectedWorkspace.name || '')
            || workspaceDescDraft.trim() !== String(selectedWorkspace.description || '')
            || (workspaceSchemaProjectId || '') !== (selectedWorkspace.schema_project_id || '')
        )
    );

    const normalizeDedupSystemSettings = (settings, defaults) => {
        const normalized = { ...defaults, ...(settings || {}) };
        if (
            Object.prototype.hasOwnProperty.call(settings || {}, 'dedup_by_key')
            && !Object.prototype.hasOwnProperty.call(settings || {}, 'dedup_prefer_physical_by_data_item')
        ) {
            normalized.dedup_prefer_physical_by_data_item = false;
        }
        if (normalized.dedup_prefer_physical_by_data_item) {
            normalized.dedup_by_key = false;
        } else if (normalized.dedup_by_key) {
            normalized.dedup_prefer_physical_by_data_item = false;
        }
        return normalized;
    };

    useEffect(() => {
        let active = true;
        getDeduplication()
            .then((data) => {
                if (!active || !data) return;
                setDeduplicationConfig({
                    legacy: normalizeDedupSystemSettings(data.legacy, DEDUP_DEFAULTS.legacy),
                    catalyst: normalizeDedupSystemSettings(data.catalyst, DEDUP_DEFAULTS.catalyst),
                });
            })
            .catch(() => {
                if (!active) return;
                setDeduplicationConfig(DEDUP_DEFAULTS);
            });
        return () => {
            active = false;
        };
    }, [DEDUP_DEFAULTS]);

    const buildThemeKeywordMap = (themesConfig) => {
        const result = {};
        if (!themesConfig || typeof themesConfig !== 'object') return result;
        Object.entries(themesConfig).forEach(([themeName, themeDef]) => {
            const keywords = new Set();
            (themeDef?.keywords || []).forEach((kw) => {
                if (kw) keywords.add(kw);
            });
            Object.values(themeDef?.sub_themes || {}).forEach((sub) => {
                (sub?.keywords || []).forEach((kw) => {
                    if (kw) keywords.add(kw);
                });
            });
            result[themeName] = Array.from(keywords);
        });
        return result;
    };

    const getKeywordMapForMode = () => {
        const map = classificationMode === 'global' ? globalThemeKeywords : localThemeKeywords;
        return map[classificationDialogSystem] || {};
    };

    // UI State
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [downloadingLineage, setDownloadingLineage] = useState(false);
    const [incrementalUploadEnabled, setIncrementalUploadEnabled] = useState(true);
    const [classifying, setClassifying] = useState(false);
    const [matching, setMatching] = useState(false);
    const [matchingProgress, setMatchingProgress] = useState('');
    const [decidedGaps, setDecidedGaps] = useState({}); // { legacyKey: 'accepted' | 'rejected' | 'corrected' }
    const [matchSelections, setMatchSelections] = useState({}); // { legacyKey: catalyst_logical||catalyst_physical }
    const [saving, setSaving] = useState(false);
    const [workspaceDecisionDirty, setWorkspaceDecisionDirty] = useState(false);
    const [recommending, setRecommending] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [runningLlm, setRunningLlm] = useState(false);
    const [queueSubmitting, setQueueSubmitting] = useState(false);
    const [stoppingMatchJobIds, setStoppingMatchJobIds] = useState({});
    const [stoppingRecommendationJobIds, setStoppingRecommendationJobIds] = useState({});
    const [autoOpenQueuedResult, setAutoOpenQueuedResult] = useState(true);
    const [llmReasoningPopover, setLlmReasoningPopover] = useState({ anchorEl: null, content: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [missingLogicalSystem, setMissingLogicalSystem] = useState('catalyst');
    const [breakdownExpanded, setBreakdownExpanded] = useState(false);

    useEffect(() => {
        const nextResetKey = recommendations
            ? [
                recommendations?.workspace_id || selectedWorkspace?.id || '',
                recommendations?.legacy_report || '',
                recommendations?.catalyst_report || '',
                recommendations?.generated_at || recommendations?.updated_at || recommendations?.created_at || '',
                (recommendations?.recommendations || []).map((rec) => getRecommendationGapIdentity(rec)).join('|'),
            ].join('||')
            : '';

        if (recommendationSelectionResetKeyRef.current === nextResetKey) {
            return;
        }
        recommendationSelectionResetKeyRef.current = nextResetKey;
        setRecommendationStageSelections({});
        setRecommendationSubstageSelections({});
        setRecommendationVisibleCounts({});
        setRecommendationTableFilter('');
        setRecommendationColumnFilter('');
        setRecommendationStatusFilter('');
        setRecommendationStageFilter('');
    }, [recommendations, selectedWorkspace?.id]);

    useEffect(() => {
        setMatchSectionSearch('');
    }, [matchResults?.legacy_report]);

    useEffect(() => {
        setIncrementalUploadEnabled(Boolean(selectedWorkspace?.lineage_file_id));
    }, [selectedWorkspace?.lineage_file_id]);

    const isMatchJobActive = (job) => ['queued', 'running'].includes(job?.status);
    const isRecommendationJobActive = (job) => ['queued', 'running'].includes(job?.status);
    const isMatchJobCancelable = (job) => ['queued', 'running'].includes(job?.status);
    const isRecommendationJobCancelable = (job) => ['queued', 'running'].includes(job?.status);
    const isMatchJobDeletable = (job) => ['failed', 'interrupted', 'cancelled'].includes(job?.status);
    const getRecommendationUiStageKey = (stageKey) => (
        String(stageKey || '').trim() === 'top_contributing_tables' ? 'schema_mapper' : String(stageKey || '').trim()
    );
    const getRecommendationJobStageIndex = (job) => {
        const currentStage = getRecommendationUiStageKey(job?.current_stage);
        const index = RECOMMENDATION_JOB_STAGE_ORDER.findIndex((stage) => stage.key === currentStage);
        if (index >= 0) return index;
        if (job?.status === 'completed') return RECOMMENDATION_JOB_STAGE_ORDER.length - 1;
        return 0;
    };
    const getRecommendationJobStageLabel = (job) => (
        getRecommendationUiStageKey(job?.current_stage) === 'schema_mapper' && String(job?.current_stage || '').trim() === 'top_contributing_tables'
            ? 'Schema Mapper'
            : job?.current_stage_label
        || RECOMMENDATION_JOB_STAGE_ORDER[getRecommendationJobStageIndex(job)]?.label
        || 'Queued'
    );
    const getRecommendationJobPhaseLabel = (job) => (
        job?.current_phase_label
        || (job?.status === 'completed' ? 'Completed' : '')
    );
    const getRecommendationJobGapProgressLabel = (job) => {
        const currentGap = Number(job?.current_gap_index || 0);
        const totalGaps = Number(job?.total_gaps || 0);
        if (!totalGaps) return '';
        const safeCurrent = Math.min(Math.max(currentGap, 0), totalGaps);
        return `Processed ${safeCurrent || 0} of ${totalGaps} fields`;
    };
    const formatRecommendationJobTimestamp = (value) => parseApiDate(value)?.toLocaleString() || '-';
    const getRecommendationStartedAt = (job, savedRecommendation) => (
        job?.started_at
        || (
            job?.legacy_report_name === savedRecommendation?.legacy_report
            && job?.catalyst_report_name === savedRecommendation?.catalyst_report
            ? savedRecommendation?.started_at
            : null
        )
        || savedRecommendation?.started_at
        || null
    );
    const getRecommendationCompletedAt = (job, savedRecommendation) => (
        job?.completed_at
        || (
            job?.legacy_report_name === savedRecommendation?.legacy_report
            && job?.catalyst_report_name === savedRecommendation?.catalyst_report
            ? savedRecommendation?.completed_at
            : null
        )
        || savedRecommendation?.completed_at
        || savedRecommendation?.saved_at
        || null
    );

    const getMatchJobStatusColor = (status) => {
        if (status === 'completed') return 'success';
        if (status === 'failed' || status === 'interrupted') return 'error';
        if (status === 'cancelled') return 'warning';
        if (status === 'running') return 'info';
        return 'warning';
    };

    const getMatchJobReasonColor = (status) => {
        if (status === 'completed') return 'text.secondary';
        if (status === 'cancelled') return 'warning.main';
        return 'error.main';
    };

    const getMatchJobReason = (job) => (
        (
            job?.status === 'running'
            && String(job?.status_reason || '').trim() === 'queued_for_processing'
                ? ''
                : job?.status_detail
        )
        || job?.error
        || job?.progress_message
        || 'Waiting for status...'
    );

    const shouldShowMatchJobReason = (job) => {
        const reason = String(getMatchJobReason(job) || '').trim();
        const progressMessage = String(job?.progress_message || '').trim();
        if (!reason || reason === progressMessage) return false;
        return Boolean(job?.status_detail || job?.error);
    };

    const selectPrimaryMatchJob = (jobs, selectedReportName = '') => {
        const list = Array.isArray(jobs) ? jobs : [];
        const selectedName = String(selectedReportName || '').trim();
        const runningJob = list.find((job) => job?.status === 'running');
        if (runningJob) return runningJob;
        const selectedActive = list.find((job) => (
            job?.legacy_report_name === selectedName && isMatchJobActive(job)
        ));
        if (selectedActive) return selectedActive;
        const queuedJob = list.find((job) => job?.status === 'queued');
        if (queuedJob) return queuedJob;
        if (selectedName) {
            return list.find((job) => job?.legacy_report_name === selectedName) || null;
        }
        return list[0] || null;
    };

    const getMatchJobProgressValue = (job, key) => {
        const value = Number(job?.progress?.[key]);
        return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
    };

    const getMatchJobProgressCountLabel = (job) => {
        const progress = job?.progress || {};
        const processed = Number(progress.processed);
        const total = Number(progress.total);
        if (Number.isFinite(processed) && Number.isFinite(total) && total > 0) {
            return `${Math.min(Math.max(processed, 0), total)} of ${total}`;
        }
        return '';
    };

    const getMatchJobPhaseCountLabel = (job) => {
        const progress = job?.progress || {};
        const processed = Number(progress.phase_processed);
        const total = Number(progress.phase_total);
        if (Number.isFinite(processed) && Number.isFinite(total) && total > 0) {
            return `${Math.min(Math.max(processed, 0), total)} of ${total}`;
        }
        return '';
    };

    const renderMatchJobProgress = (job, { compact = false } = {}) => {
        if (!job || !isMatchJobActive(job)) return null;
        const progress = job.progress || {};
        const overallPercent = getMatchJobProgressValue(job, 'overall_percent');
        const stageCountLabel = getMatchJobProgressCountLabel(job);
        const phaseCountLabel = getMatchJobPhaseCountLabel(job);
        const currentReport = String(progress.current_report || '').trim();
        const bucket = String(progress.bucket || '').trim();
        const reportCountLabel = stageCountLabel && Number(progress.total || 0) > 1 ? stageCountLabel : '';
        const overallLabel = overallPercent !== null ? `${Math.round(overallPercent)}%` : '';

        if (compact) {
            return (
                <Box sx={{ mt: 0.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {bucket || currentReport || 'Matching in progress'}
                        </Typography>
                        {overallPercent !== null && (
                            <Typography variant="caption" color="text.secondary">
                                {Math.round(overallPercent)}%
                            </Typography>
                        )}
                    </Stack>
                    <LinearProgress
                        variant={overallPercent !== null ? 'determinate' : 'indeterminate'}
                        value={overallPercent !== null ? overallPercent : undefined}
                        sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
                    />
                </Box>
            );
        }

        return (
            <Box sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                        Live matching progress
                    </Typography>
                    {overallPercent !== null && (
                        <Typography variant="caption" color="text.secondary">
                            {Math.round(overallPercent)}%
                        </Typography>
                    )}
                </Stack>
                <LinearProgress
                    variant={overallPercent !== null ? 'determinate' : 'indeterminate'}
                    value={overallPercent !== null ? overallPercent : undefined}
                    sx={{ height: 8, borderRadius: 1, mt: 0.75 }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {bucket && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Bucket: ${bucket}`}
                        />
                    )}
                    {overallLabel && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Overall ${overallLabel}${reportCountLabel ? ` (${reportCountLabel})` : ''}`}
                        />
                    )}
                    {currentReport && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Current: ${currentReport}`}
                            sx={{
                                maxWidth: '100%',
                                '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        />
                    )}
                    {phaseCountLabel && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Current step: ${phaseCountLabel}`}
                        />
                    )}
                </Stack>
            </Box>
        );
    };

    const latestWorkspaceJobs = useMemo(() => {
        const seen = new Set();
        return (workspaceMatchJobs || []).filter((job) => {
            const reportName = job?.legacy_report_name || '';
            if (!reportName || seen.has(reportName)) {
                return false;
            }
            seen.add(reportName);
            return true;
        });
    }, [workspaceMatchJobs]);

    const latestWorkspaceJobByReport = useMemo(() => {
        const entries = latestWorkspaceJobs.map((job) => [job.legacy_report_name, job]);
        return new Map(entries);
    }, [latestWorkspaceJobs]);

    const hasActiveWorkspaceMatchJobs = useMemo(
        () => (workspaceMatchJobs || []).some((job) => isMatchJobActive(job)),
        [workspaceMatchJobs]
    );

    const savedMatchingByReport = useMemo(() => {
        const entries = (savedMatchings || []).map((item) => [item.legacy_report_name, item]);
        return new Map(entries);
    }, [savedMatchings]);

    const isReportOutputReady = (reportName) => savedMatchingByReport.has(reportName);

    const pendingQueueReports = useMemo(
        () => (queuedLegacyReports || []).filter((report) => !isReportOutputReady(report?.report_name)),
        [queuedLegacyReports, savedMatchingByReport]
    );

    const workspaceQueueSummary = useMemo(() => {
        const summary = {
            queued: 0,
            running: 0,
            completed: 0,
            failed: 0,
            ready: 0,
            processed: 0,
        };

        latestWorkspaceJobs.forEach((job) => {
            if (job.status === 'queued') summary.queued += 1;
            else if (job.status === 'running') summary.running += 1;
            else if (job.status === 'completed') summary.completed += 1;
            else if (job.status === 'failed' || job.status === 'interrupted' || job.status === 'cancelled') summary.failed += 1;
        });

        summary.processed = summary.completed + summary.failed;
        summary.ready = (savedMatchings || []).length;
        return summary;
    }, [latestWorkspaceJobs, savedMatchings]);

    const applyMatchJobState = (job) => {
        setActiveMatchJob(job || null);
        setMatching(isMatchJobActive(job));
        setMatchingProgress(job?.progress_message || '');
    };

    const applyRecommendationJobState = (job) => {
        setActiveRecommendationJob(job || null);
        setRecommending(isRecommendationJobActive(job));
    };

    const getRecommendationQueueStartMessage = (job, actionLabel = 'Recommendation generation') => {
        if (!job) {
            return `${actionLabel} queued`;
        }
        if (job.status === 'completed') {
            return `${actionLabel} complete`;
        }
        const ahead = Number(job.queue_ahead_count || 0);
        if (ahead > 0) {
            return `${actionLabel} queued. ${ahead} recommendation job(s) already ahead in the workspace queue.`;
        }
        if (job.status === 'queued') {
            return `${actionLabel} queued. Starting recommendation generation...`;
        }
        return `${actionLabel} started in background`;
    };

    const refreshSavedRecommendationAvailability = async (workspaceIdValue, legacyReportName, catalystReportName) => {
        if (!workspaceIdValue || !legacyReportName || !catalystReportName) {
            setSavedRecommendationAvailable(false);
            return false;
        }
        try {
            await getSavedRecommendation(workspaceIdValue, legacyReportName, catalystReportName);
            setSavedRecommendationAvailable(true);
            return true;
        } catch (err) {
            setSavedRecommendationAvailable(false);
            return false;
        }
    };

    const isLineageClassificationActive = (state) => ['running', 'queued'].includes(state?.status);

    const openLlmReasoningPopover = (event, content) => {
        setLlmReasoningPopover({
            anchorEl: event.currentTarget,
            content: content || 'No LLM reasoning available.',
        });
    };

    const closeLlmReasoningPopover = () => {
        setLlmReasoningPopover({ anchorEl: null, content: '' });
    };

    const deriveLineageClassificationState = (meta, workspaceIdValue = selectedWorkspace?.id) => {
        if (!meta) return null;
        if (meta.classification_state) {
            return meta.classification_state;
        }
        const totalReports = (meta.legacy_report_count || 0) + (meta.catalyst_report_count || 0);
        return {
            workspace_id: workspaceIdValue || '',
            file_id: meta.file_id,
            status: meta.is_classified ? 'completed' : 'not_started',
            message: meta.is_classified ? 'Classification complete' : '',
            completed_reports: meta.is_classified ? totalReports : 0,
            total_reports: totalReports,
            progress: meta.is_classified && totalReports ? 100 : 0,
            current_system: null,
            error: null,
            started_at: meta.classified_at || null,
            updated_at: meta.classified_at || null,
            completed_at: meta.classified_at || null,
        };
    };

    const applyLineageClassificationState = (state) => {
        setLineageClassificationState(state || null);
        setClassifying(isLineageClassificationActive(state));
    };

    const isLineageIndexActive = (state) => ['running', 'queued'].includes(state?.status);

    const deriveLineageIndexState = (meta, workspaceIdValue = selectedWorkspace?.id) => {
        if (!meta) return null;
        if (meta.indexing_state) {
            return meta.indexing_state;
        }
        const totalDocs = meta.catalyst_index_total_count || 0;
        return {
            workspace_id: workspaceIdValue || '',
            file_id: meta.file_id,
            status: meta.is_indexed ? 'completed' : 'not_started',
            message: meta.is_indexed ? 'Indexing complete' : '',
            completed_docs: meta.is_indexed ? totalDocs : 0,
            total_docs: totalDocs,
            progress: meta.is_indexed && totalDocs ? 100 : 0,
            current_batch: 0,
            total_batches: 0,
            resume_from_batch: 1,
            batch_size: 0,
            error: null,
            started_at: meta.indexed_at || null,
            updated_at: meta.indexed_at || null,
            completed_at: meta.indexed_at || null,
        };
    };

    const applyLineageIndexState = (state) => {
        setLineageIndexState(state || null);
        setIndexing(isLineageIndexActive(state));
    };

    const steps = [
        { label: 'Upload Lineage' },
        { label: 'Classification' },
        { label: 'Queue Reports' },
        { label: 'View Matches' },
        { label: 'Gap Analysis' },
        { label: 'Recommendation' }
    ];

    const tableCellWrapSx = {
        maxWidth: 260,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
    };

    const chipTruncateSx = {
        maxWidth: 340,
        width: 'fit-content',
        '& .MuiChip-label': {
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
        }
    };

    const compactChipSx = {
        ...chipTruncateSx,
        height: 22,
        fontSize: '0.7rem',
        justifyContent: 'center',
        '& .MuiChip-label': {
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingLeft: 8,
            paddingRight: 8,
            textAlign: 'center',
        },
    };

    const getMatchTypeCategory = (matchType) => {
        const normalized = String(matchType || '').toLowerCase();
        if (normalized === 'llm_recovery') return 'llm_recovery';
        if (normalized === 'manual') return 'manual';
        if (normalized.startsWith('logical')) return 'logical';
        if (normalized.startsWith('physical')) return 'physical';
        return normalized || 'physical';
    };

    const getMatchTypeLabel = (matchType) => {
        const category = getMatchTypeCategory(matchType);
        if (category === 'llm_recovery') return 'LLM Recovered';
        if (category === 'logical') return 'Logical';
        if (category === 'manual') return 'Manual';
        if (category === 'physical') return 'Physical';
        return matchType || 'Unknown';
    };

    const getMatchTypeColor = (matchType) => {
        const category = getMatchTypeCategory(matchType);
        if (category === 'llm_recovery') return 'success';
        if (category === 'logical') return 'info';
        if (category === 'manual') return 'primary';
        if (category === 'physical') return 'secondary';
        return 'secondary';
    };

    const renderDecisionStatusChip = (decision) => {
        if (decision === 'accepted') {
            return (
                <Chip
                    icon={<AcceptIcon sx={{ fontSize: '0.85rem !important' }} />}
                    label="Accepted"
                    size="small"
                    color="success"
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                />
            );
        }
        if (decision === 'corrected') {
            return (
                <Chip
                    icon={<CorrectIcon sx={{ fontSize: '0.85rem !important' }} />}
                    label="Manual"
                    size="small"
                    color="info"
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                />
            );
        }
        if (decision === 'rejected') {
            return (
                <Chip
                    icon={<RejectIcon sx={{ fontSize: '0.85rem !important' }} />}
                    label="Rejected"
                    size="small"
                    color="error"
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                />
            );
        }
        return null;
    };

    const getMatchCandidateKey = (candidate) => {
        if (!candidate) return '';
        return `${candidate.catalyst_logical || ''}||${candidate.catalyst_physical || ''}||${candidate.catalyst_table || ''}`;
    };

    const isRejectedPreference = (item) => (
        String(item?.preference_status || '').trim().toLowerCase() === 'rejected'
    );

    const isAcceptedPreference = (item) => (
        String(item?.preference_status || '').trim().toLowerCase() === 'accepted'
    );

    const getRecommendationPreferenceVisual = (candidate) => {
        if (isAcceptedPreference(candidate)) {
            return {
                label: 'Accepted preference',
                color: theme.palette.success.dark,
                borderColor: alpha(theme.palette.success.main, 0.55),
                backgroundColor: alpha(theme.palette.success.main, 0.055),
                fontWeight: 800,
            };
        }
        if (isRejectedPreference(candidate)) {
            return {
                label: 'Rejected preference',
                color: theme.palette.error.dark,
                borderColor: alpha(theme.palette.error.main, 0.55),
                backgroundColor: alpha(theme.palette.error.main, 0.055),
                fontWeight: 800,
            };
        }
        return null;
    };

    const getGapUserPreferenceLabel = (gap, decision) => (
        decision === 'rejected' || isRejectedPreference(gap?.candidate) || isRejectedPreference(gap)
            ? 'Rejected'
            : 'NA'
    );

    const buildMatchCandidateFromRow = (row) => ({
        catalyst_logical: row?.catalyst_column || '',
        catalyst_physical: row?.catalyst_physical || '',
        catalyst_table: row?.catalyst_table || '',
        score: row?.score,
        match_type: row?.match_type,
        report_layout: row?.catalyst_report_layout ?? row?.report_layout ?? false,
        llm_reasoning: row?.llm_reasoning || '',
        llm_confidence: row?.llm_confidence ?? row?.score ?? 0,
        preference_status: row?.preference_status || '',
        preference_source: row?.preference_source || '',
        preference_notes: row?.preference_notes || '',
        preference_id: row?.preference_id || '',
    });

    const mergeMatchCandidates = (row, updatedCandidate = null) => {
        const candidates = [...(Array.isArray(row?.candidates) ? row.candidates : [])];
        const primaryCandidate = buildMatchCandidateFromRow(row);
        const merged = new Map();

        [primaryCandidate, ...candidates, ...(updatedCandidate ? [updatedCandidate] : [])].forEach((candidate) => {
            if (!candidate || typeof candidate !== 'object') return;
            const candidateKey = getMatchCandidateKey(candidate);
            if (!candidateKey) return;
            merged.set(candidateKey, {
                ...(merged.get(candidateKey) || {}),
                ...candidate,
            });
        });

        return Array.from(merged.values());
    };

    const selectPreferredMatchCandidate = (candidates = [], preferredCandidateKey = '') => {
        if (!Array.isArray(candidates) || candidates.length === 0) return null;
        if (preferredCandidateKey) {
            const preferredCandidate = candidates.find((candidate) => getMatchCandidateKey(candidate) === preferredCandidateKey);
            if (preferredCandidate) return preferredCandidate;
        }
        const acceptedCandidate = candidates.find((candidate) => isAcceptedPreference(candidate));
        if (acceptedCandidate) return acceptedCandidate;
        const neutralCandidate = candidates.find((candidate) => !isRejectedPreference(candidate));
        return neutralCandidate || candidates[0] || null;
    };

    const syncMatchedRowWithCandidate = (row, candidate, candidates) => {
        if (!candidate) return row;
        return {
            ...row,
            catalyst_column: candidate.catalyst_logical || row.catalyst_column,
            catalyst_physical: candidate.catalyst_physical || row.catalyst_physical,
            catalyst_table: candidate.catalyst_table || row.catalyst_table,
            score: candidate.score ?? row.score,
            match_type: candidate.match_type || row.match_type,
            catalyst_report_layout: candidate.report_layout ?? row.catalyst_report_layout,
            preference_status: candidate.preference_status || '',
            preference_source: candidate.preference_source || '',
            preference_notes: candidate.preference_notes || '',
            preference_id: candidate.preference_id || '',
            candidates,
        };
    };

    const formatPhysicalNameWithTable = (physicalName, tableName) => {
        const physical = String(physicalName || '').trim();
        const table = String(tableName || '').trim();
        if (!physical) return '';
        if (physical.includes('.')) return physical;
        return table ? `${table}.${physical}` : physical;
    };

    const getRecommendationLegacyTableName = (rec) => (
        String(rec?.legacy_table || rec?.legacy_table_physical || rec?.legacy_table_logical || '').trim()
    );

    const getRecommendationLegacyPhysicalDisplayName = (rec) => {
        const table = getRecommendationLegacyTableName(rec);
        const physical = formatPhysicalNameWithTable(rec?.legacy_physical, table);
        return physical || (table ? `${table}.no_physical_name` : 'No physical name');
    };

    const getGapPhysicalName = (gap) => (
        formatPhysicalNameWithTable(
            gap?.legacy_physical || gap?.physical || gap?.physical_name,
            gap?.legacy_table_physical || gap?.legacy_table || gap?.legacy_table_logical || gap?.table_name
        ) || 'NA'
    );

    const getLegacyPhysicalName = (field) => (
        formatPhysicalNameWithTable(
            field?.legacy_physical || field?.physical || field?.physical_name,
            field?.legacy_table_physical || field?.legacy_table || field?.legacy_table_logical || field?.table_name
        ) || 'NA'
    );

    const formatColumnNameWithTable = (columnName, tableName) => {
        const column = String(columnName || '').trim();
        const table = String(tableName || '').trim();
        if (!column) return table || '';
        if (column.includes('.')) return column;
        return table ? `${table}.${column}` : column;
    };

    const getLegacyLogicalDisplayName = (logicalName, physicalName) => {
        const logical = String(logicalName || '').trim();
        const physical = String(physicalName || '').trim();
        return logical || physical || '-';
    };

    const getMatchCandidatePhysicalLabel = (candidate) => (
        formatPhysicalNameWithTable(candidate?.catalyst_physical, candidate?.catalyst_table)
    );

    const firstNonEmptyRecommendationValue = (...values) => {
        for (const value of values) {
            const text = String(value || '').trim();
            if (text) return text;
        }
        return '';
    };

    const getRecommendationCandidateSourceMeta = (candidate) => {
        if (!Array.isArray(candidate?.schema_sources)) return {};
        return candidate.schema_sources.find((item) => item && typeof item === 'object') || {};
    };

    const isOtherReportRecommendationCandidate = (candidate) => (
        String(candidate?.source || '').trim().toLowerCase().startsWith('other_report')
        || String(candidate?.catalyst_layer || '').trim().toLowerCase() === 'report'
    );

    const getRecommendationCandidateReportLabel = (candidate) => {
        if (isOtherReportRecommendationCandidate(candidate)) {
            return String(candidate?.catalyst_report || candidate?.catalyst_table || '').trim();
        }
        return '';
    };

    const getRecommendationCandidateFieldLabel = (candidate) => {
        const tableName = String(candidate?.catalyst_table || '').trim();
        const columnName = String(candidate?.catalyst_column || candidate?.catalyst_logical || '').trim();
        const attributeName = String(candidate?.catalyst_attribute || '').trim();
        const physicalName = String(candidate?.catalyst_physical || '').trim();

        if (isOtherReportRecommendationCandidate(candidate)) {
            return columnName || attributeName || physicalName || '-';
        }

        return attributeName || columnName || physicalName || '-';
    };

    const getRecommendationCandidatePrimaryLabel = (candidate) => (
        getRecommendationCandidateFieldLabel(candidate)
    );

    const getRecommendationCandidateResolvedTable = (candidate) => {
        const sourceMeta = getRecommendationCandidateSourceMeta(candidate);
        const candidateTable = String(candidate?.catalyst_table || '').trim();

        if (isOtherReportRecommendationCandidate(candidate)) {
            const reportName = String(candidate?.catalyst_report || '').trim();
            const tableFromCandidate = candidateTable && candidateTable.toLowerCase() !== reportName.toLowerCase()
                ? candidateTable
                : '';
            return firstNonEmptyRecommendationValue(
                candidate?.source_table_physical,
                candidate?.source_table,
                candidate?.schema_table,
                candidate?.catalyst_table_physical,
                candidate?.catalyst_table_logical,
                tableFromCandidate,
                sourceMeta?.table_name,
                sourceMeta?.target_table
            );
        }

        return firstNonEmptyRecommendationValue(
            candidate?.catalyst_table_physical,
            candidate?.catalyst_table,
            candidate?.target_table,
            candidate?.table_name,
            candidate?.schema_table,
            sourceMeta?.table_name,
            sourceMeta?.target_table
        );
    };

    const getRecommendationCandidateResolvedPhysical = (candidate) => {
        const sourceMeta = getRecommendationCandidateSourceMeta(candidate);
        return firstNonEmptyRecommendationValue(
            candidate?.catalyst_physical,
            candidate?.target_physical,
            candidate?.physical_name,
            candidate?.column_name,
            sourceMeta?.column_name,
            sourceMeta?.target_column,
            candidate?.catalyst_column,
            candidate?.catalyst_logical,
            candidate?.catalyst_attribute
        );
    };

    const getRecommendationCandidateSecondaryLabel = (candidate) => {
        const physicalName = getRecommendationCandidateResolvedPhysical(candidate);
        const tableName = getRecommendationCandidateResolvedTable(candidate);
        return formatPhysicalNameWithTable(physicalName, tableName) || physicalName || '';
    };

    const getRecommendationCandidateTargetLogicalAliases = (candidate) => ([
        candidate?.catalyst_attribute,
        candidate?.catalyst_logical,
        candidate?.catalyst_column,
        candidate?.target_logical,
        candidate?.attribute_name,
    ].map((value) => String(value || '').trim()).filter(Boolean));

    const getRecommendationCandidateTargetPhysicalAliases = (candidate) => ([
        candidate?.catalyst_physical,
        candidate?.target_physical,
        candidate?.physical_name,
        candidate?.column_name,
        getRecommendationCandidateResolvedPhysical(candidate),
        candidate?.catalyst_column,
        candidate?.catalyst_logical,
        candidate?.catalyst_attribute,
    ].map((value) => String(value || '').trim()).filter(Boolean));

    const getRecommendationCandidateTargetTableAliases = (candidate) => ([
        getRecommendationCandidateResolvedTable(candidate),
        candidate?.catalyst_table,
        candidate?.target_table,
        candidate?.table_name,
        candidate?.schema_table,
    ].map((value) => String(value || '').trim()).filter(Boolean));

    const getRecommendationCandidateTargetLogical = (candidate) => (
        getRecommendationCandidateTargetLogicalAliases(candidate)[0] || ''
    );

    const getRecommendationCandidateTargetPhysical = (candidate) => (
        getRecommendationCandidateTargetPhysicalAliases(candidate)[0] || ''
    );

    const getRecommendationCandidateTargetTable = (candidate) => (
        getRecommendationCandidateTargetTableAliases(candidate)[0] || ''
    );

    const getRecommendationCandidateStrictTargetPhysical = (candidate) => {
        const sourceMeta = getRecommendationCandidateSourceMeta(candidate);
        return firstNonEmptyRecommendationValue(
            candidate?.catalyst_physical,
            candidate?.target_physical,
            candidate?.physical_name,
            candidate?.column_name,
            sourceMeta?.column_name,
            sourceMeta?.target_column
        );
    };

    // Load workspaces only if no specific one selected (for the landing list)
    useEffect(() => {
        loadSchemaProjects();
    }, []);

    useEffect(() => {
        if (!workspaceId) {
            loadWorkspaces();
        } else {
            loadSelectedWorkspace(workspaceId);
        }
    }, [workspaceId]);

    const loadWorkspaces = async () => {
        try {
            setLoading(true);
            const data = await getWorkspaces();
            setWorkspaces(data || []);
        } catch (err) {
            console.error('Failed to load workspaces:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadSelectedWorkspace = async (id) => {
        try {
            setLoading(true);
            const data = await getWorkspace(id);
            setSelectedWorkspace(data);
            setWorkspaceNameDraft(data?.name || '');
            setWorkspaceDescDraft(data?.description || '');
            setWorkspaceSchemaProjectId(data?.schema_project_id || '');
            setQueuedLegacyReports([]);
            const requestedReportName = location.state?.openReportName || new URLSearchParams(location.search).get('report') || '';
            let meta = null;
            if (data.lineage_file_id) {
                meta = await loadLineageMeta(data.lineage_file_id);
            } else {
                setLineageMeta(null);
                applyLineageClassificationState(null);
                applyLineageIndexState(null);
            }

            const isIndexed = Boolean(meta?.is_indexed);

            // Determine step and load relevant data
            if (!data.lineage_file_id || !data.is_classified) {
                setActiveStep(0);
                setClassificationReports({ legacy: [], catalyst: [] });
                setLegacyReports([]);
                setQueuedLegacyReports([]);
                setWorkspaceMatchJobs([]);
                applyMatchJobState(null);
                applyRecommendationJobState(null);
            } else if (!isIndexed) {
                setActiveStep(1);
                await loadClassificationReports(data.lineage_file_id);
                await loadSavedMatchings(data.id);
                await loadWorkspaceMatchJobs(data.id);
            } else {
                await loadClassificationReports(data.lineage_file_id);
                await loadSavedMatchings(data.id);
                await loadWorkspaceMatchJobs(data.id);
                if (requestedReportName) {
                    autoOpenedHistoryReportRef.current = `${data.id}:${requestedReportName}`;
                    await loadSavedMatchingIntoView(data.id, requestedReportName, { silent: true });
                    if (location.state?.openReportName) {
                        navigate(location.pathname, { replace: true, state: {} });
                    }
                } else {
                    setActiveStep(2);
                }
            }
        } catch (err) {
            showNotification('Failed to load workspace', 'error');
            navigate('/report-matching/workspaces');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateWorkspaceSchemaProject = async () => {
        if (!selectedWorkspace?.id) return;

        try {
            setSavingWorkspaceSettings(true);
            const updated = await updateWorkspace(selectedWorkspace.id, {
                name: workspaceNameDraft.trim() || selectedWorkspace.name,
                description: workspaceDescDraft.trim(),
                schema_project_id: workspaceSchemaProjectId || null,
            });
            setSelectedWorkspace(updated);
            setWorkspaceNameDraft(updated?.name || '');
            setWorkspaceDescDraft(updated?.description || '');
            setWorkspaceSchemaProjectId(updated?.schema_project_id || '');
            showNotification('Workspace details updated');
            if (!workspaceId) {
                loadWorkspaces();
            }
        } catch (err) {
            console.error('Failed to update workspace details:', err);
            showNotification('Failed to update workspace details', 'error');
        } finally {
            setSavingWorkspaceSettings(false);
        }
    };

    const loadSavedMatchingIntoView = async (workspaceIdValue, reportName, { silent = false } = {}) => {
        const data = await getSavedMatching(workspaceIdValue, reportName);
        setSelectedLegacyReport({ report_name: data.legacy_report_name });
        setMatchResults(data.match_results);
        setSelectedMatch(null);
        setGapAnalysis(null);
        resetWorkspaceDecisionAutosaveState();
        setSavedRecommendationAvailable(false);
        setRecommendations(null);
        setSelectedRecommendation(null);
        setActiveStep(3);
        if (!silent) {
            showNotification('Matching complete');
        }
        return data;
    };

    const loadSavedRecommendationIntoView = async (
        workspaceIdValue,
        legacyReportName,
        catalystReportName,
        { silent = false, activateRecommendationStep = false } = {}
    ) => {
        const data = await getSavedRecommendation(workspaceIdValue, legacyReportName, catalystReportName);
        setRecommendations(data);
        setSavedRecommendationAvailable(true);
        if (workspaceIdValue) {
            loadSavedMatchings(workspaceIdValue);
        }
        if (activateRecommendationStep) {
            setActiveStep(5);
        }
        if (!silent) {
            showNotification('Recommendations ready');
        }
        return data;
    };

    const loadLineageMeta = async (fileId) => {
        try {
            const data = await getLineageFile(fileId);
            setLineageMeta(data || null);
            applyLineageClassificationState(deriveLineageClassificationState(data));
            applyLineageIndexState(deriveLineageIndexState(data));
            try {
                const diag = await getLineageIndexDiagnostics(fileId);
                setLineageDiagnostics(diag || null);
            } catch (err) {
                console.error('Failed to load lineage diagnostics:', err);
                setLineageDiagnostics(null);
            }
            return data || null;
        } catch (err) {
            console.error('Failed to load lineage metadata:', err);
            setLineageMeta(null);
            applyLineageClassificationState(null);
            applyLineageIndexState(null);
            setLineageDiagnostics(null);
            return null;
        }
    };

    const loadClassificationReports = async (fileId) => {
        if (!fileId) return;
        try {
            setClassificationLoading(true);
            const [legacyResult, catalystResult] = await Promise.allSettled([
                getLineageReports(fileId, 'legacy'),
                getLineageReports(fileId, 'catalyst')
            ]);
            if (legacyResult.status === 'rejected') {
                console.error('Failed to load legacy reports:', legacyResult.reason);
            }
            if (catalystResult.status === 'rejected') {
                console.error('Failed to load catalyst reports:', catalystResult.reason);
            }
            const legacyReportsList = legacyResult.status === 'fulfilled' ? (legacyResult.value?.reports || []) : [];
            const catalystReportsList = catalystResult.status === 'fulfilled' ? (catalystResult.value?.reports || []) : [];
            setClassificationReports({
                legacy: legacyReportsList,
                catalyst: catalystReportsList
            });
            setLegacyReports(legacyReportsList);
        } catch (err) {
            console.error('Failed to load classification reports:', err);
            setClassificationReports({ legacy: [], catalyst: [] });
            setLegacyReports([]);
        } finally {
            setClassificationLoading(false);
        }
    };

    const ensureThemeKeywordsLoaded = async (system) => {
        try {
            if (!localThemeKeywords[system]) {
                const localThemes = await getThemesBySystem(system);
                setLocalThemeKeywords((prev) => ({
                    ...prev,
                    [system]: buildThemeKeywordMap(localThemes?.themes || {})
                }));
            }
            if (!globalThemeKeywords[system]) {
                const globalThemes = await getGlobalThemesBySystem(system);
                setGlobalThemeKeywords((prev) => ({
                    ...prev,
                    [system]: buildThemeKeywordMap(globalThemes?.themes || {})
                }));
            }
        } catch (err) {
            console.error('Failed to load theme keywords:', err);
        }
    };

    const handleOpenUnclassifiedReport = async (system, reportName) => {
        if (!selectedWorkspace?.lineage_file_id) return;
        setClassificationDialogSystem(system);
        setClassificationDialogReport(reportName);
        setClassificationDialogColumns([]);
        setClassificationDialogOpen(true);
        setClassificationDialogLoading(true);
        try {
            await ensureThemeKeywordsLoaded(system);
            const reportData = await getLineageReport(selectedWorkspace.lineage_file_id, system, reportName);
            const seen = new Set();
            const columns = [];
            (reportData?.columns || []).forEach((row) => {
                const logical = row?.['Report - Data Item Name'] || '';
                const physical = row?.['Physical Name'] || row?.['Physical name'] || '';
                const key = `${logical}||${physical}`;
                if (seen.has(key)) return;
                seen.add(key);
                columns.push({ logical, physical });
            });
            columns.sort((a, b) => {
                const aLogical = (a.logical || '').toLowerCase();
                const bLogical = (b.logical || '').toLowerCase();
                if (aLogical !== bLogical) {
                    return aLogical.localeCompare(bLogical);
                }
                const aPhysical = (a.physical || '').toLowerCase();
                const bPhysical = (b.physical || '').toLowerCase();
                return aPhysical.localeCompare(bPhysical);
            });
            setClassificationDialogColumns(columns);
        } catch (err) {
            console.error('Failed to load report details:', err);
        } finally {
            setClassificationDialogLoading(false);
        }
    };

    const normalizeReportColumns = (rows = []) => {
        const seen = new Set();
        const columns = [];
        rows.forEach((row) => {
            const logical = row?.['Report - Data Item Name'] || '';
            const physical = row?.['Physical Name'] || row?.['Physical name'] || '';
            const key = `${logical}||${physical}`;
            if (seen.has(key)) return;
            seen.add(key);
            columns.push({ logical, physical });
        });
        columns.sort((a, b) => {
            const aLogical = (a.logical || '').toLowerCase();
            const bLogical = (b.logical || '').toLowerCase();
            if (aLogical !== bLogical) {
                return aLogical.localeCompare(bLogical);
            }
            const aPhysical = (a.physical || '').toLowerCase();
            const bPhysical = (b.physical || '').toLowerCase();
            return aPhysical.localeCompare(bPhysical);
        });
        return columns;
    };

    const compareLegacyOptions = useMemo(
        () => legacyReports || classificationReports.legacy || [],
        [legacyReports, classificationReports]
    );

    const compareCatalystOptions = useMemo(
        () => classificationReports.catalyst || [],
        [classificationReports]
    );

    const findReportOptionByName = (reports, reportName) => (
        (reports || []).find((item) => item?.report_name === reportName) || null
    );

    const openCompareDialog = () => {
        if (!compareLegacyReport && selectedLegacyReport?.report_name) {
            const defaultLegacy = findReportOptionByName(compareLegacyOptions, selectedLegacyReport.report_name);
            if (defaultLegacy) {
                setCompareLegacyReport(defaultLegacy);
            }
        }
        if (!compareCatalystReport && selectedMatch?.catalyst_report_name) {
            const defaultCatalyst = findReportOptionByName(compareCatalystOptions, selectedMatch.catalyst_report_name);
            if (defaultCatalyst) {
                setCompareCatalystReport(defaultCatalyst);
            }
        }
        setCompareDialogOpen(true);
    };

    const closeCompareDialog = () => {
        setCompareDialogOpen(false);
    };

    const normalizeCompareText = (value) => String(value || '').trim().toLowerCase();

    const compareMatchInsights = useMemo(() => {
        if (!compareReportData?.legacy_report || !compareReportData?.catalyst_report) {
            return null;
        }

        const legacyFields = [
            ...(compareReportData.legacy_report.layout_fields || []),
            ...(compareReportData.legacy_report.non_layout_fields || []),
        ];
        const catalystFields = [
            ...(compareReportData.catalyst_report.layout_fields || []),
            ...(compareReportData.catalyst_report.non_layout_fields || []),
        ];

        const toValueSet = (fields, key) => new Set(
            (fields || [])
                .map((field) => normalizeCompareText(field?.[key]))
                .filter(Boolean)
        );

        const legacyLogical = toValueSet(legacyFields, 'logical_name');
        const catalystLogical = toValueSet(catalystFields, 'logical_name');
        const legacyPhysical = toValueSet(legacyFields, 'column_physical_name');
        const catalystPhysical = toValueSet(catalystFields, 'column_physical_name');

        const logicalExactValues = new Set(
            [...legacyLogical].filter((value) => catalystLogical.has(value))
        );
        const physicalExactValues = new Set(
            [...legacyPhysical].filter((value) => catalystPhysical.has(value))
        );

        const buildUniqueMatchMap = (fields) => {
            const matchMap = new Map();
            (fields || []).forEach((field) => {
                const fieldKey = String(field?.field_key || '').trim();
                if (!fieldKey) return;
                const physicalValue = normalizeCompareText(field?.column_physical_name);
                const logicalValue = normalizeCompareText(field?.logical_name);
                if (physicalValue && physicalExactValues.has(physicalValue)) {
                    matchMap.set(fieldKey, 'physical');
                    return;
                }
                if (logicalValue && logicalExactValues.has(logicalValue)) {
                    matchMap.set(fieldKey, 'logical');
                }
            });
            return matchMap;
        };

        const legacyMatchMap = buildUniqueMatchMap(legacyFields);
        const catalystMatchMap = buildUniqueMatchMap(catalystFields);

        const countMatchType = (matchMap, matchType) => (
            Array.from(matchMap.values()).filter((value) => value === matchType).length
        );

        return {
            logicalExactValues,
            physicalExactValues,
            legacyMatchMap,
            catalystMatchMap,
            logicalExactCount: Math.min(
                countMatchType(legacyMatchMap, 'logical'),
                countMatchType(catalystMatchMap, 'logical'),
            ),
            physicalExactCount: Math.min(
                countMatchType(legacyMatchMap, 'physical'),
                countMatchType(catalystMatchMap, 'physical'),
            ),
            totalUniqueExactOverlap: Math.min(
                countMatchType(legacyMatchMap, 'logical'),
                countMatchType(catalystMatchMap, 'logical'),
            ) + Math.min(
                countMatchType(legacyMatchMap, 'physical'),
                countMatchType(catalystMatchMap, 'physical'),
            ),
        };
    }, [compareReportData]);

    const getCompareRowMatchType = (field, matchInsights, reportCategory) => {
        if (!matchInsights || !field) return '';
        const fieldKey = String(field.field_key || '').trim();
        const matchMap = reportCategory === 'legacy'
            ? matchInsights.legacyMatchMap
            : matchInsights.catalystMatchMap;
        return matchMap?.get(fieldKey) || '';
    };

    const handleLoadReportComparison = async (
        legacyOption = compareLegacyReport,
        catalystOption = compareCatalystReport,
    ) => {
        if (!selectedWorkspace?.id || !legacyOption?.report_name || !catalystOption?.report_name) {
            showNotification('Select both a legacy report and a Catalyst report to compare.', 'warning');
            return;
        }
        setCompareReportLoading(true);
        try {
            const data = await compareWorkspaceReports(
                selectedWorkspace.id,
                legacyOption.report_name,
                catalystOption.report_name,
                {
                    topN,
                    customTopN,
                    standardOtherThemeTopN,
                    finalMatchThreshold,
                    sameThemeOnly,
                    globalThemeOnly,
                    ignoreCommon,
                    ignorePii,
                    includeCustom,
                },
            );
            setCompareReportData(data || null);
            setCompareViewTab('diagnostics');
            setShowLegacyNonLayoutFields(false);
        } catch (err) {
            console.error('Failed to load report comparison:', err);
            setCompareReportData(null);
            showNotification(err?.response?.data?.detail || 'Failed to compare reports', 'error');
        } finally {
            setCompareReportLoading(false);
        }
    };

    const renderCompareThemeChips = (themes = [], color = 'primary') => {
        if (!themes.length) {
            return <Typography variant="body2" color="text.secondary">None</Typography>;
        }
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {themes.map((themeName) => (
                    <Chip
                        key={`${color}-${themeName}`}
                        label={themeName}
                        size="small"
                        color={color}
                        variant="outlined"
                        title={themeName}
                    />
                ))}
            </Box>
        );
    };

    const sortCompareFields = (fields = []) => (
        [...(fields || [])].sort((a, b) => {
            const aLogical = String(a?.logical_name || '').toLowerCase();
            const bLogical = String(b?.logical_name || '').toLowerCase();
            if (aLogical !== bLogical) return aLogical.localeCompare(bLogical);
            const aTable = String(a?.table_physical_name || '').toLowerCase();
            const bTable = String(b?.table_physical_name || '').toLowerCase();
            if (aTable !== bTable) return aTable.localeCompare(bTable);
            return String(a?.column_physical_name || '').toLowerCase().localeCompare(String(b?.column_physical_name || '').toLowerCase());
        })
    );

    const renderCompareFieldTable = (fields = [], emptyLabel, options = {}) => (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: options.maxHeight || 280, borderRadius: 2 }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Logical Name</TableCell>
                        <TableCell>Table Physical</TableCell>
                        <TableCell>Column Physical</TableCell>
                        <TableCell>Definition</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {fields.map((field) => (
                        <TableRow
                            key={field.field_key}
                            sx={() => {
                                const matchType = getCompareRowMatchType(field, options.matchInsights, options.reportCategory);
                                if (matchType === 'physical') {
                                    return {
                                        bgcolor: alpha(theme.palette.success.main, 0.16),
                                        boxShadow: `inset 4px 0 0 ${theme.palette.success.main}`,
                                        '& td': {
                                            fontWeight: 600,
                                            borderBottomColor: alpha(theme.palette.success.main, 0.25),
                                        },
                                    };
                                }
                                if (matchType === 'logical') {
                                    return {
                                        bgcolor: alpha(theme.palette.info.main, 0.14),
                                        boxShadow: `inset 4px 0 0 ${theme.palette.info.main}`,
                                        '& td': {
                                            borderBottomColor: alpha(theme.palette.info.main, 0.22),
                                        },
                                    };
                                }
                                return {};
                            }}
                        >
                            <TableCell sx={tableCellWrapSx}>{field.logical_name || '-'}</TableCell>
                            <TableCell sx={tableCellWrapSx}>{field.table_physical_name || '-'}</TableCell>
                            <TableCell sx={tableCellWrapSx}>{field.column_physical_name || '-'}</TableCell>
                            <TableCell sx={tableCellWrapSx}>{field.definition || '-'}</TableCell>
                        </TableRow>
                    ))}
                    {fields.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderCompareReportPanel = (reportData, title) => {
        if (!reportData) return null;
        const isLegacyReport = reportData.report_category === 'legacy';
        const catalystFields = sortCompareFields([
            ...(reportData.layout_fields || []),
            ...(reportData.non_layout_fields || []),
        ]);
        const legacyVisibleFields = sortCompareFields(
            showLegacyNonLayoutFields
                ? [
                    ...(reportData.layout_fields || []),
                    ...(reportData.non_layout_fields || []),
                ]
                : (reportData.layout_fields || [])
        );
        const accordionSx = {
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            '&::before': { display: 'none' },
        };
        return (
            <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                    <Accordion disableGutters sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
                                <Box>
                                    <Typography variant="overline" color="text.secondary">{title}</Typography>
                                    <Typography variant="h6" fontWeight={700} sx={tableCellWrapSx}>
                                        {reportData.report_name}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip
                                        size="small"
                                        color={reportData.report_category === 'custom' ? 'warning' : 'success'}
                                        variant="outlined"
                                        label={reportData.report_type_label || 'Unknown'}
                                    />
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`${reportData.field_count || 0} fields`}
                                    />
                                    <Chip
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        label={`${compareMatchInsights?.totalUniqueExactOverlap || 0} total exact overlap`}
                                    />
                                    <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        label={`${compareMatchInsights?.logicalExactCount || 0} logical exact`}
                                    />
                                    <Chip
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        label={`${compareMatchInsights?.physicalExactCount || 0} physical exact`}
                                    />
                                    {isLegacyReport && (
                                        <>
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`${reportData.layout_field_count || 0} in layout`}
                                            />
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`${reportData.non_layout_field_count || 0} outside layout`}
                                            />
                                        </>
                                    )}
                                </Stack>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Local Themes</Typography>
                                {renderCompareThemeChips(reportData.themes || [], 'primary')}
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Global Themes</Typography>
                                {renderCompareThemeChips(reportData.global_themes || [], 'secondary')}
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {isLegacyReport ? (
                        <>
                            <Accordion disableGutters sx={accordionSx}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        justifyContent="space-between"
                                        spacing={2}
                                        sx={{ width: '100%', pr: 1 }}
                                    >
                                        <Typography variant="subtitle2" fontWeight={700}>
                                            {showLegacyNonLayoutFields
                                                ? `All Legacy Fields (${reportData.field_count || 0})`
                                                : `Fields Used In Layout (${reportData.layout_field_count || 0})`}
                                        </Typography>
                                        <FormControlLabel
                                            onClick={(event) => event.stopPropagation()}
                                            onFocus={(event) => event.stopPropagation()}
                                            control={(
                                                <Switch
                                                    size="small"
                                                    checked={showLegacyNonLayoutFields}
                                                    onChange={(event) => setShowLegacyNonLayoutFields(event.target.checked)}
                                                />
                                            )}
                                            label={`Show ${reportData.non_layout_field_count || 0} not used in layout`}
                                            sx={{ m: 0 }}
                                        />
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {renderCompareFieldTable(
                                        legacyVisibleFields,
                                        showLegacyNonLayoutFields ? 'No fields found.' : 'No layout-used fields found.',
                                        {
                                            maxHeight: 520,
                                            matchInsights: compareMatchInsights,
                                            reportCategory: 'legacy',
                                        }
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </>
                    ) : (
                        <Accordion disableGutters sx={accordionSx}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2" fontWeight={700}>
                                    All Fields ({reportData.field_count || 0})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {renderCompareFieldTable(
                                    catalystFields,
                                    'No fields found.',
                                    {
                                        maxHeight: 520,
                                        matchInsights: compareMatchInsights,
                                        reportCategory: 'catalyst',
                                    }
                                )}
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Stack>
            </Grid>
        );
    };

    const formatCompareDiagnosticReason = (reason) => {
        const labels = {
            report_out_of_scope: 'Report Out Of Scope',
            no_usable_stage1_field_contributions: 'No Usable Stage 1 Contribution',
            shown_on_ui: 'Shown On UI',
            outside_pool_stage2_window: 'Outside Stage 2 Window',
            stage1_candidate: 'Stage 1 Candidate',
            contributed_to_stage1: 'Contributed To Stage 1',
            column_type_mismatch: 'Column Type Mismatch',
            schema_mapper_usable_selected_report_not_discovered: 'Schema Mapper Did Not Discover Selected Report',
            exact_match_not_usable: 'Exact Match Not Usable',
            semantic_match_filtered: 'Semantic Match Filtered',
            no_exact_or_semantic_match: 'No Exact Or Semantic Match',
            no_selected_report_match: 'No Selected-Report Match',
            non_layout_auto_gap: 'Non-Layout Auto Gap',
            below_pool_display_threshold: 'Below Final Threshold',
            outside_pool_top_n_or_tie: 'Outside Final Top N',
            within_pool_top_n_or_tie: 'Shown In Final Pool',
            within_pool_stage2_window: 'Entered Stage 2',
            hidden_after_stage2: 'Hidden After Stage 2',
        };
        return labels[reason] || String(reason || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) || 'Unknown';
    };

    const renderCompareStatusChip = (value, labels = {}) => {
        if (value === true) {
            return <Chip size="small" color="success" variant="outlined" label={labels.trueLabel || 'Yes'} />;
        }
        if (value === false) {
            return <Chip size="small" color="default" variant="outlined" label={labels.falseLabel || 'No'} />;
        }
        return <Chip size="small" color="default" variant="outlined" label={labels.unknownLabel || 'Not Available'} />;
    };

    const formatCompareDetailScore = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        const numeric = Number(value);
        if (Number.isNaN(numeric)) return '-';
        const percent = numeric <= 1 ? numeric * 100 : numeric;
        return `${percent.toFixed(1)}%`;
    };

    const normalizeCompareBoolean = (value) => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (normalized === 'true') return true;
            if (normalized === 'false') return false;
        }
        return null;
    };

    const normalizeCompareDetailRow = (row) => {
        if (!row || typeof row !== 'object') return null;
        return {
            legacy_key: row.legacy_key,
            legacy_column: row.legacy_column || row.legacy_logical || '',
            legacy_physical: row.legacy_physical || '',
            legacy_table: row.legacy_table || '',
            catalyst_column: row.catalyst_column || row.catalyst_logical || '',
            catalyst_physical: row.catalyst_physical || '',
            catalyst_table: row.catalyst_table || '',
            match_type: row.match_type || '',
            match_origin: row.match_origin || '',
            column_type: row.column_type || '',
            semantic_score: row.semantic_score ?? row.technical_score ?? row.vector_score ?? row.score ?? null,
            llm_score: row.llm_score ?? row.llm_confidence ?? row.schema_mapper_llm_confidence ?? null,
            llm_reasoning: row.llm_reasoning || row.schema_mapper_llm_reasoning || '',
            llm_validated: normalizeCompareBoolean(row.llm_validated),
            llm_equivalent: (
                normalizeCompareBoolean(row.llm_is_equivalent) ?? normalizeCompareBoolean(row.llm_equivalent)
            ),
            validation_source: row.validation_source || '',
            validation_strategy: row.validation_strategy || '',
            counted_in_stage2: normalizeCompareBoolean(row.counted_in_stage2),
            stage2_status: row.stage2_status || '',
            stage2_exclusion_reason: row.stage2_exclusion_reason || '',
        };
    };

    const formatCompareValidationSource = (value) => {
        const normalized = String(value || '').trim().toLowerCase();
        const labels = {
            fresh_llm: 'Fresh LLM validation',
            reuse_schema_mapper: 'Schema Mapper validation reused',
            schema_mapper: 'Schema Mapper validation reused',
            user_preference: 'User preference match',
            user_preference_rejected: 'Rejected user preference',
            same_theme_exact: 'Same-theme exact match',
            selected_target_report_exact: 'Exact match in selected report',
            top_contributing_tables_exact: 'Exact match in top contributing tables',
            none: 'Validation skipped',
        };
        return labels[normalized] || '';
    };

    const getCompareDetailSourceLabel = (row) => {
        const validationStrategy = formatCompareValidationSource(row?.validation_strategy);
        const validationSource = formatCompareValidationSource(row?.validation_source);
        const matchType = String(row?.match_type || '').trim();

        if (validationSource) {
            return validationSource;
        }
        if (validationStrategy) {
            return validationStrategy;
        }
        return matchType || '-';
    };

    const getCompareDetailMatchMeta = (matchType) => {
        const value = String(matchType || '').trim().toLowerCase();
        if (!value) {
            return { label: '-', color: 'default' };
        }
        if (value.includes('exact')) {
            return { label: 'Exact', color: 'success' };
        }
        if (value.includes('semantic')) {
            return { label: 'Semantic', color: 'info' };
        }
        if (value.includes('recovery') || value.includes('llm')) {
            return { label: 'LLM Recovery', color: 'warning' };
        }
        return { label: matchType, color: 'default' };
    };

    const getCompareDetailLlmRunMeta = (row) => {
        if (row?.llm_validated === true) {
            return { label: 'Yes', color: 'success' };
        }
        if (row?.llm_validated === false) {
            return { label: 'No', color: 'default' };
        }
        return { label: '-', color: 'default' };
    };

    const getCompareDetailLlmVerdictMeta = (row) => {
        if (row?.llm_equivalent === true) {
            return { label: 'True', color: 'success' };
        }
        if (row?.llm_equivalent === false) {
            return { label: 'False', color: 'error' };
        }
        return { label: '-', color: 'default' };
    };

    const getCompareDetailCountedMeta = (row) => {
        if (row?.counted_in_stage2 === true) {
            return { label: 'Yes', color: 'success' };
        }
        if (row?.counted_in_stage2 === false) {
            return { label: 'No', color: 'error' };
        }
        return { label: '-', color: 'default' };
    };

    const renderCompareFieldDetailTable = (rows = [], emptyLabel, { showCountedColumn = false } = {}) => (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, borderRadius: 2 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Legacy Field</TableCell>
                        <TableCell>Catalyst Field</TableCell>
                        <TableCell>Match Type</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell align="right">Semantic Score</TableCell>
                        <TableCell align="right">LLM Score</TableCell>
                        <TableCell align="center">LLM Run</TableCell>
                        <TableCell align="center">LLM Verdict</TableCell>
                        {showCountedColumn && <TableCell align="center">Counted</TableCell>}
                        <TableCell align="center">Reasoning</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={showCountedColumn ? 10 : 9} align="center" sx={{ py: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {emptyLabel}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((rawRow, index) => {
                        const row = normalizeCompareDetailRow(rawRow);
                        if (!row) return null;
                        const matchMeta = getCompareDetailMatchMeta(row.match_type);
                        const llmRunMeta = getCompareDetailLlmRunMeta(row);
                        const llmVerdictMeta = getCompareDetailLlmVerdictMeta(row);
                        const countedMeta = getCompareDetailCountedMeta(row);
                        const reasoning = String(row.stage2_exclusion_reason || row.llm_reasoning || '').trim();
                        const legacyLabel = row.legacy_physical || row.legacy_column || '-';
                        const catalystLabel = row.catalyst_physical || row.catalyst_column || '-';
                        return (
                            <TableRow
                                key={`${row.legacy_key || index}-${row.catalyst_physical || row.catalyst_column || index}`}
                                sx={row.counted_in_stage2 === false ? { bgcolor: alpha(theme.palette.error.main, 0.06) } : undefined}
                            >
                                <TableCell sx={tableCellWrapSx}>
                                    <Typography variant="body2" fontWeight={600} sx={tableCellWrapSx}>
                                        {legacyLabel}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={tableCellWrapSx}>
                                        {row.legacy_column || '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={tableCellWrapSx}>
                                    <Typography variant="body2" fontWeight={600} sx={tableCellWrapSx}>
                                        {catalystLabel}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={tableCellWrapSx}>
                                        {row.catalyst_column || '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={tableCellWrapSx}>
                                    <Chip size="small" color={matchMeta.color} variant="outlined" label={matchMeta.label} />
                                </TableCell>
                                <TableCell sx={tableCellWrapSx}>
                                    <Typography variant="body2" sx={tableCellWrapSx}>
                                        {getCompareDetailSourceLabel(row)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">{formatCompareDetailScore(row.semantic_score)}</TableCell>
                                <TableCell align="right">{formatCompareDetailScore(row.llm_score)}</TableCell>
                                <TableCell align="center">
                                    <Chip size="small" color={llmRunMeta.color} variant="outlined" label={llmRunMeta.label} />
                                </TableCell>
                                <TableCell align="center">
                                    <Chip size="small" color={llmVerdictMeta.color} variant="outlined" label={llmVerdictMeta.label} />
                                </TableCell>
                                {showCountedColumn && (
                                    <TableCell align="center">
                                        <Chip size="small" color={countedMeta.color} variant="outlined" label={countedMeta.label} />
                                    </TableCell>
                                )}
                                <TableCell align="center">
                                    {reasoning ? (
                                        <Tooltip title={reasoning} arrow placement="left">
                                            <IconButton size="small" aria-label="View LLM reasoning">
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">-</Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderCompareDiagnostics = () => {
        const diagnostics = compareReportData?.diagnostics;
        if (!diagnostics) {
            return <Alert severity="info">Diagnostics are not available for this comparison yet.</Alert>;
        }

        const summary = diagnostics.summary || {};
        const pairMetrics = diagnostics.pair_metrics || {};
        const pools = diagnostics.pools || {};
        const fieldDiagnostics = diagnostics.field_diagnostics || [];
        const autoGapFields = diagnostics.auto_gap_fields || [];
        const selectedReportDetail = diagnostics.selected_report_detail || {};
        const stage1FallbackUsed = Boolean(pairMetrics.stage1_non_layout_fallback_used);
        const primaryStage1FieldLabel = stage1FallbackUsed
            ? `${pairMetrics.stage1_non_layout_fallback_fields || 0} fallback Stage 1 non-layout fields`
            : `${pairMetrics.layout_scored_legacy_fields || 0} layout-scored fields`;

        return (
            <Stack spacing={3}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                            <Box>
                                <Typography variant="h6" fontWeight={700}>
                                    Matching Diagnostics
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {summary.saved_matching_used
                                        ? 'Using saved report-matching telemetry for Stage 2 and final display statuses.'
                                        : 'No saved matching with the current options was found. Stage 1 diagnostics are live; Stage 2/final display statuses may be unavailable.'}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip size="small" color="primary" variant="outlined" label={summary.pool_label || 'Out of Scope'} />
                                <Chip size="small" color="warning" variant="outlined" label={primaryStage1FieldLabel} />
                                <Chip size="small" color="success" variant="outlined" label={`${pairMetrics.stage1_contributing_fields || 0} Stage 1 contributions`} />
                                <Chip size="small" color="info" variant="outlined" label={`${pairMetrics.raw_exact_selected_report_fields || 0} raw exacts in selected report`} />
                                <Chip size="small" color="default" variant="outlined" label={`${pairMetrics.column_type_mismatch_fields || 0} type mismatches`} />
                            </Stack>
                        </Stack>

                        <Alert severity={summary.in_scope ? (summary.stage1_candidate ? 'success' : 'warning') : 'info'}>
                            <Typography variant="body2" fontWeight={600}>
                                {formatCompareDiagnosticReason(summary.stop_reason)}
                            </Typography>
                            <Typography variant="body2">
                                {summary.stop_detail || 'No summary available.'}
                            </Typography>
                        </Alert>

                        <Grid container spacing={2}>
                            {[
                                { label: 'In Scope', value: summary.in_scope },
                                { label: 'Stage 1 Candidate', value: summary.stage1_candidate },
                                { label: 'Entered Stage 2', value: summary.stage2_selected, labels: { trueLabel: 'Yes', falseLabel: 'No', unknownLabel: 'Needs Saved Match' } },
                                { label: 'Shown On UI', value: summary.shown_on_ui, labels: { trueLabel: 'Yes', falseLabel: 'No', unknownLabel: 'Needs Saved Match' } },
                            ].map((item) => (
                                <Grid item xs={12} sm={6} md={3} key={item.label}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            {item.label}
                                        </Typography>
                                        {renderCompareStatusChip(item.value, item.labels)}
                                        {item.label === 'Stage 1 Candidate' && summary.stage1_rank ? (
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                Rank {summary.stage1_rank} at {Number(summary.stage1_score || 0).toFixed(1)}%
                                            </Typography>
                                        ) : null}
                                        {item.label === 'Entered Stage 2' && summary.stage2_reason ? (
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                {formatCompareDiagnosticReason(summary.stage2_reason)}
                                            </Typography>
                                        ) : null}
                                        {item.label === 'Shown On UI' && summary.display_reason ? (
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                {formatCompareDiagnosticReason(summary.display_reason)}
                                            </Typography>
                                        ) : null}
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Stack>
                </Paper>

                <Grid container spacing={2}>
                    {Object.entries(pools).map(([poolName, pool]) => (
                        <Grid item xs={12} md={4} key={poolName}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                                <Stack spacing={1.25}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {pool?.label || formatCompareDiagnosticReason(poolName)}
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {renderCompareStatusChip(pool?.report_type_allowed, { trueLabel: 'Type OK', falseLabel: 'Type Blocked' })}
                                        {renderCompareStatusChip(pool?.theme_scope_match, { trueLabel: 'Theme OK', falseLabel: 'Theme Blocked' })}
                                        {renderCompareStatusChip(pool?.in_scope, { trueLabel: 'In Scope', falseLabel: 'Out Of Scope' })}
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        Theme scope: {(pool?.theme_scope || []).join(', ') || 'None'}
                                    </Typography>
                                    {pool?.excluded_themes?.length ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Excluded themes: {pool.excluded_themes.join(', ')}
                                        </Typography>
                                    ) : null}
                                    <Divider />
                                    <Typography variant="body2">
                                        Stage 1: {pool?.stage1_candidate ? `Yes${pool?.stage1_rank ? `, rank ${pool.stage1_rank}` : ''}` : 'No'}
                                    </Typography>
                                    <Typography variant="body2">
                                        Stage 2: {pool?.stage2_selected === null || pool?.stage2_selected === undefined ? 'Not available' : (pool.stage2_selected ? 'Entered' : 'Stayed in Stage 1')}
                                    </Typography>
                                    <Typography variant="body2">
                                        Final UI: {pool?.shown_on_ui === null || pool?.shown_on_ui === undefined ? 'Not available' : (pool.shown_on_ui ? 'Shown' : 'Hidden')}
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Stack spacing={1.5}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                            <Box>
                                <Typography variant="h6" fontWeight={700}>
                                    Selected Report Field Details
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Stage 1 details come from the current compare run. Stage 2 details come from saved matching data only when the same options already exist.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip size="small" color="primary" variant="outlined" label={`Stage 1 ${Number(selectedReportDetail.stage1_overlap_percentage || 0).toFixed(1)}%`} />
                                <Chip
                                    size="small"
                                    color={selectedReportDetail.stage2_selected ? 'secondary' : 'default'}
                                    variant="outlined"
                                    label={`Stage 2 ${selectedReportDetail.stage2_overlap_percentage === null || selectedReportDetail.stage2_overlap_percentage === undefined ? '-' : `${Number(selectedReportDetail.stage2_overlap_percentage || 0).toFixed(1)}%`}`}
                                />
                                <Chip size="small" variant="outlined" label={`Stage 1 Fields ${selectedReportDetail.stage1_field_count || 0}`} />
                                <Chip size="small" variant="outlined" label={`Stage 2 Fields ${selectedReportDetail.stage2_field_count || 0}`} />
                                <Chip size="small" variant="outlined" label={`Stage 2 Counted ${selectedReportDetail.stage2_counted_field_count || 0}`} />
                                <Chip
                                    size="small"
                                    color={selectedReportDetail.displayed ? 'success' : 'default'}
                                    variant="outlined"
                                    label={formatCompareDiagnosticReason(selectedReportDetail.final_reason || summary.stop_reason)}
                                />
                            </Stack>
                        </Stack>

                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.25 }}>
                                Stage 1 Field Matches
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                                These are the selected report field contributions from the current compare run.
                            </Typography>
                            {renderCompareFieldDetailTable(
                                selectedReportDetail.stage1_field_details || [],
                                'No Stage 1 field details are available for this selected report.'
                            )}
                        </Box>

                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.25 }}>
                                Stage 2 Verified Field Matches
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                                Stage 2 shows every reviewed field from saved matching output. Rows marked Counted = No were reviewed in Stage 2 but excluded from the final Stage 2 score. The compare page does not rerun Stage 2 implicitly.
                            </Typography>
                            {renderCompareFieldDetailTable(
                                selectedReportDetail.stage2_field_details || [],
                                selectedReportDetail.stage2_selected
                                    ? 'No Stage 2 verified field details are available for this selected report.'
                                    : 'This selected report does not have saved Stage 2 field details for the current matching options.',
                                { showCountedColumn: true }
                            )}
                        </Box>
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Stack spacing={1.5}>
                        <Typography variant="h6" fontWeight={700}>
                            Pair Metrics
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" variant="outlined" label={`${pairMetrics.raw_legacy_fields || 0} raw legacy fields`} />
                            <Chip size="small" variant="outlined" label={`${pairMetrics.filtered_legacy_fields || 0} filtered legacy fields`} />
                            <Chip size="small" variant="outlined" label={`${pairMetrics.stage1_input_legacy_fields || 0} Stage 1 input fields`} />
                            <Chip size="small" variant="outlined" label={`${pairMetrics.non_layout_auto_gap_fields || 0} auto-gap non-layout`} />
                            <Chip size="small" variant="outlined" label={`${pairMetrics.schema_mapper_usable_fields || 0} Schema Mapper usable`} />
                            <Chip size="small" variant="outlined" label={`${pairMetrics.direct_fallback_fields || 0} direct fallback fields`} />
                            <Chip size="small" variant="outlined" label={`${Number(pairMetrics.stage1_overlap_percentage || 0).toFixed(1)}% Stage 1 coverage`} />
                            <Chip size="small" variant="outlined" label={`${Number(pairMetrics.stage1_precision_percentage || 0).toFixed(1)}% Stage 1 precision`} />
                        </Stack>
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    <Stack spacing={1.5}>
                        <Typography variant="h6" fontWeight={700}>
                            {stage1FallbackUsed ? 'Stage 1 Fallback Field Diagnostics' : 'Layout-Scored Field Diagnostics'}
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520, borderRadius: 2 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Legacy Field</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Schema Mapper</TableCell>
                                        <TableCell>Selected Report Raw Exact</TableCell>
                                        <TableCell>Fallback</TableCell>
                                        <TableCell>Outcome</TableCell>
                                        <TableCell>Why It Stopped</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fieldDiagnostics.map((field) => (
                                        <TableRow
                                            key={field.legacy_key}
                                            sx={{
                                                bgcolor: field.final_stage1_contribution
                                                    ? alpha(theme.palette.success.main, 0.08)
                                                    : field.stop_reason === 'column_type_mismatch'
                                                        ? alpha(theme.palette.warning.main, 0.08)
                                                        : undefined,
                                            }}
                                        >
                                            <TableCell sx={tableCellWrapSx}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {field.legacy_logical || field.legacy_physical || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {field.legacy_physical || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={tableCellWrapSx}>
                                                <Chip size="small" variant="outlined" label={field.legacy_column_type || 'unknown'} />
                                            </TableCell>
                                            <TableCell sx={tableCellWrapSx}>
                                                <Stack spacing={0.5}>
                                                    {renderCompareStatusChip(field.schema_mapper_usable, { trueLabel: 'Usable', falseLabel: 'Not Usable' })}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {field.schema_mapper_lookup_level || 'No lookup hit'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {field.schema_mapper_selected_report_hits || 0} selected-report hits
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={tableCellWrapSx}>
                                                <Stack spacing={0.5}>
                                                    {field.raw_exact_type ? (
                                                        <Chip
                                                            size="small"
                                                            color={field.raw_exact_type === 'physical' ? 'success' : 'info'}
                                                            variant="outlined"
                                                            label={`${field.raw_exact_type} exact`}
                                                        />
                                                    ) : (
                                                        <Chip size="small" variant="outlined" label="None" />
                                                    )}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {(field.raw_exact_column_types || []).join(', ') || 'No exact in selected report'}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={tableCellWrapSx}>
                                                <Stack spacing={0.5}>
                                                    {renderCompareStatusChip(field.direct_fallback_allowed, { trueLabel: 'Allowed', falseLabel: 'Skipped' })}
                                                    <Typography variant="caption" color="text.secondary">
                                                        exact hits: {field.selected_report_exact_hits || 0}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        semantic hits: {field.fallback_semantic_selected_report_hits || 0}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={tableCellWrapSx}>
                                                <Stack spacing={0.5}>
                                                    {renderCompareStatusChip(field.final_stage1_contribution, { trueLabel: 'Contributed', falseLabel: 'Blocked' })}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {field.final_match_type || 'No final match'}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={tableCellWrapSx}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {formatCompareDiagnosticReason(field.stop_reason)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {field.stop_detail || '-'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {fieldDiagnostics.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No layout-scored field diagnostics are available.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {autoGapFields.length > 0 && (
                            <Alert severity="info">
                                {autoGapFields.length} legacy fields are outside report layout and move to gaps automatically before report scoring.
                            </Alert>
                        )}
                    </Stack>
                </Paper>
            </Stack>
        );
    };

    const loadSavedMatchings = async (wsId) => {
        try {
            const data = await getWorkspaceMatchings(wsId);
            setSavedMatchings(data || []);
        } catch (err) {
            console.error('Failed to load saved matchings:', err);
        }
    };

    const loadWorkspaceMatchJobs = async (wsId, options = {}) => {
        try {
            const data = await listReportMatchingJobs(wsId, { limit: options.limit || 100 });
            const jobs = data || [];
            setWorkspaceMatchJobs(jobs);
            applyMatchJobState(selectPrimaryMatchJob(jobs, selectedLegacyReport?.report_name));
            return jobs;
        } catch (err) {
            console.error('Failed to load match jobs:', err);
            return [];
        }
    };

    const loadSchemaProjects = async () => {
        try {
            const data = await getProjects();
            // API returns { projects: [...] } or just array
            const projectsArray = Array.isArray(data) ? data : (data?.projects || []);
            setSchemaMapperProjects(projectsArray);
        } catch (err) {
            console.error('Failed to load schema mapper projects:', err);
            setSchemaMapperProjects([]);
        }
    };

    const loadExistingIndexedLineageFiles = async () => {
        try {
            const files = await getLineageFiles();
            const eligible = (Array.isArray(files) ? files : [])
                .filter((file) => file?.is_classified && file?.is_indexed)
                .sort((left, right) => String(right?.uploaded_at || '').localeCompare(String(left?.uploaded_at || '')));
            setExistingIndexedLineageFiles(eligible);
        } catch (err) {
            console.error('Failed to load indexed lineage files:', err);
            setExistingIndexedLineageFiles([]);
        }
    };

    const showNotification = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const buildPreferenceDecisionPayload = ({
        legacyLogical,
        legacyPhysical,
        legacyTable,
        catalystLogical,
        catalystPhysical,
        catalystTable,
        columnType,
        matchType,
        status,
        source,
        notes = '',
    }) => ({
        legacy_logical: legacyLogical || '',
        legacy_physical: legacyPhysical || '',
        legacy_report_name: gapAnalysis?.legacy_report || selectedLegacyReport?.report_name || '',
        legacy_table: legacyTable || '',
        catalyst_logical: catalystLogical || '',
        catalyst_physical: catalystPhysical || '',
        catalyst_report_name: gapAnalysis?.catalyst_report || selectedMatch?.catalyst_report_name || '',
        catalyst_table: catalystTable || '',
        column_type: columnType || 'base',
        status,
        source,
        notes,
        decided_by: 'user',
        context: {
            workspace_id: selectedWorkspace?.id || '',
            file_id: lineageMeta?.file_id || selectedWorkspace?.lineage_file_id || '',
            schema_project_id: selectedWorkspace?.schema_project_id || selectedSchemaProject?.id || '',
            legacy_report_name: gapAnalysis?.legacy_report || selectedLegacyReport?.report_name || '',
            catalyst_report_name: gapAnalysis?.catalyst_report || selectedMatch?.catalyst_report_name || '',
            legacy_table: legacyTable || '',
            catalyst_table: catalystTable || '',
            match_type: matchType || '',
        },
    });

    const getRecommendationCandidateLooseKey = (candidate) => [
        candidate?.catalyst_table || '',
        candidate?.catalyst_physical || '',
        candidate?.catalyst_column || candidate?.catalyst_logical || candidate?.catalyst_attribute || '',
        candidate?.catalyst_layer || '',
    ].map((part) => String(part || '').trim().toLowerCase()).join('||');

    const getRecommendationCandidateKey = (candidate) => {
        const explicitKey = String(candidate?.recommendation_action_key || '').trim().toLowerCase();
        if (explicitKey) return explicitKey;
        return [
            candidate?.source || '',
            candidate?.match_origin || '',
            candidate?.recommendation_kind || '',
            getRecommendationCandidateLooseKey(candidate),
        ].map((part) => String(part || '').trim().toLowerCase()).join('||');
    };

    const buildSchemaMappingPreferencePayload = (gap, candidate, status, source, notes = '') => ({
        legacy_logical: gap?.legacy_column || '',
        legacy_physical: gap?.legacy_physical || '',
        legacy_table: gap?.legacy_table || gap?.legacy_table_physical || gap?.legacy_table_logical || '',
        target_logical: getRecommendationCandidateTargetLogical(candidate),
        target_physical: getRecommendationCandidateTargetPhysical(candidate),
        target_table: getRecommendationCandidateTargetTable(candidate),
        target_layer: candidate?.catalyst_layer || '',
        status,
        source,
        notes,
        decided_by: 'user',
        context: {
            workspace_id: selectedWorkspace?.id || '',
            file_id: lineageMeta?.file_id || selectedWorkspace?.lineage_file_id || '',
            schema_project_id: selectedWorkspace?.schema_project_id || selectedSchemaProject?.id || '',
            source_origin: 'gap_recommendation',
            source_label: recommendations?.legacy_report || gapAnalysis?.legacy_report || selectedLegacyReport?.report_name || '',
            legacy_report_name: recommendations?.legacy_report || gapAnalysis?.legacy_report || selectedLegacyReport?.report_name || '',
            catalyst_report_name: recommendations?.catalyst_report || gapAnalysis?.catalyst_report || selectedMatch?.catalyst_report_name || '',
            recommendation_source: candidate?.source || '',
            match_origin: candidate?.match_origin || '',
            recommendation_kind: candidate?.recommendation_kind || '',
            llm_confidence: candidate?.llm_confidence ?? null,
            llm_is_equivalent: candidate?.llm_is_equivalent ?? null,
            vector_score: candidate?.vector_score ?? null,
        },
    });

    const buildRecommendationDeveloperUsePayload = (gap, candidate, developerSelected) => ({
        legacy_report_name: recommendations?.legacy_report || gapAnalysis?.legacy_report || selectedLegacyReport?.report_name || '',
        catalyst_report_name: recommendations?.catalyst_report || gapAnalysis?.catalyst_report || selectedMatch?.catalyst_report_name || '',
        gap_identity: getRecommendationGapIdentity(gap),
        candidate_action_key: getRecommendationCandidateKey(candidate),
        candidate_loose_key: getRecommendationCandidateLooseKey(candidate),
        developer_selected: Boolean(developerSelected),
    });

    const getActiveSchemaProjectId = () => (
        selectedWorkspace?.schema_project_id
        || workspaceSchemaProjectId
        || selectedSchemaProject?.id
        || recommendations?.options?.schema_project_id
        || ''
    );

    const normalizeRecommendationPreferenceText = (value) => String(value || '').trim().toLowerCase();

    const recommendationPreferenceFieldMatchRank = (recordField = {}, queryField = {}, options = {}) => {
        const recordTable = normalizeRecommendationPreferenceText(recordField.table_name);
        const queryTable = normalizeRecommendationPreferenceText(queryField.table_name);
        const recordPhysical = normalizeRecommendationPreferenceText(recordField.physical_name);
        const queryPhysical = normalizeRecommendationPreferenceText(queryField.physical_name);
        const recordLogical = normalizeRecommendationPreferenceText(recordField.logical_name);
        const queryLogical = normalizeRecommendationPreferenceText(queryField.logical_name);

        if (options.requireTablePhysical) {
            return (
                recordTable
                && queryTable
                && recordPhysical
                && queryPhysical
                && recordTable === queryTable
                && recordPhysical === queryPhysical
            ) ? 300 : 0;
        }

        if (recordTable && queryTable && recordPhysical && queryPhysical && recordTable === queryTable && recordPhysical === queryPhysical) {
            return 300;
        }
        if (recordPhysical && queryPhysical && recordPhysical === queryPhysical) {
            return 200;
        }
        if (recordLogical && queryLogical && recordLogical === queryLogical) {
            return 100;
        }
        return 0;
    };

    const matchesSchemaPreferencePair = (record, gap, candidate) => {
        const legacy = record?.legacy_field || {};
        const target = record?.target_field || {};
        const legacyRank = recommendationPreferenceFieldMatchRank(legacy, {
            logical_name: gap?.legacy_column || '',
            physical_name: gap?.legacy_physical || '',
            table_name: gap?.legacy_table || gap?.legacy_table_physical || gap?.legacy_table_logical || '',
        });
        const targetRank = recommendationPreferenceFieldMatchRank(target, {
            logical_name: getRecommendationCandidateTargetLogical(candidate),
            physical_name: getRecommendationCandidateStrictTargetPhysical(candidate),
            table_name: getRecommendationCandidateTargetTable(candidate),
        }, { requireTablePhysical: true });
        return (
            legacyRank > 0
            && targetRank > 0
            && normalizeRecommendationPreferenceText(target.layer) === normalizeRecommendationPreferenceText(candidate?.catalyst_layer)
        );
    };

    const resolveSchemaMappingPreferenceId = async (gap, candidate, options = {}) => {
        const existingId = options.ignoreExistingId
            ? ''
            : candidate?.schema_mapping_preference_id || candidate?.preference_id || '';
        if (existingId) return existingId;
        const search = gap?.legacy_physical || gap?.legacy_column || '';
        const response = await getSchemaMappingPreferences({
            search,
            layer: candidate?.catalyst_layer || '',
            limit: 1000,
        });
        const match = (response?.preferences || []).find((record) => matchesSchemaPreferencePair(record, gap, candidate));
        return match?.id || '';
    };

    const deleteSchemaMappingPreferenceForCandidate = async (gap, candidate) => {
        const attemptedIds = new Set();
        const candidateIds = [
            candidate?.schema_mapping_preference_id,
            candidate?.preference_id,
        ].map((value) => String(value || '').trim()).filter(Boolean);

        for (const preferenceId of candidateIds) {
            attemptedIds.add(preferenceId);
            try {
                await deleteSchemaMappingPreference(preferenceId);
                return true;
            } catch (err) {
                if (err?.response?.status !== 404) {
                    throw err;
                }
            }
        }

        const resolvedPreferenceId = await resolveSchemaMappingPreferenceId(gap, candidate, { ignoreExistingId: true });
        if (!resolvedPreferenceId || attemptedIds.has(resolvedPreferenceId)) {
            return false;
        }

        try {
            await deleteSchemaMappingPreference(resolvedPreferenceId);
            return true;
        } catch (err) {
            if (err?.response?.status !== 404) {
                throw err;
            }
            return false;
        }
    };

    const getRecommendationPreferenceActionKey = (gap, candidate) => (
        [
            gap?.__recommendation_row_key || getRecommendationGapIdentity(gap),
            getRecommendationCandidateKey(candidate),
        ].filter(Boolean).join('::')
    );

    const setRecommendationPreferenceBusy = (actionKey, busy) => {
        if (!actionKey) return;
        if (busy) {
            recommendationPreferenceBusyRef.current.add(actionKey);
        } else {
            recommendationPreferenceBusyRef.current.delete(actionKey);
        }
        setRecommendationPreferenceBusyKeys((prev) => {
            const next = { ...prev };
            if (busy) {
                next[actionKey] = true;
            } else {
                delete next[actionKey];
            }
            return next;
        });
    };

    const updateRecommendationCandidateState = (gap, candidate, updates, options = {}) => {
        const legacyIdentity = getRecommendationGapIdentity(gap);
        const rowKey = String(gap?.__recommendation_row_key || '').trim();
        const candidateKey = getRecommendationCandidateKey(candidate);
        const candidateLooseKey = getRecommendationCandidateLooseKey(candidate);
        const replaceCandidate = Boolean(options.replaceCandidate);
        const allowLooseFallback = options.allowLooseFallback !== false;
        if ((!rowKey && !legacyIdentity) || !candidateKey) return;

        const updateCandidateList = (items = []) => {
            const list = Array.isArray(items) ? [...items] : [];
            if (!list.length) return list;

            let targetIndex = list.findIndex((item) => getRecommendationCandidateKey(item) === candidateKey);
            if (targetIndex < 0 && allowLooseFallback && candidateLooseKey) {
                const looseMatches = list
                    .map((item, index) => ({ index, key: getRecommendationCandidateLooseKey(item) }))
                    .filter((entry) => entry.key === candidateLooseKey);
                if (looseMatches.length === 1) {
                    targetIndex = looseMatches[0].index;
                } else if (looseMatches.length > 1) {
                    targetIndex = looseMatches[0].index;
                }
            }
            if (targetIndex < 0) return list;

            list[targetIndex] = replaceCandidate ? { ...updates } : { ...list[targetIndex], ...updates };
            return list;
        };

        const rebuildRecommendationLayerBuckets = (items = []) => {
            const buckets = {
                L2: { exact: [], semantic: [] },
                L3: { exact: [], semantic: [] },
            };
            (items || []).forEach((item) => {
                const layer = String(item?.catalyst_layer || '').trim().toUpperCase();
                if (!buckets[layer]) return;
                const kind = String(item?.recommendation_kind || '').trim().toLowerCase();
                const origin = String(item?.match_origin || '').trim().toLowerCase();
                const bucketName = kind === 'exact' || [
                    'logical_exact',
                    'physical_exact',
                    'logical_exact_sibling',
                    'physical_exact_sibling',
                    'exact_table_physical',
                    'exact_physical',
                    'priority_table_exact_physical',
                    'priority_table_exact_logical',
                    'schema_mapper_exact',
                    'exact',
                ].includes(origin) ? 'exact' : 'semantic';
                buckets[layer][bucketName].push(item);
            });
            return buckets;
        };

        setRecommendations((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                recommendations: (prev.recommendations || []).map((rec, recIndex) => {
                    const matchesRow = rowKey
                        ? getRecommendationCardKey(rec, recIndex) === rowKey
                        : getRecommendationGapIdentity(rec) === legacyIdentity;
                    if (!matchesRow) return rec;
                    const nextRecommendations = updateCandidateList(rec.recommendations || []);
                    const nextAllRecommendations = updateCandidateList(rec.all_recommendations || rec.recommendations || []);
                    const nextReportRecommendations = updateCandidateList(rec.report_recommendations || []);
                    const nextAllReportRecommendations = updateCandidateList(rec.all_report_recommendations || rec.report_recommendations || []);
                    const existingLayerCandidates = Object.values(rec.recommendations_by_layer || {}).flatMap((groups) => [
                        ...(groups?.exact || []),
                        ...(groups?.semantic || []),
                    ]);
                    const existingAllLayerCandidates = Object.values(rec.all_recommendations_by_layer || rec.recommendations_by_layer || {}).flatMap((groups) => [
                        ...(groups?.exact || []),
                        ...(groups?.semantic || []),
                    ]);
                    const nextLayerCandidates = updateCandidateList(existingLayerCandidates);
                    const nextAllLayerCandidates = updateCandidateList(existingAllLayerCandidates);
                    return {
                        ...rec,
                        recommendations: nextRecommendations,
                        all_recommendations: nextAllRecommendations,
                        report_recommendations: nextReportRecommendations,
                        all_report_recommendations: nextAllReportRecommendations,
                        recommendations_by_layer: rebuildRecommendationLayerBuckets(nextLayerCandidates),
                        all_recommendations_by_layer: rebuildRecommendationLayerBuckets(nextAllLayerCandidates),
                    };
                }),
            };
        });
    };

    const clearRecommendationPreferenceDecision = async (gap, candidate) => {
        await deleteSchemaMappingPreferenceForCandidate(gap, candidate);
        const originalCandidate = (
            candidate?.manual_override_original_candidate
            && typeof candidate.manual_override_original_candidate === 'object'
        )
            ? candidate.manual_override_original_candidate
            : candidate;
        const preserveDeveloperSelected = !(
            candidate?.manual_override
            || ['manual_override', 'schema_mapper_manual_override'].includes(String(candidate?.preference_source || '').trim().toLowerCase())
        );
        updateRecommendationCandidateState(
            gap,
            candidate,
            {
                ...originalCandidate,
                preference_status: '',
                preference_source: '',
                preference_notes: '',
                schema_mapping_preference_id: '',
                preference_id: '',
                manual_override: false,
                manual_override_original_candidate: null,
                developer_selected: preserveDeveloperSelected ? Boolean(candidate?.developer_selected) : false,
            },
            { replaceCandidate: true, allowLooseFallback: false }
        );
    };

    const handleRecommendationPreferenceDecision = async (gap, candidate, status, source, notes = '') => {
        const actionKey = getRecommendationPreferenceActionKey(gap, candidate);
        if (actionKey && recommendationPreferenceBusyRef.current.has(actionKey)) {
            return;
        }
        setRecommendationPreferenceBusy(actionKey, true);
        try {
            const currentStatus = String(candidate?.preference_status || '').trim().toLowerCase();
            const currentSource = String(candidate?.preference_source || '').trim().toLowerCase();
            const nextSource = String(source || '').trim().toLowerCase();
            if (
                currentStatus === status
                && (
                    currentSource === nextSource
                    || (status === 'accepted' && nextSource === 'schema_mapper_accept')
                )
            ) {
                await clearRecommendationPreferenceDecision(gap, candidate);
                showNotification(status === 'accepted' ? 'Accepted preference cleared' : 'Rejected preference cleared', 'info');
                return;
            }

            const record = await saveSchemaMappingPreference(
                buildSchemaMappingPreferencePayload(gap, candidate, status, source, notes)
            );
            updateRecommendationCandidateState(gap, candidate, {
                preference_status: record?.status || status,
                preference_source: record?.source || source,
                preference_notes: record?.notes || notes,
                schema_mapping_preference_id: record?.id || '',
                manual_override: source === 'manual_override',
            });
            showNotification(
                status === 'accepted' ? 'Schema Analyzer preference accepted' : 'Schema Analyzer preference rejected',
                status === 'accepted' ? 'success' : 'info'
            );
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to save Schema Analyzer preference', 'error');
        } finally {
            setRecommendationPreferenceBusy(actionKey, false);
        }
    };

    const handleRecommendationDeveloperSelection = async (gap, candidate, checked) => {
        const actionKey = getRecommendationPreferenceActionKey(gap, candidate);
        if (actionKey && recommendationPreferenceBusyRef.current.has(actionKey)) {
            return;
        }
        if (!selectedWorkspace?.id) {
            showNotification('Workspace is not selected', 'error');
            return;
        }
        setRecommendationPreferenceBusy(actionKey, true);
        try {
            const payload = buildRecommendationDeveloperUsePayload(gap, candidate, checked);
            if (!payload.gap_identity || !(payload.candidate_action_key || payload.candidate_loose_key)) {
                throw new Error('Could not identify the selected recommendation candidate.');
            }
            const updatedRecommendation = await updateSavedRecommendationDeveloperUse(
                selectedWorkspace.id,
                payload
            );
            setRecommendations(updatedRecommendation);
            setSavedRecommendationAvailable(true);
            showNotification(checked ? 'Developer-use indicator saved' : 'Developer-use indicator cleared', checked ? 'success' : 'info');
        } catch (err) {
            showNotification(err.response?.data?.detail || err.message || 'Failed to update developer-use indicator', 'error');
        } finally {
            setRecommendationPreferenceBusy(actionKey, false);
        }
    };

    const getUniqueSortedTables = (tables = []) => {
        const seen = new Set();
        const unique = [];
        (tables || []).forEach((table) => {
            const tableName = String(table?.table_name || '').trim();
            if (!tableName) return;
            const key = tableName.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            unique.push(table);
        });
        return unique.sort((left, right) => String(left.table_name || '').localeCompare(String(right.table_name || '')));
    };

    const getUniqueSortedColumns = (columns = []) => {
        const seen = new Set();
        const unique = [];
        (columns || []).forEach((column) => {
            const tableName = String(column?.table_name || '').trim();
            const columnName = String(column?.column_name || '').trim();
            if (!columnName) return;
            const key = `${tableName.toLowerCase()}|${columnName.toLowerCase()}`;
            if (seen.has(key)) return;
            seen.add(key);
            unique.push(column);
        });
        return unique.sort((left, right) => String(left.column_name || '').localeCompare(String(right.column_name || '')));
    };

    const loadManualOverrideTables = async (layer) => {
        const projectId = getActiveSchemaProjectId();
        const sourceSystem = CATALYST_LAYER_SOURCE_SYSTEMS[layer];
        if (!projectId || !sourceSystem) {
            setManualOverrideDialog((prev) => ({
                ...prev,
                tables: [],
                loadingTables: false,
                error: 'Schema project is not selected.',
            }));
            return;
        }

        const cacheKey = `${projectId}:${sourceSystem}`;
        if (manualOverrideTableCache[cacheKey]) {
            setManualOverrideDialog((prev) => ({
                ...prev,
                tables: manualOverrideTableCache[cacheKey],
                loadingTables: false,
                error: '',
            }));
            return;
        }

        setManualOverrideDialog((prev) => ({ ...prev, loadingTables: true, error: '' }));
        try {
            const response = await getTablesBySource(projectId, sourceSystem);
            const tables = getUniqueSortedTables(response?.tables || []);
            setManualOverrideTableCache((prev) => ({ ...prev, [cacheKey]: tables }));
            setManualOverrideDialog((prev) => ({
                ...prev,
                tables,
                loadingTables: false,
                error: '',
            }));
        } catch (err) {
            setManualOverrideDialog((prev) => ({
                ...prev,
                tables: [],
                loadingTables: false,
                error: err.response?.data?.detail || `Failed to load ${layer} tables.`,
            }));
        }
    };

    const loadManualOverrideColumns = async (tableOption, layer) => {
        const projectId = getActiveSchemaProjectId();
        const sourceSystem = CATALYST_LAYER_SOURCE_SYSTEMS[layer];
        const tableName = String(tableOption?.table_name || '').trim();
        if (!projectId || !sourceSystem || !tableName) {
            setManualOverrideDialog((prev) => ({ ...prev, columns: [], loadingColumns: false }));
            return;
        }

        const cacheKey = `${projectId}:${sourceSystem}:${tableName.toLowerCase()}`;
        if (manualOverrideColumnCache[cacheKey]) {
            setManualOverrideDialog((prev) => ({
                ...prev,
                columns: manualOverrideColumnCache[cacheKey],
                loadingColumns: false,
                error: '',
            }));
            return;
        }

        setManualOverrideDialog((prev) => ({ ...prev, loadingColumns: true, error: '' }));
        try {
            let response = await getColumnsBySource(projectId, sourceSystem, tableName);
            let responseColumns = Array.isArray(response?.columns) ? response.columns : [];
            if (responseColumns.length === 0) {
                const fallbackResponse = await getColumnsBySource(projectId, sourceSystem);
                responseColumns = (Array.isArray(fallbackResponse?.columns) ? fallbackResponse.columns : []).filter(
                    (column) => String(column?.table_name || '').trim().toLowerCase() === tableName.toLowerCase()
                );
            }
            const columns = getUniqueSortedColumns(responseColumns);
            setManualOverrideColumnCache((prev) => ({ ...prev, [cacheKey]: columns }));
            setManualOverrideDialog((prev) => ({
                ...prev,
                columns,
                loadingColumns: false,
                error: '',
            }));
        } catch (err) {
            setManualOverrideDialog((prev) => ({
                ...prev,
                columns: [],
                loadingColumns: false,
                error: err.response?.data?.detail || `Failed to load ${layer} columns.`,
            }));
        }
    };

    const openRecommendationManualOverrideDialog = async (gap, candidate) => {
        const layer = String(candidate?.catalyst_layer || 'L2').trim().toUpperCase() === 'L3' ? 'L3' : 'L2';
        setManualOverrideDialog({
            open: true,
            gap,
            candidate,
            layer,
            table: null,
            column: null,
            tables: [],
            columns: [],
            loadingTables: true,
            loadingColumns: false,
            saving: false,
            error: '',
        });
        await loadManualOverrideTables(layer);
    };

    const closeRecommendationManualOverrideDialog = () => {
        setManualOverrideDialog((prev) => ({
            ...prev,
            open: false,
            gap: null,
            candidate: null,
            table: null,
            column: null,
            columns: [],
            saving: false,
            error: '',
        }));
    };

    const handleManualOverrideLayerChange = async (layer) => {
        setManualOverrideDialog((prev) => ({
            ...prev,
            layer,
            table: null,
            column: null,
            columns: [],
            loadingTables: true,
            error: '',
        }));
        await loadManualOverrideTables(layer);
    };

    const handleManualOverrideTableChange = async (tableOption) => {
        const layer = manualOverrideDialog.layer;
        setManualOverrideDialog((prev) => ({
            ...prev,
            table: tableOption,
            column: null,
            columns: [],
            error: '',
        }));
        await loadManualOverrideColumns(tableOption, layer);
    };

    const handleSaveRecommendationManualOverride = async () => {
        const { gap, candidate, layer, table, column } = manualOverrideDialog;
        if (!gap || !candidate || !table || !column) return;
        const hasExistingOriginal = (
            candidate?.manual_override_original_candidate
            && typeof candidate.manual_override_original_candidate === 'object'
            && Object.keys(candidate.manual_override_original_candidate).length > 0
        );
        const originalCandidate = hasExistingOriginal
            ? { ...candidate.manual_override_original_candidate }
            : Object.fromEntries(
                Object.entries(candidate || {}).filter(([key]) => ![
                    'preference_status',
                    'preference_source',
                    'preference_notes',
                    'schema_mapping_preference_id',
                    'manual_override',
                    'manual_override_original_candidate',
                    'developer_selected',
                ].includes(key))
            );
        const overrideCandidate = {
            ...candidate,
            catalyst_table: table.table_name || column.table_name || '',
            catalyst_physical: column.column_name || '',
            catalyst_column: column.attribute_name || column.column_name || '',
            catalyst_logical: column.attribute_name || column.column_name || '',
            catalyst_attribute: column.attribute_name || '',
            catalyst_definition: column.definition || '',
            catalyst_data_type: column.data_type || '',
            catalyst_layer: layer,
            source: 'schema_mapper',
            match_origin: 'manual_override',
            recommendation_kind: isExactRecommendationCandidate(candidate) ? 'exact' : 'semantic',
            manual_override: true,
            manual_override_original_candidate: originalCandidate,
            developer_selected: false,
            schema_mapper_fallback: false,
            fallback_used: false,
        };

        setManualOverrideDialog((prev) => ({ ...prev, saving: true, error: '' }));
        try {
            const record = await saveSchemaMappingPreference(
                buildSchemaMappingPreferencePayload(gap, overrideCandidate, 'accepted', 'schema_mapper_manual_override', 'Manual override from report recommendation')
            );
            updateRecommendationCandidateState(gap, candidate, {
                ...overrideCandidate,
                preference_status: record?.status || 'accepted',
                preference_source: record?.source || 'schema_mapper_manual_override',
                preference_notes: record?.notes || 'Manual override from report recommendation',
                schema_mapping_preference_id: record?.id || '',
                manual_override: true,
            }, { allowLooseFallback: false });
            showNotification('Manual override saved and accepted', 'success');
            closeRecommendationManualOverrideDialog();
        } catch (err) {
            setManualOverrideDialog((prev) => ({
                ...prev,
                saving: false,
                error: err.response?.data?.detail || 'Failed to save manual override.',
            }));
        }
    };

    const buildGapCandidateFromMatch = (match, preferenceStatus = 'rejected') => ({
        catalyst_column: match.catalyst_column,
        catalyst_physical: match.catalyst_physical,
        catalyst_table: match.catalyst_table || '',
        score: match.score || match.llm_confidence,
        match_type: match.match_type,
        llm_reasoning: match.llm_reasoning,
        llm_confidence: match.llm_confidence || match.score,
        preference_status: preferenceStatus,
        preference_source: 'user_action',
        preference_notes: match.preference_notes || '',
        preference_id: match.preference_id || '',
    });

    const buildInitialGapDecisions = (gaps = []) => {
        const decisions = {};
        (gaps || []).forEach((gap) => {
            const rowKey = gap?.legacy_key;
            if (!rowKey) return;
            const status = String(gap?.candidate?.preference_status || '').trim().toLowerCase();
            if (status === 'accepted' || status === 'rejected') {
                decisions[rowKey] = status;
            }
        });
        return decisions;
    };

    const getExpressionFieldType = (field) => {
        const marker = String(field?.legacy_report_sql_filter || field?.report_sql_filter || '').trim().toLowerCase();
        if (marker === 'filter') return 'Filter';
        if (marker === 'join') return 'Join';
        if (marker === 'sql') return 'SQL';

        const columnType = String(field?.column_type || field?.legacy_column_type || '').trim().toLowerCase();
        if (columnType === 'filter') return 'Filter';
        if (columnType === 'join') return 'Join';
        if (columnType === 'sql') return 'SQL';
        if (columnType === 'derived') return 'Calculation';

        const calculated = String(field?.legacy_calculated || field?.calculated || field?.Calculated || '').trim().toLowerCase();
        if (calculated === 'yes' || calculated === 'y' || calculated === 'true' || calculated === '1') {
            return 'Calculation';
        }

        return '';
    };

    const getCoveragePercentage = (matchedCount, totalLegacyColumns) => {
        if (!totalLegacyColumns) return 0;
        return Math.round((matchedCount / totalLegacyColumns) * 1000) / 10;
    };

    const markMatchedPreferenceStatus = (legacyKey, match, status) => {
        const candidateKey = getMatchCandidateKey({
            catalyst_logical: match?.catalyst_column || match?.catalyst_logical,
            catalyst_physical: match?.catalyst_physical,
            catalyst_table: match?.catalyst_table,
        });
        setGapAnalysis((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                matched_columns: (prev.matched_columns || []).map((item) => (
                    item.legacy_key !== legacyKey
                        ? item
                        : (() => {
                            const candidates = mergeMatchCandidates(item).map((candidate) => (
                                getMatchCandidateKey(candidate) === candidateKey
                                    ? {
                                        ...candidate,
                                        preference_status: status,
                                        preference_source: 'user_action',
                                    }
                                    : candidate
                            ));
                            const primaryCandidate = selectPreferredMatchCandidate(candidates, candidateKey);
                            return syncMatchedRowWithCandidate(item, primaryCandidate, candidates);
                        })()
                )),
            };
        });
        if (candidateKey) {
            setMatchSelections((prev) => ({ ...prev, [legacyKey]: candidateKey }));
        }
    };

    const getMatchPreferenceId = (match) => {
        if (!match) return '';
        if (match.preference_id) return String(match.preference_id);
        const candidateKey = getMatchCandidateKey({
            catalyst_logical: match?.catalyst_column || match?.catalyst_logical,
            catalyst_physical: match?.catalyst_physical,
            catalyst_table: match?.catalyst_table,
        });
        const candidates = mergeMatchCandidates(match);
        const candidate = candidates.find((item) => getMatchCandidateKey(item) === candidateKey);
        return String(candidate?.preference_id || '');
    };

    const applyMatchedPreferenceRecord = (legacyKey, match, record) => {
        const candidateKey = getMatchCandidateKey({
            catalyst_logical: match?.catalyst_column || match?.catalyst_logical,
            catalyst_physical: match?.catalyst_physical,
            catalyst_table: match?.catalyst_table,
        });
        autosaveLastFailedSnapshotRef.current = '';
        setWorkspaceDecisionDirty(true);
        setGapAnalysis((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                matched_columns: (prev.matched_columns || []).map((item) => (
                    item.legacy_key !== legacyKey
                        ? item
                        : (() => {
                            const candidates = mergeMatchCandidates(item).map((candidate) => (
                                getMatchCandidateKey(candidate) === candidateKey
                                    ? {
                                        ...candidate,
                                        preference_status: record?.status || '',
                                        preference_source: record?.source || 'user_action',
                                        preference_notes: record?.notes || '',
                                        preference_id: record?.id || '',
                                    }
                                    : candidate
                            ));
                            const primaryCandidate = selectPreferredMatchCandidate(candidates, candidateKey);
                            return syncMatchedRowWithCandidate(item, primaryCandidate, candidates);
                        })()
                )),
            };
        });
        if (candidateKey) {
            setMatchSelections((prev) => ({ ...prev, [legacyKey]: candidateKey }));
        }
    };

    const clearMatchedPreferenceStatus = (match) => {
        const clearedCandidateKey = getMatchCandidateKey({
            catalyst_logical: match?.catalyst_column || match?.catalyst_logical,
            catalyst_physical: match?.catalyst_physical,
            catalyst_table: match?.catalyst_table,
        });
        autosaveLastFailedSnapshotRef.current = '';
        setWorkspaceDecisionDirty(true);
        setGapAnalysis((prev) => {
            if (!prev) return prev;
            const targetIdentity = getLegacyFieldIdentity(match);
            const matchedRows = prev.matched_columns || [];
            const targetRow = matchedRows.find((item) => getLegacyFieldIdentity(item) === targetIdentity);
            if (!targetRow) return prev;

            const candidates = mergeMatchCandidates(targetRow).map((candidate) => (
                getMatchCandidateKey(candidate) === clearedCandidateKey
                    ? {
                        ...candidate,
                        preference_status: '',
                        preference_source: '',
                        preference_notes: '',
                        preference_id: '',
                    }
                    : candidate
            ));

            const clearedCandidate = candidates.find((candidate) => getMatchCandidateKey(candidate) === clearedCandidateKey) || null;
            const preferenceSource = String(match?.preference_source || targetRow?.preference_source || '').toLowerCase();
            const shouldReturnToGap = ['gap_accept', 'manual_correction', 'manual'].includes(preferenceSource);

            if (shouldReturnToGap) {
                const newMatched = matchedRows.filter((item) => getLegacyFieldIdentity(item) !== targetIdentity);
                const gapCandidate = clearedCandidate
                    ? {
                        ...clearedCandidate,
                        preference_status: '',
                        preference_source: '',
                        preference_notes: '',
                        preference_id: '',
                    }
                    : null;
                const revertedGap = {
                    ...targetRow,
                    candidate: gapCandidate,
                    preference_status: '',
                    preference_source: '',
                    preference_notes: '',
                    preference_id: '',
                };
                const newGaps = [
                    revertedGap,
                    ...(prev.gaps || []).filter((item) => getLegacyFieldIdentity(item) !== targetIdentity),
                ];
                const overlapPercentage = getCoveragePercentage(newMatched.length, prev.total_legacy_columns);
                return {
                    ...prev,
                    matched_columns: newMatched,
                    gaps: newGaps,
                    matched_count: newMatched.length,
                    gap_count: newGaps.length,
                    overlap_count: newMatched.length,
                    overlap_percentage: overlapPercentage,
                };
            }

            const primaryCandidate = selectPreferredMatchCandidate(candidates, clearedCandidateKey);
            return {
                ...prev,
                matched_columns: matchedRows.map((item) => (
                    getLegacyFieldIdentity(item) === targetIdentity
                        ? syncMatchedRowWithCandidate(item, primaryCandidate, candidates)
                        : item
                )),
            };
        });
        setDecidedGaps((prev) => {
            if (!match?.legacy_key || !prev?.[match.legacy_key]) return prev;
            const next = { ...prev };
            delete next[match.legacy_key];
            return next;
        });
        setMatchSelections((prev) => {
            if (!match?.legacy_key || !prev?.[match.legacy_key]) return prev;
            const next = { ...prev };
            delete next[match.legacy_key];
            return next;
        });
    };

    const rejectMatchedInView = (match) => {
        const rejectedCandidateKey = getMatchCandidateKey({
            catalyst_logical: match?.catalyst_column || match?.catalyst_logical,
            catalyst_physical: match?.catalyst_physical,
            catalyst_table: match?.catalyst_table,
        });
        autosaveLastFailedSnapshotRef.current = '';
        setWorkspaceDecisionDirty(true);
        setGapAnalysis((prev) => {
            if (!prev) return prev;
            const targetIdentity = getLegacyFieldIdentity(match);
            const matchedRows = prev.matched_columns || [];
            const targetRow = matchedRows.find((item) => getLegacyFieldIdentity(item) === targetIdentity);
            if (!targetRow) {
                return prev;
            }

            const candidates = mergeMatchCandidates(targetRow).map((candidate) => (
                getMatchCandidateKey(candidate) === rejectedCandidateKey
                    ? {
                        ...candidate,
                        preference_status: 'rejected',
                        preference_source: 'user_action',
                    }
                    : candidate
            ));
            const remainingAccepted = candidates.filter((candidate) => String(candidate?.preference_status || '').toLowerCase() === 'accepted');

            if (remainingAccepted.length > 0) {
                const primaryCandidate = selectPreferredMatchCandidate(candidates);
                return {
                    ...prev,
                    matched_columns: matchedRows.map((item) => (
                        getLegacyFieldIdentity(item) === targetIdentity
                            ? syncMatchedRowWithCandidate(item, primaryCandidate, candidates)
                            : item
                    )),
                };
            }

            const rejectedCandidate = candidates.find((candidate) => getMatchCandidateKey(candidate) === rejectedCandidateKey) || buildGapCandidateFromMatch(match, 'rejected');
            const newMatched = matchedRows.filter((m) => getLegacyFieldIdentity(m) !== targetIdentity);
            const newGaps = [{
                ...match,
                candidate: buildGapCandidateFromMatch({
                    ...match,
                    catalyst_column: rejectedCandidate.catalyst_logical || rejectedCandidate.catalyst_column || match.catalyst_column,
                    catalyst_physical: rejectedCandidate.catalyst_physical || match.catalyst_physical,
                    catalyst_table: rejectedCandidate.catalyst_table || match.catalyst_table,
                    score: rejectedCandidate.score ?? match.score,
                    match_type: rejectedCandidate.match_type || match.match_type,
                    llm_reasoning: rejectedCandidate.llm_reasoning || match.llm_reasoning,
                    llm_confidence: rejectedCandidate.llm_confidence ?? match.llm_confidence,
                    preference_notes: rejectedCandidate.preference_notes || match.preference_notes || '',
                }, 'rejected'),
            }, ...(prev.gaps || []).filter((g) => getLegacyFieldIdentity(g) !== targetIdentity)];
            const overlapPercentage = getCoveragePercentage(newMatched.length, prev.total_legacy_columns);

            return {
                ...prev,
                matched_columns: newMatched,
                gaps: newGaps,
                matched_count: newMatched.length,
                gap_count: newGaps.length,
                overlap_count: newMatched.length,
                overlap_percentage: overlapPercentage,
            };
        });
        if (rejectedCandidateKey) {
            setMatchSelections((prev) => {
                const next = { ...prev };
                delete next[match.legacy_key];
                return next;
            });
        }
    };

    const persistPreferenceDecision = async (payload) => {
        return await saveReportMatchingPreference(payload);
    };

    const handleCreateWorkspace = async () => {
        if (!newWorkspaceName.trim()) return;
        if (createWorkspaceMode === 'use_existing_index' && !selectedExistingLineageFile?.file_id) return;

        try {
            setLoading(true);
            const ws = await createWorkspace({
                name: newWorkspaceName.trim(),
                description: newWorkspaceDesc.trim(),
                schema_project_id: selectedSchemaProject?.id || null,
                existing_lineage_file_id: createWorkspaceMode === 'use_existing_index'
                    ? selectedExistingLineageFile?.file_id || null
                    : null,
            });
            showNotification(
                createWorkspaceMode === 'use_existing_index'
                    ? 'Workspace created from existing indexed lineage'
                    : 'Workspace created'
            );
            setShowCreateDialog(false);
            setNewWorkspaceName('');
            setNewWorkspaceDesc('');
            setSelectedSchemaProject(null);
            setCreateWorkspaceMode('upload_later');
            setSelectedExistingLineageFile(null);

            // Important: Navigate to new workspace, which will trigger Sidebar reload on refresh
            // For instant reload without full refresh, we could use a custom event
            navigate(`/report-matching/workspaces/${ws.id}`);
            window.location.reload(); // Quickest way to refresh the Layout sidebar for now
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to create workspace', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteWorkspace = async () => {
        if (!workspaceToDelete) return;

        try {
            setLoading(true);
            await deleteWorkspace(workspaceToDelete.id);
            showNotification('Workspace deleted');
            setShowDeleteDialog(false);
            setWorkspaceToDelete(null);
            loadWorkspaces();

            // If we were in this workspace, go back to landing
            if (workspaceId === workspaceToDelete.id) {
                navigate('/report-matching/workspaces');
            } else {
                window.location.reload();
            }
        } catch (err) {
            showNotification('Failed to delete workspace', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !selectedWorkspace) return;

        try {
            setUploading(true);
            const result = await uploadToWorkspace(selectedWorkspace.id, file, {
                incremental: Boolean(selectedWorkspace.lineage_file_id && incrementalUploadEnabled),
            });
            const warnings = Array.isArray(result?.processing_warnings)
                ? result.processing_warnings.filter(Boolean)
                : [];
            if (warnings.length > 0) {
                showNotification(warnings[0], 'warning');
            } else {
                showNotification(
                    result?.incremental_upload?.enabled
                        ? 'Incremental upload complete. Run classification to continue.'
                        : 'File uploaded'
                );
            }
            loadSelectedWorkspace(selectedWorkspace.id);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Upload failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleDownloadWorkspaceLineage = async () => {
        if (!selectedWorkspace?.id || !selectedWorkspace?.lineage_file_id) return;

        try {
            setDownloadingLineage(true);
            const { blob, filename } = await downloadWorkspaceLineage(
                selectedWorkspace.id,
                selectedWorkspace.name || ''
            );
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename || `report_lineage_${selectedWorkspace.name || selectedWorkspace.id}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            showNotification('Cleaned lineage file downloaded successfully!', 'success');
        } catch (err) {
            console.error('Workspace lineage download failed:', err);
            showNotification(err.response?.data?.detail || 'Failed to download cleaned lineage file.', 'error');
        } finally {
            setDownloadingLineage(false);
        }
    };

    const handleClassify = async () => {
        if (!selectedWorkspace) return;

        try {
            const state = await classifyWorkspace(selectedWorkspace.id);
            applyLineageClassificationState(state);
            showNotification('Classification started');
        } catch (err) {
            setClassifying(false);
            showNotification(err.response?.data?.detail || 'Classification failed', 'error');
        }
    };

    const handleIndex = async () => {
        if (!selectedWorkspace?.id) return;

        try {
            const state = await indexWorkspace(selectedWorkspace.id);
            applyLineageIndexState(state);
            showNotification(
                (state?.resume_from_batch || 1) > 1 ? 'Indexing resumed' : 'Indexing started'
            );
        } catch (err) {
            setIndexing(false);
            showNotification(err.response?.data?.detail || 'Indexing failed', 'error');
        }
    };

    const openHistoryView = (view) => {
        if (!selectedWorkspace?.id) return;
        const normalizedView = view === 'history' ? 'history' : 'outputs';
        navigate(`/report-matching/workspaces/${selectedWorkspace.id}/history?view=${normalizedView}`);
    };

    const moveQueuedLegacyReport = (index, direction) => {
        setQueuedLegacyReports((prev) => {
            const nextIndex = index + direction;
            if (nextIndex < 0 || nextIndex >= prev.length) {
                return prev;
            }
            const next = [...prev];
            const [item] = next.splice(index, 1);
            next.splice(nextIndex, 0, item);
            return next;
        });
    };

    const handleCancelMatchJob = async (job) => {
        if (!selectedWorkspace?.id || !job?.id || !isMatchJobCancelable(job)) {
            return;
        }

        setStoppingMatchJobIds((prev) => ({ ...prev, [job.id]: true }));
        try {
            const updatedJob = await cancelReportMatchingJob(selectedWorkspace.id, job.id);
            setWorkspaceMatchJobs((prev) => prev.map((item) => (item.id === updatedJob.id ? updatedJob : item)));
            if (selectedLegacyReport?.report_name === updatedJob.legacy_report_name) {
                applyMatchJobState(updatedJob);
            }
            showNotification(updatedJob.progress_message || 'Stop request submitted', 'warning');
        } catch (err) {
            showNotification(err?.response?.data?.detail || 'Failed to stop matching job', 'error');
        } finally {
            setStoppingMatchJobIds((prev) => {
                const next = { ...prev };
                delete next[job.id];
                return next;
            });
        }
    };

    const handleDeleteMatchJob = async (job) => {
        if (!selectedWorkspace?.id || !job?.id || !isMatchJobDeletable(job)) {
            return;
        }
        if (!window.confirm(`Delete failed queue entry for "${job.legacy_report_name}"?`)) {
            return;
        }

        try {
            await deleteReportMatchingJob(selectedWorkspace.id, job.id);
            setWorkspaceMatchJobs((prev) => prev.filter((item) => item.id !== job.id));
            if (activeMatchJob?.id === job.id) {
                applyMatchJobState(null);
            }
            showNotification('Failed job deleted');
        } catch (err) {
            showNotification(err?.response?.data?.detail || 'Failed to delete job', 'error');
        }
    };

    const handleCancelRecommendationJob = async (job) => {
        if (!selectedWorkspace?.id || !job?.id || !isRecommendationJobCancelable(job)) {
            return;
        }

        setStoppingRecommendationJobIds((prev) => ({ ...prev, [job.id]: true }));
        try {
            const updatedJob = await cancelRecommendationJob(selectedWorkspace.id, job.id);
            if (activeRecommendationJob?.id === updatedJob.id) {
                applyRecommendationJobState(updatedJob);
            }
            showNotification(updatedJob.progress_message || 'Stop request submitted', 'warning');
        } catch (err) {
            showNotification(err?.response?.data?.detail || 'Failed to stop recommendation job', 'error');
        } finally {
            setStoppingRecommendationJobIds((prev) => {
                const next = { ...prev };
                delete next[job.id];
                return next;
            });
        }
    };

    const handleFindMatches = async () => {
        if (!selectedWorkspace?.id || queuedLegacyReports.length === 0) return;
        if (!lineageMeta?.is_indexed) {
            showNotification('Please index Catalyst columns before matching.', 'warning');
            return;
        }

        try {
            setQueueSubmitting(true);
            setGapAnalysis(null);
            resetWorkspaceDecisionAutosaveState();
            setSelectedMatch(null);
            setSavedRecommendationAvailable(false);
            setRecommendations(null);
            setSelectedRecommendation(null);
            applyRecommendationJobState(null);
            setMatchOnlyLayout(false);
            setAutoOpenQueuedResult(queuedLegacyReports.length === 1);

            const jobSettled = await Promise.allSettled(
                queuedLegacyReports.map((report) => startReportMatchingJob(
                    selectedWorkspace.id,
                    report.report_name,
                    {
                        topN,
                        standardTopN: topN,
                        customTopN,
                        standardOtherThemeTopN,
                        finalMatchThreshold,
                        sameThemeOnly,
                        globalThemeOnly,
                        ignoreCommon,
                        ignorePII: ignorePii,
                        includeCustom,
                    }
                ))
            );

            const jobResults = [];
            const failedReports = [];
            jobSettled.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    jobResults.push(result.value);
                    return;
                }
                failedReports.push(
                    `${queuedLegacyReports[index]?.report_name || 'Unknown report'}: ${result.reason?.response?.data?.detail || result.reason?.message || 'Unable to queue'}`
                );
            });

            if (jobResults.length === 0) {
                throw new Error(failedReports[0] || 'Unable to queue reports');
            }

            const firstQueuedJob = jobResults[0] || null;
            const firstReport = firstQueuedJob
                ? queuedLegacyReports.find((report) => report.report_name === firstQueuedJob.legacy_report_name) || null
                : null;
            if (firstReport) {
                setSelectedLegacyReport(firstReport);
                const firstJob = jobResults.find((job) => job.legacy_report_name === firstReport.report_name) || null;
                applyMatchJobState(firstJob);
            }

            setQueuedLegacyReports([]);
            await loadWorkspaceMatchJobs(selectedWorkspace.id);
            await loadSavedMatchings(selectedWorkspace.id);

            const queuedCount = jobResults.filter((job) => ['queued', 'running'].includes(job?.status)).length;
            const readyCount = jobResults.filter((job) => job?.status === 'completed').length;
            const message = [
                queuedCount ? `${queuedCount} queued` : '',
                readyCount ? `${readyCount} already ready` : '',
            ].filter(Boolean).join(' • ');
            showNotification(message || 'Reports accepted for background processing');
            if (failedReports.length) {
                showNotification(failedReports[0], 'warning');
            }
        } catch (err) {
            showNotification(err.response?.data?.detail || err.message || 'Matching failed', 'error');
        } finally {
            setQueueSubmitting(false);
        }
    };

    const handleViewQueuedReportMatches = async (report) => {
        if (!selectedWorkspace?.id || !report?.report_name) return;
        try {
            await loadSavedMatchingIntoView(selectedWorkspace.id, report.report_name, { silent: false });
        } catch (err) {
            showNotification(err?.response?.data?.detail || 'Failed to load saved matching', 'error');
        }
    };

    const handleQueueQueuedReport = async (report) => {
        if (!selectedWorkspace?.id || !report?.report_name) return;
        if (!lineageMeta?.is_indexed) {
            showNotification('Please index Catalyst columns before matching.', 'warning');
            return;
        }

        try {
            setQueueSubmitting(true);
            const job = await startReportMatchingJob(
                selectedWorkspace.id,
                report.report_name,
                {
                    topN,
                    standardTopN: topN,
                    customTopN,
                    standardOtherThemeTopN,
                    finalMatchThreshold,
                    sameThemeOnly,
                    globalThemeOnly,
                    ignoreCommon,
                    ignorePII: ignorePii,
                    includeCustom,
                }
            );
            setSelectedLegacyReport(report);
            applyMatchJobState(job);
            setAutoOpenQueuedResult(true);
            await loadWorkspaceMatchJobs(selectedWorkspace.id);
            showNotification(`${report.report_name} queued for report matching`);
        } catch (err) {
            showNotification(err?.response?.data?.detail || 'Failed to queue report', 'error');
        } finally {
            setQueueSubmitting(false);
        }
    };

    const handleSelectMatch = async (match) => {
        const resolvedMatch = getAllTopMatches(matchResults).find(
            (m) => m.catalyst_report_name === match.catalyst_report_name
        ) || match;

        setSelectedMatch(resolvedMatch);
        setRecommendations(null);
        setSelectedRecommendation(null);
        applyRecommendationJobState(null);
        setGapAnalysis(null);
        resetWorkspaceDecisionAutosaveState();
        refreshSavedRecommendationAvailability(
            selectedWorkspace?.id,
            selectedLegacyReport?.report_name,
            resolvedMatch.catalyst_report_name,
        );

        // OPTIMIZATION: If Stage 2 already verified the report, skip redundant LLM validation
        if (resolvedMatch.stage2_verified) {
            const activeMatchedColumns = getActiveMatchedColumns(resolvedMatch.matched_columns);
            const totalLegacyColumns = matchResults.filtered_columns || matchResults.legacy_breakdown?.unique_rows || matchResults.total_legacy_columns;
            const mergedGaps = mergeInvalidatedMatchesIntoGaps(
                resolvedMatch.matched_columns,
                resolvedMatch.invalidated_matches,
                resolvedMatch.gaps
            );
            setGapAnalysis({
                legacy_report: matchResults.legacy_report,
                catalyst_report: resolvedMatch.catalyst_report_name,
                total_legacy_columns: totalLegacyColumns,
                matched_count: activeMatchedColumns.length,
                gap_count: mergedGaps.length,
                invalidated_count: resolvedMatch.invalidated_matches?.length || 0,
                recovered_count: resolvedMatch.recovered_count || 0,
                overlap_count: activeMatchedColumns.length,
                overlap_percentage: getCoveragePercentage(activeMatchedColumns.length, totalLegacyColumns),
                legacy_breakdown: matchResults.legacy_breakdown,
                catalyst_breakdown: resolvedMatch.catalyst_breakdown,
                legacy_removed_common: matchResults?.legacy_removed_common || matchResults?.removed_common || [],
                legacy_removed_pii: matchResults?.legacy_removed_pii || matchResults?.removed_pii || [],
                catalyst_removed_common: resolvedMatch?.catalyst_removed_common || [],
                catalyst_removed_pii: resolvedMatch?.catalyst_removed_pii || [],
                legacy_breakdown_layout: matchResults.legacy_breakdown_layout,
                catalyst_breakdown_layout: resolvedMatch.catalyst_breakdown_layout,
                legacy_raw_expression_rows: matchResults?.legacy_raw_expression_rows || [],
                matched_columns: activeMatchedColumns,
                invalidated_matches: [], // Merged into gaps
                gaps: mergedGaps,
                all_catalyst_columns: resolvedMatch.all_catalyst_columns || [],
                analysis_source: 'pre_verified',
                telemetry: matchResults?.telemetry || null,
            });
            setDecidedGaps(buildInitialGapDecisions(mergedGaps));
            resetWorkspaceDecisionAutosaveState();

            const initialMatchSelections = {};
            activeMatchedColumns.forEach(m => {
                initialMatchSelections[m.legacy_key] = getMatchCandidateKey(
                    selectPreferredMatchCandidate(mergeMatchCandidates(m))
                    || {
                        catalyst_logical: m.catalyst_column,
                        catalyst_physical: m.catalyst_physical,
                        catalyst_table: m.catalyst_table,
                    }
                );
            });
            setMatchSelections(initialMatchSelections);

            setActiveStep(4);
            return;
        }

        try {
            setLoading(true);
            setRunningLlm(true);
            showNotification('Analyzing gaps with AI... This may take a moment.', 'info');

            const result = await analyzeGaps(
                selectedWorkspace.id,
                selectedLegacyReport.report_name,
                resolvedMatch.catalyst_report_name,
                {
                    ignoreCommon,
                    ignorePii,
                    matchOnlyLayout,
                }
            );

            const activeMatchedColumns = getActiveMatchedColumns(result.matched_columns);
            const mergedGaps = mergeInvalidatedMatchesIntoGaps(
                result.matched_columns,
                result.invalidated_matches,
                result.gaps
            );
            setGapAnalysis({
                ...result,
                legacy_report: result.legacy_report || selectedLegacyReport.report_name,
                catalyst_report: result.catalyst_report || resolvedMatch.catalyst_report_name,
                legacy_removed_common: result.legacy_removed_common || [],
                legacy_removed_pii: result.legacy_removed_pii || [],
                catalyst_removed_common: result.catalyst_removed_common || [],
                catalyst_removed_pii: result.catalyst_removed_pii || [],
                legacy_breakdown_layout: matchResults?.legacy_breakdown_layout || null,
                catalyst_breakdown_layout: resolvedMatch.catalyst_breakdown_layout || null,
                matched_columns: activeMatchedColumns,
                matched_count: activeMatchedColumns.length,
                overlap_count: activeMatchedColumns.length,
                overlap_percentage: getCoveragePercentage(activeMatchedColumns.length, result.total_legacy_columns),
                gap_count: mergedGaps.length,
                invalidated_matches: [], // Merged into gaps
                gaps: mergedGaps,
                analysis_source: 'api_with_llm'
            });
            setDecidedGaps(buildInitialGapDecisions(mergedGaps));
            resetWorkspaceDecisionAutosaveState();

            const initialMatchSelections = {};
            activeMatchedColumns.forEach(m => {
                initialMatchSelections[m.legacy_key] = getMatchCandidateKey(
                    selectPreferredMatchCandidate(mergeMatchCandidates(m))
                    || {
                        catalyst_logical: m.catalyst_column,
                        catalyst_physical: m.catalyst_physical,
                        catalyst_table: m.catalyst_table,
                    }
                );
            });
            setMatchSelections(initialMatchSelections);

            setActiveStep(4);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to analyze gaps', 'error');
        } finally {
            setLoading(false);
            setRunningLlm(false);
        }
    };

    const handleAcceptValidatedMatch = async (match) => {
        try {
            if (String(match?.preference_status || '').toLowerCase() === 'accepted') {
                const preferenceId = getMatchPreferenceId(match);
                if (preferenceId) {
                    try {
                        await deleteReportMatchingPreference(preferenceId);
                    } catch (err) {
                        if (err?.response?.status !== 404) {
                            throw err;
                        }
                    }
                }
                clearMatchedPreferenceStatus(match);
                showNotification('Accepted state cleared', 'info');
                return;
            }

            const record = await persistPreferenceDecision(buildPreferenceDecisionPayload({
                legacyLogical: match.legacy_column,
                legacyPhysical: match.legacy_physical,
                legacyTable: match.legacy_table,
                catalystLogical: match.catalyst_column,
                catalystPhysical: match.catalyst_physical,
                catalystTable: match.catalyst_table,
                columnType: match.column_type,
                matchType: match.match_type,
                status: 'accepted',
                source: 'validated_match',
            }));
            applyMatchedPreferenceRecord(match.legacy_key, match, record);
            showNotification('User preference saved for this validated match', 'success');
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to save preference', 'error');
        }
    };

    const handleRejectMatch = async (match) => {
        try {
            await persistPreferenceDecision(buildPreferenceDecisionPayload({
                legacyLogical: match.legacy_column,
                legacyPhysical: match.legacy_physical,
                legacyTable: match.legacy_table,
                catalystLogical: match.catalyst_column,
                catalystPhysical: match.catalyst_physical,
                catalystTable: match.catalyst_table,
                columnType: match.column_type,
                matchType: match.match_type,
                status: 'rejected',
                source: 'validated_match_reject',
            }));
            rejectMatchedInView(match);
            showNotification('Match rejected and moved to gaps', 'info');
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to save preference', 'error');
        }
    };

    const buildWorkspaceDecisionSnapshot = (analysis = gapAnalysis, selections = matchSelections) => JSON.stringify({
        catalyst_report: analysis?.catalyst_report || '',
        matched_columns: analysis?.matched_columns || [],
        gaps: analysis?.gaps || [],
        invalidated_matches: analysis?.invalidated_matches || [],
        matchSelections: selections || {},
    });

    const resetWorkspaceDecisionAutosaveState = () => {
        autosaveLastFailedSnapshotRef.current = '';
        setWorkspaceDecisionDirty(false);
    };

    const persistWorkspaceDecisions = async ({ silent = true } = {}) => {
        const currentGapAnalysis = latestGapAnalysisRef.current;
        const currentMatchSelections = latestMatchSelectionsRef.current;
        if (!selectedWorkspace?.id || !selectedLegacyReport || !currentGapAnalysis || !matchResults) {
            return false;
        }

        const snapshot = buildWorkspaceDecisionSnapshot(currentGapAnalysis, currentMatchSelections);

        try {
            setSaving(true);
            const finalMatched = (currentGapAnalysis.matched_columns || []).map((m) => {
                const selectedCandidateKey = currentMatchSelections[m.legacy_key];
                const currentCandidateKey = getMatchCandidateKey({
                    catalyst_logical: m.catalyst_column,
                    catalyst_physical: m.catalyst_physical,
                    catalyst_table: m.catalyst_table,
                });
                if (selectedCandidateKey && selectedCandidateKey !== currentCandidateKey) {
                    const selectedCand = m.candidates?.find((cand) => getMatchCandidateKey(cand) === selectedCandidateKey);
                    if (selectedCand) {
                        return {
                            ...m,
                            catalyst_column: selectedCand.catalyst_logical,
                            catalyst_physical: selectedCand.catalyst_physical || '',
                            catalyst_table: selectedCand.catalyst_table || m.catalyst_table || '',
                            score: selectedCand.score,
                            match_type: selectedCand.match_type || m.match_type,
                            report_layout: selectedCand.report_layout || false,
                        };
                    }
                }
                return m;
            });

            const updatedMatchResults = updateMatchCollections(matchResults, (m) => {
                if (m.catalyst_report_name === currentGapAnalysis.catalyst_report) {
                    const metrics = buildReportMatchMetrics(
                        m,
                        finalMatched,
                        currentGapAnalysis.total_legacy_columns || matchResults.filtered_columns || matchResults.total_legacy_columns || 0
                    );
                    return {
                        ...m,
                        ...metrics,
                        invalidated_matches: currentGapAnalysis.invalidated_matches,
                        gaps: currentGapAnalysis.gaps,
                        stage2_verified: true,
                    };
                }
                return m;
            });

            await saveMatchingResult(selectedWorkspace.id, {
                legacy_report_name: selectedLegacyReport.report_name,
                match_results: updatedMatchResults,
                options: { topN, standardTopN: topN, customTopN, standardOtherThemeTopN, finalMatchThreshold, sameThemeOnly, globalThemeOnly, ignoreCommon, ignorePii, includeCustom },
            });

            setMatchResults(updatedMatchResults);
            autosaveLastFailedSnapshotRef.current = '';
            if (buildWorkspaceDecisionSnapshot(latestGapAnalysisRef.current, latestMatchSelectionsRef.current) === snapshot) {
                setWorkspaceDecisionDirty(false);
            }
            if (!silent) {
                showNotification('Workspace decisions saved successfully', 'success');
            }
            return true;
        } catch (err) {
            autosaveLastFailedSnapshotRef.current = snapshot;
            showNotification(silent ? 'Failed to auto-save decisions' : 'Failed to save decisions', 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };
    const handleExportExcel = async () => {
        if (!gapAnalysis) return;

        try {
            setExporting(true);
            const blob = await exportGapAnalysis({
                legacy_report: gapAnalysis.legacy_report,
                catalyst_report: gapAnalysis.catalyst_report,
                matched_columns: gapAnalysis.matched_columns || [],
                gaps: gapAnalysis.gaps || [],
                summary: {
                    total_legacy_columns: gapAnalysis.total_legacy_columns,
                    matched_count: gapAnalysis.matched_count,
                    gap_count: gapAnalysis.gap_count,
                    overlap_percentage: gapAnalysis.overlap_percentage
                }
            });

            // Trigger download
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            const filename = `${gapAnalysis.legacy_report.replace(/\s+/g, '_')}_vs_${gapAnalysis.catalyst_report.replace(/\s+/g, '_')}_Mapping.xlsx`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            showNotification('Excel report downloaded successfully!', 'success');
        } catch (err) {
            console.error('Export failed:', err);
            showNotification('Failed to export Excel report.', 'error');
        } finally {
            setExporting(false);
        }
    };

    const handleGetRecommendations = async () => {
        if (!selectedWorkspace?.id || !selectedLegacyReport || !selectedMatch) return;

        try {
            setRecommending(true);
            const job = await startRecommendationJob(selectedWorkspace.id, {
                legacy_report_name: selectedLegacyReport.report_name,
                catalyst_report_name: selectedMatch.catalyst_report_name,
                ignore_common: ignoreCommon,
                ignore_pii: ignorePii,
                same_theme_only: sameThemeOnly,
                global_theme_only: globalThemeOnly,
                gaps: gapView?.gaps || [],
                force_refresh: true,
                include_custom: includeCustom
            });
            applyRecommendationJobState(job);
            if (job.status === 'completed') {
                await loadSavedRecommendationIntoView(
                    selectedWorkspace.id,
                    selectedLegacyReport.report_name,
                    selectedMatch.catalyst_report_name,
                    { silent: false, activateRecommendationStep: true }
                );
            } else {
                showNotification(getRecommendationQueueStartMessage(job));
            }
        } catch (err) {
            setRecommending(false);
            showNotification(err.response?.data?.detail || 'Failed to get recommendations', 'error');
        }
    };

    const hasRecommendationsForSelectedMatch = (
        recommendations?.legacy_report === selectedLegacyReport?.report_name
        && recommendations?.catalyst_report === selectedMatch?.catalyst_report_name
    );

    const hasCompletedRecommendationForSelectedMatch = (
        hasRecommendationsForSelectedMatch
        || savedRecommendationAvailable
        || (
            activeRecommendationJob?.status === 'completed'
            && activeRecommendationJob?.saved_recommendation
            && activeRecommendationJob?.legacy_report_name === selectedLegacyReport?.report_name
            && activeRecommendationJob?.catalyst_report_name === selectedMatch?.catalyst_report_name
        )
    );

    const handleOpenRecommendations = async () => {
        if (!selectedWorkspace?.id || !selectedLegacyReport?.report_name || !selectedMatch?.catalyst_report_name) {
            return;
        }

        if (hasRecommendationsForSelectedMatch) {
            setActiveStep(5);
            return;
        }

        try {
            setRecommending(true);
            await loadSavedRecommendationIntoView(
                selectedWorkspace.id,
                selectedLegacyReport.report_name,
                selectedMatch.catalyst_report_name,
                { silent: false, activateRecommendationStep: true }
            );
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to load recommendations', 'error');
        } finally {
            setRecommending(false);
        }
    };

    const handleExportRecommendations = async () => {
        if (!recommendations || !gapView) return;

        try {
            const fullGapState = gapAnalysis || gapView;
            const selectedLegacyReportFromList = (classificationReports?.legacy || []).find(
                (item) => item?.report_name === gapView.legacy_report
            ) || null;
            const selectedCatalystReport = (classificationReports?.catalyst || []).find(
                (item) => item?.report_name === gapView.catalyst_report
            ) || null;
            const legacyBreakdown = fullGapState?.legacy_breakdown || null;
            const dedupLegacyFields = Number(legacyBreakdown?.dedup_rows);
            const totalLegacyFields = Number.isFinite(dedupLegacyFields)
                ? Math.max(
                    dedupLegacyFields
                    - Number(legacyBreakdown?.missing_logical ?? 0)
                    - Number(legacyBreakdown?.ignored_common ?? 0)
                    - Number(legacyBreakdown?.ignored_pii ?? 0),
                    0
                )
                : Number(
                    legacyBreakdown?.unique_rows
                    ?? gapView.total_legacy_columns
                    ?? 0
                );
            const usedOnReportLayout = Number(
                fullGapState?.legacy_breakdown_layout?.unique_rows
                ?? gapView.total_legacy_columns
                ?? 0
            );
            const notUsedOnReportLayout = Math.max(totalLegacyFields - usedOnReportLayout, 0);
            const allGapFields = fullGapState?.gaps || gapView.gaps || [];
            const usedOnReportLayoutGapFields = allGapFields.filter((item) => isLayoutYes(item?.report_layout)).length;

            setExporting(true);
            const blob = await exportRecommendations({
                legacy_report: gapView.legacy_report,
                catalyst_report: gapView.catalyst_report,
                legacy_report_path: selectedLegacyReport?.report_path || selectedLegacyReportFromList?.report_path || '',
                catalyst_report_path: selectedMatch?.catalyst_report_path || selectedCatalystReport?.report_path || '',
                recommendations: recommendations.recommendations || [],
                matched_columns: gapView.matched_columns || [],
                legacy_removed_common: fullGapState?.legacy_removed_common || gapView.legacy_removed_common || [],
                legacy_removed_pii: fullGapState?.legacy_removed_pii || gapView.legacy_removed_pii || [],
                catalyst_removed_common: fullGapState?.catalyst_removed_common || gapView.catalyst_removed_common || [],
                catalyst_removed_pii: fullGapState?.catalyst_removed_pii || gapView.catalyst_removed_pii || [],
                summary: {
                    ...(recommendations.summary || {}),
                    total_legacy_fields: totalLegacyFields,
                    used_on_report_layout_fields: usedOnReportLayout,
                    not_used_on_report_layout_fields: notUsedOnReportLayout,
                    used_on_report_layout_gap_fields: usedOnReportLayoutGapFields,
                },
                options: recommendations.options || {},
                generated_at: recommendations.generated_at || ''
            });

            // Trigger download
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            const filename = `${gapView.legacy_report.replace(/\s+/g, '_')}_vs_${gapView.catalyst_report.replace(/\s+/g, '_')}_Mapping_Recommendations.xlsx`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            showNotification('Recommendations report downloaded successfully!', 'success');
        } catch (err) {
            console.error('Export failed:', err);
            showNotification('Failed to export recommendations report.', 'error');
        } finally {
            setExporting(false);
        }
    };

    const handleLoadSavedMatching = async (reportName) => {
        try {
            setLoading(true);
            await loadSavedMatchingIntoView(selectedWorkspace.id, reportName, { silent: true });
        } catch (err) {
            showNotification('Failed to load saved matching', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMatching = async (e, reportName) => {
        e.stopPropagation();
        try {
            await deleteMatchingResult(selectedWorkspace.id, reportName);
            showNotification('Matching deleted');
            if (selectedLegacyReport?.report_name === reportName) {
                setMatchResults(null);
                setSelectedMatch(null);
                setGapAnalysis(null);
                resetWorkspaceDecisionAutosaveState();
                setRecommendations(null);
                setSelectedRecommendation(null);
                applyMatchJobState(null);
                applyRecommendationJobState(null);
                setMatchSelections({});
                setActiveStep(2);
            }
            setRecommendations((prev) => {
                if (!prev) return prev;
                return prev.legacy_report === reportName ? null : prev;
            });
            await Promise.all([
                loadSavedMatchings(selectedWorkspace.id),
                loadWorkspaceMatchJobs(selectedWorkspace.id),
            ]);
        } catch (err) {
            showNotification('Failed to delete matching', 'error');
        }
    };

    const handleRefreshRecommendations = async () => {
        if (!selectedWorkspace?.id || !selectedLegacyReport || !selectedMatch || !gapView) return;

        const currentGaps = Array.isArray(gapView.gaps) ? gapView.gaps : [];
        const savedRecommendationRows = Array.isArray(recommendations?.recommendations) ? recommendations.recommendations : [];
        const savedGapIds = new Set(savedRecommendationRows.map((item) => getRecommendationGapIdentity(item)).filter(Boolean));
        const currentGapIds = new Set(currentGaps.map((item) => getRecommendationGapIdentity(item)).filter(Boolean));

        const changedGaps = currentGaps.filter((item) => !savedGapIds.has(getRecommendationGapIdentity(item)));
        const removedGapCount = savedRecommendationRows.filter((item) => !currentGapIds.has(getRecommendationGapIdentity(item))).length;

        if (changedGaps.length === 0 && removedGapCount === 0) {
            showNotification('No changed gap fields to refresh');
            return;
        }

        try {
            setRecommending(true);
            const job = await startRecommendationJob(selectedWorkspace.id, {
                legacy_report_name: selectedLegacyReport.report_name,
                catalyst_report_name: selectedMatch.catalyst_report_name,
                ignore_common: ignoreCommon,
                ignore_pii: ignorePii,
                same_theme_only: sameThemeOnly,
                global_theme_only: globalThemeOnly,
                gaps: changedGaps,
                all_gaps: currentGaps,
                merge_with_existing: true,
                force_refresh: true,
                include_custom: includeCustom,
            });
            applyRecommendationJobState(job);
            if (job.status === 'completed') {
                await loadSavedRecommendationIntoView(
                    selectedWorkspace.id,
                    selectedLegacyReport.report_name,
                    selectedMatch.catalyst_report_name,
                    { silent: false, activateRecommendationStep: true }
                );
            } else {
                showNotification(
                    getRecommendationQueueStartMessage(
                        job,
                        `Recommendation refresh queued for ${changedGaps.length || removedGapCount} changed field(s)`
                    )
                );
            }
        } catch (err) {
            setRecommending(false);
            showNotification(err.response?.data?.detail || 'Failed to refresh recommendations', 'error');
        }
    };

    const handleDeleteRecommendations = async () => {
        if (!selectedWorkspace?.id || !selectedLegacyReport?.report_name || !selectedMatch?.catalyst_report_name) {
            return;
        }
        if (!window.confirm(`Delete saved recommendations for "${selectedLegacyReport.report_name}" vs "${selectedMatch.catalyst_report_name}"?`)) {
            return;
        }

        try {
            await deleteSavedRecommendation(
                selectedWorkspace.id,
                selectedLegacyReport.report_name,
                selectedMatch.catalyst_report_name,
            );
            setRecommendations(null);
            setSelectedRecommendation(null);
            setSavedRecommendationAvailable(false);
            setActiveStep(4);
            setActiveRecommendationJob((prev) => {
                if (!prev) return prev;
                if (
                    prev.legacy_report_name !== selectedLegacyReport.report_name
                    || prev.catalyst_report_name !== selectedMatch.catalyst_report_name
                ) {
                    return prev;
                }
                return {
                    ...prev,
                    saved_recommendation: null,
                };
            });
            await loadSavedMatchings(selectedWorkspace.id);
            showNotification('Recommendations deleted');
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to delete recommendations', 'error');
        }
    };

    const getMatchColor = (pct) => {
        if (pct >= 70) return 'success';
        if (pct >= 40) return 'warning';
        return 'error';
    };

    const getAllTopMatches = (results) => {
        if (!results) return [];
        if (Array.isArray(results.top_matches) && results.top_matches.length > 0) {
            return results.top_matches;
        }
        return [
            ...(results.standard_top_matches || []),
            ...(results.custom_top_matches || []),
            ...(results.standard_other_theme_top_matches || []),
        ];
    };

    const isCustomCatalystReport = (match) => {
        if (typeof match?.is_custom_report === 'boolean') return match.is_custom_report;
        const reportType = String(match?.catalyst_report_type || '').trim().toLowerCase();
        return reportType.includes('custom');
    };

    const getMatchCardPalette = (match) => {
        if (isCustomCatalystReport(match)) {
            return {
                border: '#FFB75D',
                hoverBorder: '#F08A24',
                background: alpha('#F08A24', 0.06),
                badgeLabel: 'Custom',
                badgeColor: 'warning',
            };
        }
        return {
            border: '#27C46B',
            hoverBorder: '#0E9F4B',
            background: alpha('#27C46B', 0.06),
            badgeLabel: 'Standard',
            badgeColor: 'success',
        };
    };

    const getCatalystReportTypeLabel = (match) => {
        const reportType = String(match?.catalyst_report_type || '').trim();
        if (reportType) return reportType;
        return isCustomCatalystReport(match) ? 'Custom' : 'Standard';
    };

    const renderTruncatedChip = (label, options = {}) => {
        const {
            chipProps = {},
            tooltipTitle,
            maxWidth = 160,
            width,
            minWidth,
        } = options;
        const resolvedLabel = String(label || '').trim();
        if (!resolvedLabel) return null;
        const colorKey = chipProps.color || 'default';
        const paletteEntry = colorKey !== 'default' ? theme.palette[colorKey] : null;
        const outlined = chipProps.variant === 'outlined';
        const borderColor = paletteEntry?.main || theme.palette.divider;
        const textColor = paletteEntry?.main || theme.palette.text.secondary;
        const backgroundColor = outlined
            ? alpha(borderColor, 0.04)
            : (paletteEntry?.main || theme.palette.grey[300]);
        return (
            <Tooltip title={tooltipTitle || resolvedLabel} arrow placement="top">
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 22,
                        maxWidth,
                        width,
                        minWidth,
                        px: 1,
                        borderRadius: 999,
                        border: '1px solid',
                        borderColor,
                        backgroundColor,
                        color: textColor,
                        fontSize: '0.72rem',
                        fontWeight: 500,
                        lineHeight: 1,
                        boxSizing: 'border-box',
                        verticalAlign: 'middle',
                        ...(chipProps.sx || {}),
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            display: 'block',
                            width: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                        }}
                    >
                        {resolvedLabel}
                    </Box>
                </Box>
            </Tooltip>
        );
    };

    const getApprovalColor = (status) => {
        if (status === 'approved') return 'success';
        return 'warning';
    };

    const updateMatchCollections = (results, updater) => {
        if (!results) return results;
        const updateList = (matches = []) => sortTopMatchesByMetrics(matches.map((match) => updater(match)));
        const combinedMatches = getAllTopMatches(results);
        const standardSource = Array.isArray(results.standard_top_matches)
            ? results.standard_top_matches
            : combinedMatches.filter((match) => !isCustomCatalystReport(match));
        const customSource = Array.isArray(results.custom_top_matches)
            ? results.custom_top_matches
            : combinedMatches.filter((match) => isCustomCatalystReport(match));
        const standardOtherThemeSource = Array.isArray(results.standard_other_theme_top_matches)
            ? results.standard_other_theme_top_matches
            : [];
        return {
            ...results,
            standard_top_matches: updateList(standardSource),
            custom_top_matches: updateList(customSource),
            standard_other_theme_top_matches: updateList(standardOtherThemeSource),
            top_matches: updateList(combinedMatches),
        };
    };

    const formatFieldTypeLabel = (value) => {
        const normalized = String(value || '').trim().toLowerCase();
        if (!normalized) return 'Unknown';
        if (normalized === 'sql') return 'SQL';
        if (normalized === 'filter') return 'Filter';
        if (normalized === 'join') return 'Join';
        if (normalized === 'base') return 'Base';
        if (normalized === 'derived') return 'Derived';
        if (normalized === 'others') return 'Others';
        return normalized;
    };

    const renderLogicalNameWithTypeTooltip = (label, fieldType, content) => {
        const resolvedLabel = label || '-';
        const resolvedType = formatFieldTypeLabel(fieldType);
        return (
            <Tooltip
                title={`Field Type: ${resolvedType}`}
                arrow
                placement="top"
            >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}>
                    {content || (
                        <Typography variant="body2" sx={tableCellWrapSx}>
                            {resolvedLabel}
                        </Typography>
                    )}
                </Box>
            </Tooltip>
        );
    };

    useEffect(() => {
        if (!classificationDialogOpen) return;
        const keywordMap = getKeywordMapForMode();
        const themeNames = Object.keys(keywordMap);
        if (!themeNames.length) {
            setSelectedKeywordTheme('');
            return;
        }
        if (!selectedKeywordTheme || !themeNames.includes(selectedKeywordTheme)) {
            setSelectedKeywordTheme(themeNames.sort((left, right) => String(left).localeCompare(String(right)))[0]);
        }
    }, [
        classificationDialogOpen,
        classificationMode,
        classificationDialogSystem,
        localThemeKeywords,
        globalThemeKeywords
    ]);

    useEffect(() => {
        if (!compareLegacyReport) return;
        const stillExists = compareLegacyOptions.some((report) => report.report_name === compareLegacyReport.report_name);
        if (!stillExists) {
            setCompareLegacyReport(null);
            setCompareReportData(null);
        }
    }, [compareLegacyOptions, compareLegacyReport]);

    useEffect(() => {
        if (!compareCatalystReport) return;
        const stillExists = compareCatalystOptions.some((report) => report.report_name === compareCatalystReport.report_name);
        if (!stillExists) {
            setCompareCatalystReport(null);
            setCompareReportData(null);
        }
    }, [compareCatalystOptions, compareCatalystReport]);

    useEffect(() => {
        if (!selectedWorkspace?.id || !selectedWorkspace?.lineage_file_id) {
            applyLineageClassificationState(null);
            return undefined;
        }

        let cancelled = false;

        const syncClassificationStatus = async () => {
            try {
                const status = await getWorkspaceClassificationStatus(selectedWorkspace.id);
                if (cancelled) return;
                applyLineageClassificationState(status);

                if (status.status === 'completed' && !selectedWorkspace?.is_classified) {
                    await loadSelectedWorkspace(selectedWorkspace.id);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load lineage classification status:', err);
                }
            }
        };

        syncClassificationStatus();

        if (!isLineageClassificationActive(lineageClassificationState)) {
            return () => {
                cancelled = true;
            };
        }

        const intervalId = window.setInterval(syncClassificationStatus, 2500);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [selectedWorkspace?.id, selectedWorkspace?.lineage_file_id, selectedWorkspace?.is_classified, lineageClassificationState?.status]);

    useEffect(() => {
        if (!selectedWorkspace?.id || !selectedWorkspace?.lineage_file_id) {
            applyLineageIndexState(null);
            return undefined;
        }

        let cancelled = false;

        const syncIndexStatus = async () => {
            try {
                const status = await getWorkspaceIndexStatus(selectedWorkspace.id);
                if (cancelled) return;
                applyLineageIndexState(status);

                if (status.status === 'completed') {
                    if (!lineageMeta?.is_indexed) {
                        await loadLineageMeta(selectedWorkspace.lineage_file_id);
                    }
                    setActiveStep(2);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load lineage indexing status:', err);
                }
            }
        };

        syncIndexStatus();

        if (!isLineageIndexActive(lineageIndexState)) {
            return () => {
                cancelled = true;
            };
        }

        const intervalId = window.setInterval(syncIndexStatus, 2500);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [selectedWorkspace?.id, selectedWorkspace?.lineage_file_id, lineageIndexState?.status, lineageMeta?.is_indexed]);

    useEffect(() => {
        const reportParam = location.state?.openReportName || new URLSearchParams(location.search).get('report') || '';
        if (!reportParam) {
            autoOpenedHistoryReportRef.current = '';
            return;
        }
        if (!selectedWorkspace?.id || !savedMatchingByReport.has(reportParam)) {
            return;
        }

        const handledKey = `${selectedWorkspace.id}:${reportParam}`;
        if (autoOpenedHistoryReportRef.current === handledKey) {
            return;
        }

        autoOpenedHistoryReportRef.current = handledKey;
        loadSavedMatchingIntoView(selectedWorkspace.id, reportParam, { silent: true }).catch((err) => {
            console.error('Failed to auto-open saved matching from history:', err);
        });
        if (location.state?.openReportName) {
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.search, location.state, navigate, savedMatchingByReport, selectedWorkspace?.id]);

    useEffect(() => {
        if (!selectedWorkspace?.id || !selectedLegacyReport?.report_name) {
            applyMatchJobState(null);
            return;
        }

        let isMounted = true;

        const restoreLatestJob = async () => {
            try {
                const jobs = await listReportMatchingJobs(selectedWorkspace.id, {
                    legacyReportName: selectedLegacyReport.report_name,
                    limit: 1
                });
                if (!isMounted) return;
                const latestJob = Array.isArray(jobs) && jobs.length > 0 ? jobs[0] : null;
                const currentQueueJob = selectPrimaryMatchJob(workspaceMatchJobs, selectedLegacyReport.report_name);
                applyMatchJobState(isMatchJobActive(currentQueueJob) ? currentQueueJob : latestJob);
                if (
                    autoOpenQueuedResult
                    && latestJob?.status === 'completed'
                    && latestJob?.saved_matching
                    && matchResults?.legacy_report !== selectedLegacyReport.report_name
                ) {
                    await loadSavedMatchingIntoView(selectedWorkspace.id, selectedLegacyReport.report_name, { silent: true });
                } else if (
                    latestJob?.status === 'completed'
                    && latestJob?.saved_matching
                ) {
                    loadSavedMatchings(selectedWorkspace.id);
                }
            } catch (err) {
                console.error('Failed to restore matching job state:', err);
            }
        };

        restoreLatestJob();

        return () => {
            isMounted = false;
        };
    }, [autoOpenQueuedResult, matchResults?.legacy_report, selectedWorkspace?.id, selectedLegacyReport?.report_name]);

    useEffect(() => {
        if (!selectedWorkspace?.id) {
            completedMatchJobSignatureRef.current = '';
            setWorkspaceMatchJobs([]);
            return undefined;
        }

        let cancelled = false;

        const syncWorkspaceJobs = async () => {
            const jobs = await loadWorkspaceMatchJobs(selectedWorkspace.id);
            if (cancelled) return;
            const completedSignature = jobs
                .filter((job) => job.status === 'completed')
                .map((job) => job.id)
                .sort((left, right) => String(left).localeCompare(String(right)))
                .join('|');

            if (completedSignature !== completedMatchJobSignatureRef.current) {
                completedMatchJobSignatureRef.current = completedSignature;
                loadSavedMatchings(selectedWorkspace.id);
            }
        };

        syncWorkspaceJobs();
        if (!hasActiveWorkspaceMatchJobs) {
            return () => {
                cancelled = true;
            };
        }

        const intervalId = window.setInterval(syncWorkspaceJobs, REPORT_MATCHING_WORKSPACE_JOBS_POLL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [hasActiveWorkspaceMatchJobs, selectedWorkspace?.id, selectedLegacyReport?.report_name]);

    useEffect(() => {
        if (!selectedWorkspace?.id || !selectedLegacyReport?.report_name || !selectedMatch?.catalyst_report_name) {
            applyRecommendationJobState(null);
            setSavedRecommendationAvailable(false);
            return;
        }

        let isMounted = true;

        const checkSavedRecommendationAvailability = async () => {
            const available = await refreshSavedRecommendationAvailability(
                selectedWorkspace.id,
                selectedLegacyReport.report_name,
                selectedMatch.catalyst_report_name,
            );
            if (!isMounted) {
                return;
            }
            return available;
        };

        const restoreLatestRecommendationJob = async () => {
            try {
                const jobs = await listRecommendationJobs(selectedWorkspace.id, {
                    legacyReportName: selectedLegacyReport.report_name,
                    catalystReportName: selectedMatch.catalyst_report_name,
                    limit: 1
                });
                if (!isMounted) return;
                const latestJob = Array.isArray(jobs) && jobs.length > 0 ? jobs[0] : null;
                applyRecommendationJobState(latestJob);
                if (
                    latestJob?.status === 'completed'
                    && latestJob?.saved_recommendation
                    && (
                        recommendations?.legacy_report !== selectedLegacyReport.report_name
                        || recommendations?.catalyst_report !== selectedMatch.catalyst_report_name
                    )
                ) {
                    await loadSavedRecommendationIntoView(
                        selectedWorkspace.id,
                        selectedLegacyReport.report_name,
                        selectedMatch.catalyst_report_name,
                        { silent: true }
                    );
                }
            } catch (err) {
                console.error('Failed to restore recommendation job state:', err);
            }
        };

        checkSavedRecommendationAvailability();
        restoreLatestRecommendationJob();

        return () => {
            isMounted = false;
        };
    }, [selectedWorkspace?.id, selectedLegacyReport?.report_name, selectedMatch?.catalyst_report_name]);

    useEffect(() => {
        if (!selectedWorkspace?.id || !activeMatchJob?.id || !isMatchJobActive(activeMatchJob)) {
            return undefined;
        }

        let cancelled = false;

        const pollJob = async () => {
            try {
                const job = await getReportMatchingJob(selectedWorkspace.id, activeMatchJob.id);
                if (cancelled) return;
                applyMatchJobState(job);

                if (job.status === 'completed') {
                    if (autoOpenQueuedResult) {
                        await loadSavedMatchingIntoView(selectedWorkspace.id, job.legacy_report_name, { silent: false });
                    }
                    loadSavedMatchings(selectedWorkspace.id);
                    loadWorkspaceMatchJobs(selectedWorkspace.id);
                } else if (job.status === 'cancelled') {
                    showNotification(job.status_detail || job.progress_message || 'Matching cancelled', 'warning');
                } else if (job.status === 'failed' || job.status === 'interrupted') {
                    showNotification(job.status_detail || job.error || 'Matching failed', 'error');
                }
            } catch (err) {
                if (!cancelled) {
                    if (err?.response?.status === 404) {
                        applyMatchJobState(null);
                        loadWorkspaceMatchJobs(selectedWorkspace.id);
                        loadSavedMatchings(selectedWorkspace.id);
                        return;
                    }
                    console.error('Failed to poll report matching job:', err);
                }
            }
        };

        pollJob();
        const intervalId = window.setInterval(pollJob, REPORT_MATCHING_ACTIVE_JOB_POLL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [autoOpenQueuedResult, selectedWorkspace?.id, activeMatchJob?.id, activeMatchJob?.status]);

    useEffect(() => {
        if (!selectedWorkspace?.id || !activeRecommendationJob?.id || !isRecommendationJobActive(activeRecommendationJob)) {
            return undefined;
        }

        let cancelled = false;

        const pollJob = async () => {
            try {
                const job = await getRecommendationJob(selectedWorkspace.id, activeRecommendationJob.id);
                if (cancelled) return;
                applyRecommendationJobState(job);

                if (job.status === 'completed') {
                    await loadSavedRecommendationIntoView(
                        selectedWorkspace.id,
                        job.legacy_report_name,
                        job.catalyst_report_name,
                        { silent: false }
                    );
                } else if (job.status === 'cancelled') {
                    showNotification(job.status_detail || job.progress_message || 'Recommendation generation cancelled', 'warning');
                } else if (job.status === 'failed' || job.status === 'interrupted') {
                    showNotification(job.status_detail || job.error || 'Recommendation generation failed', 'error');
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to poll recommendation job:', err);
                }
            }
        };

        pollJob();
        const intervalId = window.setInterval(pollJob, 2500);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [selectedWorkspace?.id, activeRecommendationJob?.id, activeRecommendationJob?.status]);

    const formatCount = (value) => {
        if (value === null || value === undefined) {
            return '-';
        }
        const num = Number(value);
        if (Number.isNaN(num)) {
            return '-';
        }
        return num.toLocaleString();
    };

    const renderThemeStatsCard = (title, stats, system) => {
        const unclassifiedCount = stats.unclassified.length;
        return (
            <Card sx={{ borderRadius: 3, height: '100%' }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h6" fontWeight={700}>{title}</Typography>
                        <Chip
                            label={`${formatCount(stats.classifiedCount)}/${formatCount(stats.total)} classified`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    </Stack>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={4}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="overline" color="text.secondary">Total Reports</Typography>
                                <Typography variant="h6" fontWeight={700}>{formatCount(stats.total)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="overline" color="text.secondary">Classified</Typography>
                                <Typography variant="h6" fontWeight={700}>{formatCount(stats.classifiedCount)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="overline" color="text.secondary">Unclassified</Typography>
                                <Typography variant="h6" fontWeight={700}>{formatCount(unclassifiedCount)}</Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Theme Counts
                    </Typography>
                    {stats.themeEntries.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            No themes assigned yet.
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 220 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Theme</TableCell>
                                        <TableCell align="right">Reports</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stats.themeEntries.map((row) => (
                                        <TableRow key={row.themeName}>
                                            <TableCell>{row.themeName}</TableCell>
                                            <TableCell align="right">{formatCount(row.count)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Unclassified Reports ({formatCount(unclassifiedCount)})
                    </Typography>
                    {unclassifiedCount === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            All reports are classified.
                        </Typography>
                    ) : (
                        <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                            <List dense disablePadding>
                                {stats.unclassified.map((name) => (
                                    <ListItemButton
                                        key={name}
                                        sx={{ px: 2 }}
                                        onClick={() => handleOpenUnclassifiedReport(system, name)}
                                    >
                                        <ListItemText
                                            primary={name}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Paper>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderTotalsCard = (title, totalValue, rawValue, subtitle) => (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
                {title}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
                {formatCount(totalValue)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {subtitle} {formatCount(rawValue)}
            </Typography>
        </Paper>
    );

    const renderIndexCard = (title, value, lines) => (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
                {title}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
                {formatCount(value)}
            </Typography>
            {(lines || []).map((line, index) => (
                <Typography key={index} variant="body2" color="text.secondary" display="block">
                    {line}
                </Typography>
            ))}
        </Paper>
    );

    const getLlmColor = (isEquivalent) => {
        if (isEquivalent) return 'success';
        return 'warning';
    };

    const getRecommendationSourceLabel = (source) => {
        if (source === 'schema_mapper') return 'Schema Mapper';
        if (source === 'top_contributing_tables') return 'Top Tables';
        if (source === 'selected_target_report') return 'Selected Target Report';
        if (source === 'same_theme_reports') return 'Other Reports';
        if (source === 'other_report_same_theme') return 'Other Reports';
        if (source === 'other_report_other_theme') return 'Other Report (Other Theme)';
        if (source === 'direct_search' || source === 'qdrant') return 'Direct Search';
        return 'No Matches';
    };

    const getRecommendationSourceColor = (source) => {
        if (source === 'schema_mapper') return 'success';
        if (source === 'top_contributing_tables') return 'primary';
        if (source === 'selected_target_report') return 'primary';
        if (source === 'same_theme_reports') return 'info';
        if (source === 'other_report_same_theme') return 'info';
        if (source === 'other_report_other_theme') return 'warning';
        if (source === 'direct_search' || source === 'qdrant') return 'warning';
        return 'default';
    };

    const isRecommendationFallbackCandidate = (candidate) => (
        Boolean(candidate?.schema_mapper_fallback || candidate?.fallback_used)
        || String(candidate?.match_origin || '').trim().toLowerCase() === 'schema_mapper_fallback'
    );

    const isHighlyContributedRecommendationCandidate = (candidate) => {
        const source = String(candidate?.source || '').trim().toLowerCase();
        return source === 'top_contributing_tables' || source === 'direct_search';
    };

    const isSelectedTargetReportRecommendationCandidate = (candidate) => {
        const source = String(candidate?.source || '').trim().toLowerCase();
        return source === 'selected_target_report' || Boolean(candidate?.selected_target_report_candidate);
    };

    const isExactRecommendationCandidate = (candidate, rec = null) => {
        const kind = String(candidate?.recommendation_kind || '').trim().toLowerCase();
        const origin = String(candidate?.match_origin || '').trim().toLowerCase();
        const hasExactOrigin = [
            'logical_exact',
            'physical_exact',
            'logical_exact_sibling',
            'physical_exact_sibling',
            'exact_table_physical',
            'exact_physical',
            'priority_table_exact_physical',
            'priority_table_exact_logical',
            'schema_mapper_exact',
            'exact',
        ].includes(origin);
        return kind === 'exact' || hasExactOrigin;
    };

    const isUserPreferredRecommendationCandidate = (candidate) => {
        const preferenceStatus = String(candidate?.preference_status || '').trim().toLowerCase();
        const origin = String(candidate?.match_origin || '').trim().toLowerCase();
        return (
            preferenceStatus === 'accepted'
            || origin === 'schema_mapper_preference'
            || Boolean(candidate?.schema_mapping_preference_id)
        );
    };

    const getRecommendationTypeChipMeta = (candidate, rec = null) => {
        if (isUserPreferredRecommendationCandidate(candidate)) {
            return { label: 'User Preferred', color: 'secondary' };
        }
        return isExactRecommendationCandidate(candidate, rec)
            ? { label: 'Exact', color: 'success' }
            : { label: 'Semantic', color: 'info' };
    };

    const getLayerRecommendationGroups = (rec, layerKey, options = {}) => {
        const useAll = options.useAll === true;
        const grouped = (useAll ? rec?.all_recommendations_by_layer : rec?.recommendations_by_layer)?.[layerKey];
        if (grouped && !Array.isArray(grouped)) {
            return {
                exact: grouped.exact || [],
                semantic: grouped.semantic || [],
            };
        }
        const flat = ((useAll ? rec?.all_recommendations : rec?.recommendations) || []).filter(
            (cand) => String(cand?.catalyst_layer || '').toUpperCase() === layerKey
        );
        return {
            exact: flat.filter((cand) => isExactRecommendationCandidate(cand)),
            semantic: flat.filter((cand) => !isExactRecommendationCandidate(cand)),
        };
    };

    const getReportRecommendations = (rec, options = {}) => {
        const useAll = options.useAll === true;
        const explicit = useAll ? rec?.all_report_recommendations : rec?.report_recommendations;
        if (Array.isArray(explicit)) return explicit;
        return ((useAll ? rec?.all_recommendations : rec?.recommendations) || []).filter(
            (cand) => String(cand?.catalyst_layer || '').toLowerCase() === 'report'
        );
    };

    const getLayerSelectionCaption = (rec, layerKey) => {
        const meta = rec?.layer_selection_meta?.[layerKey];
        if (!meta) return '';
        if (meta.used_priority_level) {
            return `${meta.used_priority_level} tables`;
        }
        if (meta.selection_strategy === 'schema_mapper_all_tables') {
            return 'All Schema Mapper tables';
        }
        if (meta.fallback_used) {
            return 'All tables fallback';
        }
        return '';
    };

    const getRecommendationCardKey = (rec, idx) => (
        [
            rec?.legacy_table || 'legacy-table',
            rec?.legacy_column || 'legacy-column',
            rec?.legacy_physical || 'legacy-physical',
            idx,
        ].join('::')
    );

    const matchesRecommendationSource = (candidate, sourceKeys, fallbackSource = '') => {
        const candidateSource = String(candidate?.source || fallbackSource || '').trim().toLowerCase();
        return sourceKeys.includes(candidateSource);
    };

    const getSourceLayerRecommendationGroups = (rec, sourceKeys, options = {}) => (
        ['L2', 'L3'].reduce((acc, layerKey) => {
            const baseGroups = getLayerRecommendationGroups(rec, layerKey, options);
            acc[layerKey] = {
                exact: baseGroups.exact.filter((cand) => matchesRecommendationSource(cand, sourceKeys, rec?.recommendation_source)),
                semantic: baseGroups.semantic.filter((cand) => matchesRecommendationSource(cand, sourceKeys, rec?.recommendation_source)),
            };
            return acc;
        }, {})
    );

    const getLayeredRecommendationCount = (groups) => (
        ['L2', 'L3'].reduce(
            (total, layerKey) => total + (groups?.[layerKey]?.exact?.length || 0) + (groups?.[layerKey]?.semantic?.length || 0),
            0,
        )
    );

    const getAvailableRecommendationSubstages = () => RECOMMENDATION_SUBSTAGE_ORDER;

    const getRecommendationLayerCandidates = (stage, layerKey, options = {}) => {
        const useAll = options.useAll === true;
        const groups = useAll ? (stage?.allGroups || stage?.groups) : stage?.groups;
        return [
            ...(groups?.[layerKey]?.exact || []),
            ...(groups?.[layerKey]?.semantic || []),
        ];
    };

    const getRecommendationStages = (rec) => {
        const reportCandidates = getReportRecommendations(rec);
        const allReportCandidates = getReportRecommendations(rec, { useAll: true });

        return RECOMMENDATION_STAGE_ORDER.map((stage) => {
            if (stage.mode === 'report') {
                const candidates = reportCandidates.filter((cand) => matchesRecommendationSource(cand, stage.sourceKeys, rec?.recommendation_source));
                const allCandidates = allReportCandidates.filter((cand) => matchesRecommendationSource(cand, stage.sourceKeys, rec?.recommendation_source));
                return {
                    ...stage,
                    candidates,
                    allCandidates,
                    count: allCandidates.length || candidates.length,
                };
            }

            const groups = getSourceLayerRecommendationGroups(rec, stage.sourceKeys);
            const allGroups = getSourceLayerRecommendationGroups(rec, stage.sourceKeys, { useAll: true });
            return {
                ...stage,
                groups,
                allGroups,
                count: getLayeredRecommendationCount(allGroups) || getLayeredRecommendationCount(groups),
            };
        });
    };

    const getStageButtonSx = (active) => ({
        borderRadius: 999,
        px: 1.75,
        minHeight: 34,
        fontWeight: 700,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        boxShadow: active ? 2 : 'none',
    });

    const getRecommendationStageCandidates = (stage, options = {}) => {
        const useAll = options.useAll === true;
        if (!stage) return [];
        if (stage.mode === 'report') return (useAll ? (stage.allCandidates || stage.candidates) : stage.candidates) || [];
        const groups = useAll ? (stage.allGroups || stage.groups) : stage.groups;
        return [
            ...(groups?.L2?.exact || []),
            ...(groups?.L2?.semantic || []),
            ...(groups?.L3?.exact || []),
            ...(groups?.L3?.semantic || []),
        ];
    };

    const getRecommendationStatusForCandidates = (candidates) => {
        if (!(candidates || []).length) return 'none';
        if ((candidates || []).some((cand) => cand.approval_status === 'approved')) return 'approved';
        return 'review';
    };

    const getRecommendationPrimaryScore = (candidate) => {
        if (!candidate) return null;
        if (typeof candidate.llm_confidence === 'number' && candidate.llm_confidence > 0) return candidate.llm_confidence;
        if (typeof candidate.vector_score === 'number') return candidate.vector_score;
        return null;
    };

    const getRecommendationScoreColor = (score) => {
        if (score === null || score === undefined) return 'default';
        if (score >= 0.9) return 'success';
        if (score >= 0.75) return 'primary';
        return 'warning';
    };

    const getRecommendationRowDisplayLimit = () => {
        const configured = Number(recommendations?.options?.max_candidates || 0);
        return configured > 0 ? configured : 3;
    };

    const getRecommendationRowLoadMoreSize = () => {
        const configured = Number(recommendations?.options?.max_candidates || 0);
        return configured > 0 ? configured : Math.max(1, getRecommendationRowDisplayLimit());
    };

    const getRecommendationVisibilityKey = (recommendationKey, stageKey, substageKey = '') => (
        `${recommendationKey}::${stageKey || 'stage'}::${substageKey || 'all'}`
    );

    const dedupeRecommendationCandidates = (candidates = []) => {
        const deduped = [];
        const seen = new Set();
        (candidates || []).forEach((candidate) => {
            const key = getRecommendationCandidateKey(candidate);
            if (!key || seen.has(key)) return;
            seen.add(key);
            deduped.push(candidate);
        });
        return deduped;
    };

    const shouldPinRecommendationCandidate = (candidate) => (
        isAcceptedPreference(candidate)
        || String(candidate?.approval_status || '').trim().toLowerCase() === 'approved'
        || Boolean(candidate?.developer_selected)
    );

    const getVisibleRecommendationCandidateView = ({
        recommendationKey,
        stageKey,
        substageKey,
        displayCandidates,
        allCandidates,
    }) => {
        const candidatePool = dedupeRecommendationCandidates([
            ...(displayCandidates || []),
            ...(allCandidates || []),
        ]);
        const visibilityKey = getRecommendationVisibilityKey(recommendationKey, stageKey, substageKey);
        const hasCustomVisibleCount = Object.prototype.hasOwnProperty.call(recommendationVisibleCounts, visibilityKey);
        const configuredVisibleCount = Number(recommendationVisibleCounts[visibilityKey] || 0);
        const displayCount = Math.max(0, (displayCandidates || []).length || getRecommendationRowDisplayLimit());
        const baseVisibleCount = configuredVisibleCount > 0 ? configuredVisibleCount : displayCount;
        const visibleCandidates = baseVisibleCount > 0 ? candidatePool.slice(0, baseVisibleCount) : [...candidatePool];

        candidatePool.forEach((candidate) => {
            if (
                shouldPinRecommendationCandidate(candidate)
                && !visibleCandidates.some((item) => getRecommendationCandidateKey(item) === getRecommendationCandidateKey(candidate))
            ) {
                visibleCandidates.push(candidate);
            }
        });

        return {
            candidates: visibleCandidates,
            totalAvailable: candidatePool.length,
            shownCount: visibleCandidates.length,
            canLoadMore: visibleCandidates.length < candidatePool.length,
            canShowLess: hasCustomVisibleCount && candidatePool.length > displayCount,
            visibilityKey,
            defaultVisibleCount: displayCount,
        };
    };

    const handleLoadMoreRecommendationCandidates = (recommendationKey, stageKey, substageKey = '') => {
        const visibilityKey = getRecommendationVisibilityKey(recommendationKey, stageKey, substageKey);
        const currentVisibleCount = Number(recommendationVisibleCounts[visibilityKey] || 0);
        const displayCount = getRecommendationRowDisplayLimit();
        const nextVisibleCount = (currentVisibleCount > 0 ? currentVisibleCount : displayCount) + getRecommendationRowLoadMoreSize();
        setRecommendationVisibleCounts((prev) => ({
            ...prev,
            [visibilityKey]: nextVisibleCount,
        }));
    };

    const handleShowLessRecommendationCandidates = (recommendationKey, stageKey, substageKey = '') => {
        const visibilityKey = getRecommendationVisibilityKey(recommendationKey, stageKey, substageKey);
        setRecommendationVisibleCounts((prev) => {
            const next = { ...prev };
            delete next[visibilityKey];
            return next;
        });
    };

    const getRecommendationActiveSubstage = (stage, recommendationKey) => {
        const availableSubstages = getAvailableRecommendationSubstages();
        const substageSelectionKey = `${recommendationKey}::${stage.key}`;
        const defaultSubstageKey = availableSubstages.find(
            (item) => getRecommendationLayerCandidates(stage, item.layer, { useAll: true }).length > 0,
        )?.key || availableSubstages[0].key;
        const activeSubstageKey = availableSubstages.some((item) => item.key === recommendationSubstageSelections[substageSelectionKey])
            ? recommendationSubstageSelections[substageSelectionKey]
            : defaultSubstageKey;
        return availableSubstages.find((item) => item.key === activeSubstageKey) || availableSubstages[0];
    };

    const getRecommendationRowPresentation = (rec, idx) => {
        const recommendationKey = getRecommendationCardKey(rec, idx);
        const availableStages = getRecommendationStages(rec);
        const defaultStageKey = availableStages.find((stage) => stage.count > 0)?.key || availableStages[0]?.key;
        const activeStageKey = availableStages.some((stage) => stage.key === recommendationStageSelections[recommendationKey])
            ? recommendationStageSelections[recommendationKey]
            : defaultStageKey;
        const activeStage = availableStages.find((stage) => stage.key === activeStageKey) || null;
        const activeSubstage = activeStage?.mode === 'layered' ? getRecommendationActiveSubstage(activeStage, recommendationKey) : null;
        const displayCandidates = activeStage?.mode === 'report'
            ? (activeStage?.candidates || [])
            : (activeSubstage ? getRecommendationLayerCandidates(activeStage, activeSubstage.layer) : getRecommendationStageCandidates(activeStage));
        const allCandidates = activeStage?.mode === 'report'
            ? (activeStage?.allCandidates || activeStage?.candidates || [])
            : (activeSubstage ? getRecommendationLayerCandidates(activeStage, activeSubstage.layer, { useAll: true }) : getRecommendationStageCandidates(activeStage, { useAll: true }));
        const visibilityView = getVisibleRecommendationCandidateView({
            recommendationKey,
            stageKey: activeStage?.key || '',
            substageKey: activeSubstage?.key || '',
            displayCandidates,
            allCandidates,
        });
        const bestCandidate = visibilityView.candidates[0] || allCandidates[0] || getRecommendationStageCandidates(activeStage, { useAll: true })[0] || rec?.recommendations?.[0] || null;
        const overallStatus = getRecommendationStatusForCandidates(getRecommendationStageCandidates(activeStage, { useAll: true }));

        return {
            recommendationKey,
            availableStages,
            activeStage,
            activeSubstage,
            activeCandidates: visibilityView.candidates,
            allActiveCandidates: allCandidates,
            bestCandidate,
            overallStatus,
            shownCount: visibilityView.shownCount,
            totalAvailable: visibilityView.totalAvailable,
            canLoadMore: visibilityView.canLoadMore,
            canShowLess: visibilityView.canShowLess,
            defaultVisibleCount: visibilityView.defaultVisibleCount,
        };
    };

    const renderRecommendationVisibilityFooter = (presentation) => {
        if (!presentation?.activeStage || presentation.totalAvailable <= 0) return null;
        const showFooter = presentation.canLoadMore || presentation.canShowLess || presentation.totalAvailable > presentation.defaultVisibleCount;
        if (!showFooter) return null;

        return (
            <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                        Showing {presentation.shownCount} of {presentation.totalAvailable}
                    </Typography>
                    <Stack direction="row" spacing={0.75}>
                        {presentation.canLoadMore && (
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => handleLoadMoreRecommendationCandidates(
                                    presentation.recommendationKey,
                                    presentation.activeStage?.key || '',
                                    presentation.activeSubstage?.key || '',
                                )}
                                sx={{ minWidth: 0, px: 0.5, py: 0, lineHeight: 1.2 }}
                            >
                                Load More
                            </Button>
                        )}
                        {presentation.canShowLess && (
                            <Button
                                size="small"
                                variant="text"
                                color="primary"
                                onClick={() => handleShowLessRecommendationCandidates(
                                    presentation.recommendationKey,
                                    presentation.activeStage?.key || '',
                                    presentation.activeSubstage?.key || '',
                                )}
                                sx={{ minWidth: 0, px: 0.5, py: 0, lineHeight: 1.2 }}
                            >
                                Show Less
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Box>
        );
    };

    const renderRecommendationTargetCell = (rec, presentation) => {
        const activeStage = presentation.activeStage;
        if (!activeStage) {
            return (
                <Alert severity="info" sx={{ py: 0.75 }}>
                    No recommendations found for this gap.
                </Alert>
            );
        }

        return (
            <Box sx={{ py: 0.5 }}>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                    {presentation.availableStages.map((stage) => {
                        const active = stage.key === activeStage.key;
                        return (
                            <Button
                                key={`${presentation.recommendationKey}-${stage.key}`}
                                size="small"
                                variant={active ? 'contained' : 'outlined'}
                                color={stage.color}
                                onClick={() => setRecommendationStageSelections((prev) => ({
                                    ...prev,
                                    [presentation.recommendationKey]: stage.key,
                                }))}
                                sx={{ ...getStageButtonSx(active), minHeight: 26, px: 1.1, fontSize: '0.7rem' }}
                            >
                                {stage.label}
                            </Button>
                        );
                    })}
                </Stack>

                {activeStage.mode === 'layered' && (
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                        {getAvailableRecommendationSubstages().map((item) => {
                            const count = getRecommendationLayerCandidates(activeStage, item.layer, { useAll: true }).length;
                            const active = presentation.activeSubstage?.key === item.key;
                            return (
                                <Button
                                    key={`${activeStage.key}-${item.key}`}
                                    size="small"
                                    variant={active ? 'contained' : 'outlined'}
                                    color={activeStage.color}
                                    onClick={() => setRecommendationSubstageSelections((prev) => ({
                                        ...prev,
                                        [`${presentation.recommendationKey}::${activeStage.key}`]: item.key,
                                    }))}
                                    sx={{ ...getStageButtonSx(active), minHeight: 24, px: 0.9, fontSize: '0.66rem' }}
                                >
                                    {`${item.label} (${count})`}
                                </Button>
                            );
                        })}
                    </Stack>
                )}

                <Stack spacing={1}>
                    {(presentation.activeCandidates || []).length > 0 ? presentation.activeCandidates.map((cand, index) => {
                        const preferenceActionKey = getRecommendationPreferenceActionKey(rec, cand);
                        const preferenceBusy = Boolean(recommendationPreferenceBusyKeys[preferenceActionKey]);
                        const preferenceVisual = getRecommendationPreferenceVisual(cand);
                        return (
                        <Box
                            key={`${presentation.recommendationKey}-${cand.catalyst_table || 'table'}-${cand.catalyst_column || 'column'}-${index}`}
                            sx={{
                                p: 1.25,
                                border: '1px solid',
                                borderColor: preferenceVisual?.borderColor || 'divider',
                                borderRadius: 2,
                                bgcolor: preferenceVisual?.backgroundColor || (index === 0 ? alpha(theme.palette.primary.main, 0.03) : 'background.paper'),
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                <Box sx={{ minWidth: 0 }}>
                                    {getRecommendationCandidateReportLabel(cand) ? (
                                        <Typography
                                            variant="body2"
                                            fontWeight={preferenceVisual?.fontWeight || 700}
                                            sx={{ wordBreak: 'break-word', color: preferenceVisual?.color || 'text.primary' }}
                                        >
                                            {getRecommendationCandidateReportLabel(cand)}
                                        </Typography>
                                    ) : null}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            wordBreak: 'break-all',
                                            mt: getRecommendationCandidateReportLabel(cand) ? 0.25 : 0,
                                            color: preferenceVisual?.color || 'text.secondary',
                                            fontWeight: preferenceVisual?.fontWeight || 400,
                                        }}
                                    >
                                        {getRecommendationCandidateFieldLabel(cand)}
                                    </Typography>
                                    {getRecommendationCandidateSecondaryLabel(cand) && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                wordBreak: 'break-all',
                                                color: preferenceVisual?.color || 'text.secondary',
                                                fontWeight: preferenceVisual?.fontWeight || 400,
                                            }}
                                        >
                                            {getRecommendationCandidateSecondaryLabel(cand)}
                                        </Typography>
                                    )}
                                </Box>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                                    {getRecommendationPrimaryScore(cand) !== null && (
                                        <Chip
                                            label={`${Math.round(getRecommendationPrimaryScore(cand) * 100)}%`}
                                            size="small"
                                            color={getRecommendationScoreColor(getRecommendationPrimaryScore(cand))}
                                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                                        />
                                    )}
                                    {['L2', 'L3'].includes(String(cand.catalyst_layer || '').toUpperCase()) && (
                                        <>
                                            <Tooltip title={isAcceptedPreference(cand) ? 'Clear accepted preference' : 'Accept and store in Schema Analyzer preferences'}>
                                                <IconButton
                                                    size="small"
                                                    color={isAcceptedPreference(cand) ? 'success' : 'default'}
                                                    onClick={() => handleRecommendationPreferenceDecision(rec, cand, 'accepted', 'schema_mapper_accept')}
                                                    disabled={preferenceBusy}
                                                    sx={{ width: 26, height: 26 }}
                                                >
                                                    <AcceptIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reject and store in Schema Analyzer preferences">
                                                <IconButton
                                                    size="small"
                                                    color={isRejectedPreference(cand) ? 'error' : 'default'}
                                                    onClick={() => handleRecommendationPreferenceDecision(rec, cand, 'rejected', 'schema_mapper_reject')}
                                                    disabled={preferenceBusy}
                                                    sx={{ width: 26, height: 26 }}
                                                >
                                                    <RejectIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Manual override">
                                                <IconButton
                                                    size="small"
                                                    color={cand.manual_override || ['manual_override', 'schema_mapper_manual_override'].includes(String(cand.preference_source || '')) ? 'info' : 'default'}
                                                    onClick={() => openRecommendationManualOverrideDialog(rec, cand)}
                                                    sx={{ width: 26, height: 26 }}
                                                >
                                                    <CorrectIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={Boolean(cand.developer_selected)}
                                                onChange={(event) => handleRecommendationDeveloperSelection(rec, cand, event.target.checked)}
                                                disabled={preferenceBusy}
                                                sx={{ p: 0.25 }}
                                            />
                                        )}
                                        label="Use"
                                        sx={{
                                            m: 0,
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                            },
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        onClick={() => setSelectedRecommendation({ gap: rec, candidate: cand })}
                                    >
                                        View
                                    </Button>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                                {(() => {
                                    const typeChip = getRecommendationTypeChipMeta(cand, rec);
                                    return (
                                        <Chip
                                            label={typeChip.label}
                                            size="small"
                                            variant="outlined"
                                            color={typeChip.color}
                                            sx={{ height: 18, fontSize: '0.6rem' }}
                                        />
                                    );
                                })()}
                                {isSelectedTargetReportRecommendationCandidate(cand) && (
                                    <Chip label="Selected Target Report" size="small" variant="outlined" color="primary" sx={{ height: 18, fontSize: '0.6rem' }} />
                                )}
                                {isHighlyContributedRecommendationCandidate(cand) && (
                                    <Chip label="Highly Contributed table" size="small" variant="outlined" color="primary" sx={{ height: 18, fontSize: '0.6rem' }} />
                                )}
                                {isRecommendationFallbackCandidate(cand) && (
                                    <Chip label="Fallback" size="small" variant="outlined" color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />
                                )}
                            </Stack>
                        </Box>
                        );
                    }) : (
                        <Alert severity="info" sx={{ py: 0.75 }}>
                            No recommendations found in this stage.
                        </Alert>
                    )}
                </Stack>
                {renderRecommendationVisibilityFooter(presentation)}
            </Box>
        );
    };

    const renderTelemetryPanel = (telemetry, title = 'Runtime Telemetry') => {
        return null;
    };

    const renderMatchCardSection = (title, matches, emptyMessage, sectionKey) => (
        <Box sx={{ mb: 4 }} key={sectionKey}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h6" fontWeight={700}>{title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {matches.length ? `${matches.length} reports shown` : emptyMessage}
                    </Typography>
                </Box>
            </Stack>
            {matches.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {matches.map((match, idx) => (
                        <Grid item xs={12} md={6} lg={4} key={`${sectionKey}-${idx}-${match.catalyst_report_name}`}>
                            {(() => {
                                const cardPalette = getMatchCardPalette(match);
                                return (
                                    <Card
                                        onClick={() => handleSelectMatch(match)}
                                        sx={{
                                            cursor: 'pointer',
                                            border: '2px solid',
                                            borderColor: cardPalette.border,
                                            background: `linear-gradient(180deg, ${cardPalette.background} 0%, rgba(255,255,255,0) 42%)`,
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 6,
                                                borderColor: cardPalette.hoverBorder,
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                                                    <Tooltip title={match.catalyst_report_name || ''} arrow placement="top-start">
                                                        <Typography
                                                            variant="h6"
                                                            fontWeight={700}
                                                            color="primary.main"
                                                            sx={{
                                                                display: '-webkit-box',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                WebkitBoxOrient: 'vertical',
                                                                WebkitLineClamp: 2,
                                                                lineHeight: 1.45,
                                                                minHeight: '2.9em',
                                                            }}
                                                        >
                                                            {match.catalyst_report_name}
                                                        </Typography>
                                                    </Tooltip>
                                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                                        {renderTruncatedChip(getCatalystReportTypeLabel(match), {
                                                            chipProps: {
                                                                color: 'default',
                                                                variant: 'outlined',
                                                            },
                                                            width: 92,
                                                            maxWidth: 92,
                                                        })}
                                                    </Stack>
                                                </Box>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {match.stage2_verified && (
                                                        <Tooltip title="High Reliability: Every column match has been verified by AI">
                                                            <Chip
                                                                icon={<AutoAwesomeIcon sx={{ fontSize: '14px !important' }} />}
                                                                label="Verified"
                                                                size="small"
                                                                color="secondary"
                                                                variant="outlined"
                                                                sx={{ height: 24, fontWeight: 700, borderColor: 'secondary.main', color: 'secondary.main' }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    <Chip
                                                        label={`${match.overlap_percentage}%`}
                                                        color={getMatchColor(match.overlap_percentage)}
                                                        size="small"
                                                        sx={{ fontWeight: 700, height: 24 }}
                                                    />
                                                </Stack>
                                            </Box>

                                            <Typography variant="body2" color="text.secondary" mb={2}>
                                                Overlaps on <strong>{match.overlap_count}</strong> of <strong>{match.total_legacy_columns}</strong> key data items.
                                            </Typography>

                                            <Divider sx={{ my: 2 }} />

                                            <Typography variant="caption" color="text.secondary" component="div" gutterBottom>
                                                {resolvedThemeMode === 'global' ? 'Common Global Themes:' : 'Common Themes:'}
                                            </Typography>
                                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                                {legacyHasSelectedThemes && match.common_themes?.map(t => (
                                                    <Box key={t}>
                                                        {renderTruncatedChip(t, {
                                                            chipProps: {
                                                                color: 'primary',
                                                                variant: 'outlined',
                                                            },
                                                            width: 156,
                                                            maxWidth: 156,
                                                        })}
                                                    </Box>
                                                ))}
                                                {!legacyHasSelectedThemes && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        No legacy themes selected.
                                                    </Typography>
                                                )}
                                            </Stack>
                                            {resolvedThemeMode !== 'none' && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary" component="div" gutterBottom sx={{ mt: 1 }}>
                                                        {resolvedThemeMode === 'global' ? 'Catalyst Global Themes:' : 'Catalyst Themes:'}
                                                    </Typography>
                                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                                        {(resolvedThemeMode === 'global'
                                                            ? match.catalyst_global_themes
                                                            : match.catalyst_themes
                                                        )?.map(t => (
                                                            <Box key={t}>
                                                                {renderTruncatedChip(t, {
                                                                    chipProps: {
                                                                        color: 'secondary',
                                                                        variant: 'outlined',
                                                                    },
                                                                    width: 156,
                                                                    maxWidth: 156,
                                                                })}
                                                            </Box>
                                                        ))}
                                                        {(resolvedThemeMode === 'global'
                                                            ? match.catalyst_global_themes
                                                            : match.catalyst_themes
                                                        )?.length === 0 && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                None
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })()}
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );

    const renderCleaningSummaryTable = (title, rows) => (
        <Box>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
                {title}
            </Typography>
            {rows && rows.length > 0 ? (
                <TableContainer sx={{ maxHeight: 320, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Report</TableCell>
                                <TableCell align="right">Raw</TableCell>
                                <TableCell align="right">Dedup</TableCell>
                                <TableCell align="right">After Physical</TableCell>
                                <TableCell align="right">After Layout</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.report_name}>
                                    <TableCell sx={{ maxWidth: 280 }}>
                                        <Typography variant="body2" noWrap title={row.report_name}>
                                            {row.report_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{row.raw_rows}</TableCell>
                                    <TableCell align="right">{row.dedup_rows}</TableCell>
                                    <TableCell align="right">{row.after_physical_rows}</TableCell>
                                    <TableCell align="right">{row.after_layout_rows}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No summary available.
                </Typography>
            )}
        </Box>
    );

    const renderMissingLogicalTable = (rows, system) => (
        <Box sx={{ mt: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} sx={{ flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                    Missing Logical Names ({system === 'legacy' ? 'Legacy' : 'Catalyst'})
                </Typography>
                <ToggleButtonGroup
                    size="small"
                    color="primary"
                    value={system}
                    exclusive
                    onChange={(event, value) => {
                        if (value) {
                            setMissingLogicalSystem(value);
                        }
                    }}
                >
                    <ToggleButton value="catalyst">Catalyst</ToggleButton>
                    <ToggleButton value="legacy">Legacy</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Reports with rows missing logical names are listed below so they can be corrected in future extracts.
            </Typography>
            {rows && rows.length > 0 ? (
                <TableContainer sx={{ maxHeight: 280, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Report</TableCell>
                                <TableCell align="right">Missing</TableCell>
                                <TableCell align="right">Total Rows</TableCell>
                                <TableCell align="right">Missing %</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.report_name}>
                                    <TableCell sx={{ maxWidth: 280 }}>
                                        <Typography variant="body2" noWrap title={row.report_name}>
                                            {row.report_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{formatCount(row.missing_logical_rows)}</TableCell>
                                    <TableCell align="right">{formatCount(row.total_rows)}</TableCell>
                                    <TableCell align="right">{row.missing_percent ?? 0}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No missing logical names detected.
                </Typography>
            )}
        </Box>
    );

    const normalizeKeyPart = (value) => String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ');

    const normalizeExpressionPart = (value) => String(value || '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ');

    const getColumnKey = (column, system = 'legacy') => {
        if (!column) return '';
        const settings = deduplicationConfig?.[system] || DEDUP_DEFAULTS[system] || {};
        const tableLogical = normalizeKeyPart(
            column.legacy_table_logical
            || column.table_logical_name
            || column.table_logical
            || column.logical_query_subject_name
            || ''
        );
        const tablePhysical = normalizeKeyPart(
            column.legacy_table_physical
            || column.table_physical_name
            || column.table_name
            || column.catalyst_table
            || column.physical_query_subject_name
            || ''
        );
        const logical = normalizeKeyPart(column.legacy_logical || column.legacy_column || column.logical || '');
        const physical = normalizeKeyPart(column.legacy_physical || column.physical || column.physical_name || '');
        const embeddedSql = normalizeKeyPart(column.legacy_has_embedded_sql || column.has_embedded_sql || column.hasEmbeddedSql || '');
        const sqlFilter = normalizeKeyPart(column.legacy_report_sql_filter || column.report_sql_filter || column.reportSqlFilter || '');
        const expression = normalizeExpressionPart(column.legacy_expression || column.expression || '');
        const layout = normalizeKeyPart(column.legacy_report_layout ?? column.report_layout ?? column.reportLayout ?? '');
        if (!logical && !physical) return '';
        const tableKeyParts = [];
        if (settings.include_table_logical_name ?? false) {
            tableKeyParts.push(tableLogical);
        }
        if (settings.include_table_physical_name ?? false) {
            tableKeyParts.push(tablePhysical);
        }
        if (settings.dedup_prefer_physical_by_data_item) {
            return [...tableKeyParts, logical, physical].join('|');
        }
        const keyParts = [...tableKeyParts, logical, physical, embeddedSql];
        if (settings.include_sql_filter ?? true) {
            keyParts.push(sqlFilter);
        }
        if (settings.include_expression ?? false) {
            keyParts.push(expression);
        }
        keyParts.push(layout);
        return keyParts.join('|');
    };

    const getLegacyFieldIdentity = (field) => {
        if (!field) return '';
        const legacyKey = String(field.legacy_key || '').trim();
        if (legacyKey) {
            return `key:${legacyKey}`;
        }

        const legacyColumnKey = getColumnKey({
            legacy_logical: field.legacy_logical || field.legacy_column || field.logical_name || field.logical || '',
            legacy_physical: field.legacy_physical || field.physical_name || field.physical || '',
            legacy_table_logical: field.legacy_table_logical || field.table_logical_name || '',
            legacy_table_physical: field.legacy_table_physical || field.table_name || field.legacy_table || '',
            legacy_expression: field.legacy_expression || field.expression || '',
            legacy_has_embedded_sql: field.legacy_has_embedded_sql || field.has_embedded_sql || '',
            legacy_report_sql_filter: field.legacy_report_sql_filter || field.report_sql_filter || '',
            legacy_report_layout: field.legacy_report_layout ?? field.report_layout ?? '',
        }, 'legacy');
        if (legacyColumnKey) {
            return `legacy:${legacyColumnKey}`;
        }

        return [
            normalizeKeyPart(field.legacy_column || field.logical_name || field.logical || ''),
            normalizeKeyPart(field.legacy_physical || field.physical_name || field.physical || ''),
            normalizeKeyPart(field.legacy_table || field.table_name || ''),
            normalizeExpressionPart(field.expression || ''),
            normalizeKeyPart(field.column_type || ''),
        ].join('|');
    };

    const shouldMoveMatchedColumnToGap = (match) => (
        isRejectedPreference(match)
        && !mergeMatchCandidates(match).some((candidate) => isAcceptedPreference(candidate))
    );

    const getActiveMatchedColumns = (matchedColumns = []) => (
        (matchedColumns || []).filter((match) => !shouldMoveMatchedColumnToGap(match))
    );

    const buildGapFromRejectedMatchedColumn = (match) => ({
        ...match,
        was_invalidated: true,
        invalidated_match: match?.catalyst_column || '',
        invalidation_reason: match?.llm_reasoning || 'Rejected by user preference.',
        candidate: buildGapCandidateFromMatch(match, 'rejected'),
    });

    const mergeInvalidatedMatchesIntoGaps = (matchedColumns = [], invalidatedMatches = [], gaps = []) => {
        const activeMatchedColumns = getActiveMatchedColumns(matchedColumns);
        const rejectedMatchedColumns = (matchedColumns || []).filter((match) => shouldMoveMatchedColumnToGap(match));
        const matchedIdentities = new Set(
            activeMatchedColumns.map((item) => getLegacyFieldIdentity(item)).filter(Boolean)
        );
        const gapIdentities = new Set(
            (gaps || []).map((item) => getLegacyFieldIdentity(item)).filter(Boolean)
        );

        const unresolvedInvalidated = (invalidatedMatches || []).filter((inv) => {
            const identity = getLegacyFieldIdentity(inv);
            if (!identity) return true;
            return !matchedIdentities.has(identity) && !gapIdentities.has(identity);
        });

        const rejectedGaps = rejectedMatchedColumns.filter((match) => {
            const identity = getLegacyFieldIdentity(match);
            if (!identity) return true;
            return !gapIdentities.has(identity);
        });

        return [
            ...rejectedGaps.map(buildGapFromRejectedMatchedColumn),
            ...unresolvedInvalidated.map((inv) => ({
                ...inv,
                was_invalidated: true,
                candidate: {
                    catalyst_column: inv.catalyst_column,
                    catalyst_physical: inv.catalyst_physical,
                    score: inv.score,
                    match_type: inv.match_type,
                    llm_reasoning: inv.llm_reasoning,
                    llm_confidence: inv.llm_confidence,
                    preference_status: inv.preference_status,
                    preference_source: inv.preference_source,
                    preference_notes: inv.preference_notes,
                }
            })),
            ...(gaps || [])
        ];
    };

    const sortTopMatchesByMetrics = (matches) => (
        [...(matches || [])].sort((a, b) => {
            const coverageDiff = (b.coverage_ratio || 0) - (a.coverage_ratio || 0);
            if (coverageDiff !== 0) return coverageDiff;
            const precisionDiff = (b.precision_ratio || 0) - (a.precision_ratio || 0);
            if (precisionDiff !== 0) return precisionDiff;
            const scoreDiff = (b.avg_score || 0) - (a.avg_score || 0);
            if (scoreDiff !== 0) return scoreDiff;
            return (a.catalyst_report_name || '').localeCompare(b.catalyst_report_name || '');
        })
    );

    const buildReportMatchMetrics = (reportMatch, matchedColumns, totalLegacyColumns) => {
        const overlapCount = matchedColumns.length;
        const coverageRatio = totalLegacyColumns ? overlapCount / totalLegacyColumns : 0;
        const precisionDenominator = reportMatch?.catalyst_breakdown?.unique_rows || 0;
        const precisionRatio = precisionDenominator ? overlapCount / precisionDenominator : 0;
        const avgScore = overlapCount
            ? matchedColumns.reduce((sum, item) => sum + (item?.score || 0), 0) / overlapCount
            : 0;

        return {
            overlap_count: overlapCount,
            overlap_percentage: getCoveragePercentage(overlapCount, totalLegacyColumns),
            coverage_ratio: coverageRatio,
            precision_ratio: precisionRatio,
            avg_score: avgScore,
            matched_columns: matchedColumns,
        };
    };

    useEffect(() => {
        latestGapAnalysisRef.current = gapAnalysis;
    }, [gapAnalysis]);

    useEffect(() => {
        latestMatchSelectionsRef.current = matchSelections;
    }, [matchSelections]);

    useEffect(() => {
        if (
            !workspaceDecisionDirty
            || saving
            || activeStep !== 4
            || !gapAnalysis
            || !selectedWorkspace?.id
            || !selectedLegacyReport?.report_name
        ) {
            return undefined;
        }

        const snapshot = buildWorkspaceDecisionSnapshot(gapAnalysis, matchSelections);
        if (snapshot === autosaveLastFailedSnapshotRef.current) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            persistWorkspaceDecisions({ silent: true });
        }, 300);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [
        workspaceDecisionDirty,
        saving,
        activeStep,
        gapAnalysis,
        matchSelections,
        selectedWorkspace?.id,
        selectedLegacyReport?.report_name,
        matchResults,
        topN,
        customTopN,
        standardOtherThemeTopN,
        finalMatchThreshold,
        sameThemeOnly,
        globalThemeOnly,
        ignoreCommon,
        ignorePii,
        includeCustom,
    ]);

    const dedupeByColumnKey = (columns, { keepBestScore = false, system = 'legacy' } = {}) => {
        const settings = deduplicationConfig?.[system] || DEDUP_DEFAULTS[system] || {};
        if (settings.dedup_prefer_physical_by_data_item) {
            const hasMeaningfulDedupeValue = (value) => {
                const text = String(value ?? '').trim().toLowerCase();
                return Boolean(text) && !['nan', 'none', '<na>'].includes(text);
            };
            const isTruthyDedupeValue = (value) => {
                if (value === true) return true;
                return ['yes', 'y', 'true', '1'].includes(String(value ?? '').trim().toLowerCase());
            };
            const hasPhysicalColumn = (col) => hasMeaningfulDedupeValue(
                col?.legacy_physical || col?.physical || col?.physical_name
            );
            const isCalculatedColumn = (col) => {
                if (isTruthyDedupeValue(
                    col?.legacy_calculated
                    || col?.calculated
                    || col?.Calculated
                    || ''
                )) return true;
                return String(col?.column_type || col?.legacy_column_type || '').trim().toLowerCase() === 'derived';
            };
            const isLayoutColumn = (col) => isTruthyDedupeValue(
                col?.legacy_report_layout ?? col?.report_layout ?? col?.catalyst_report_layout
            );
            const isBetterDedupeSurvivor = (candidate, existing) => {
                const candidatePriority = [
                    hasPhysicalColumn(candidate) ? 1 : 0,
                    isLayoutColumn(candidate) ? 1 : 0,
                    isCalculatedColumn(candidate) ? 1 : 0,
                ];
                const existingPriority = [
                    hasPhysicalColumn(existing) ? 1 : 0,
                    isLayoutColumn(existing) ? 1 : 0,
                    isCalculatedColumn(existing) ? 1 : 0,
                ];
                for (let i = 0; i < candidatePriority.length; i += 1) {
                    if (candidatePriority[i] !== existingPriority[i]) {
                        return candidatePriority[i] > existingPriority[i];
                    }
                }
                return false;
            };
            const grouped = new Map();
            (columns || []).forEach((col, index) => {
                const tableLogical = normalizeKeyPart(
                    col?.legacy_table_logical
                    || col?.table_logical_name
                    || col?.table_logical
                    || col?.logical_query_subject_name
                    || ''
                );
                const tablePhysical = normalizeKeyPart(
                    col?.legacy_table_physical
                    || col?.table_physical_name
                    || col?.table_name
                    || col?.catalyst_table
                    || col?.physical_query_subject_name
                    || ''
                );
                const logical = normalizeKeyPart(col?.legacy_logical || col?.legacy_column || col?.logical || '');
                if (!logical) return;

                const groupKeyParts = [];
                if (settings.include_table_logical_name ?? false) {
                    groupKeyParts.push(tableLogical);
                }
                if (settings.include_table_physical_name ?? false) {
                    groupKeyParts.push(tablePhysical);
                }
                groupKeyParts.push(logical);
                const logicalKey = groupKeyParts.join('|');
                const physical = normalizeKeyPart(col?.legacy_physical || col?.physical || col?.physical_name || '');
                const physicalKey = [...groupKeyParts, physical].join('|');
                const state = grouped.get(logicalKey) || { physical: new Map(), missing: null };
                if (physical) {
                    const existing = state.physical.get(physicalKey);
                    if (!existing) {
                        state.physical.set(physicalKey, { col, index });
                    } else if (isBetterDedupeSurvivor(col, existing.col)) {
                        state.physical.set(physicalKey, { col, index: existing.index });
                    }
                } else {
                    const existing = state.missing;
                    if (!existing) {
                        state.missing = { col, index };
                    } else if (isBetterDedupeSurvivor(col, existing.col)) {
                        state.missing = { col, index: existing.index };
                    }
                }
                grouped.set(logicalKey, state);
            });

            const selected = [];
            grouped.forEach((state) => {
                if (state.physical.size > 0) {
                    selected.push(...Array.from(state.physical.values()));
                } else if (state.missing) {
                    selected.push(state.missing);
                }
            });

            selected.sort((a, b) => a.index - b.index);
            return selected.map((item) => item.col);
        }

        const map = new Map();
        (columns || []).forEach((col) => {
            const key = getColumnKey(col, system);
            if (!key) return;
            if (!map.has(key)) {
                map.set(key, col);
                return;
            }
            if (keepBestScore) {
                const existing = map.get(key);
                const existingScore = existing?.score ?? -1;
                const nextScore = col?.score ?? -1;
                if (nextScore > existingScore) {
                    map.set(key, col);
                }
            }
        });
        return Array.from(map.values());
    };

    const isLayoutYes = (value) => {
        if (value === true) return true;
        if (!value) return false;
        return String(value).trim().toLowerCase() === 'yes';
    };

    const getUsedInLayoutLabel = (value) => (isLayoutYes(value) ? 'Yes' : 'No');

    const renderUsedInLayoutChip = (value) => (
        <Chip
            label={getUsedInLayoutLabel(value)}
            size="small"
            color={isLayoutYes(value) ? 'success' : 'default'}
            variant={isLayoutYes(value) ? 'filled' : 'outlined'}
            sx={{ minWidth: 56, fontWeight: 700 }}
        />
    );

    const normalizeCatalystSearchValue = (value) => String(value || '').trim().toLowerCase();

    const filterMatchCandidateOptions = (options, { inputValue }) => {
        const query = normalizeCatalystSearchValue(inputValue);
        if (!query) return options;

        return options.filter((option) => (
            normalizeCatalystSearchValue(option?.catalyst_logical).includes(query) ||
            normalizeCatalystSearchValue(getMatchCandidatePhysicalLabel(option)).includes(query)
        ));
    };


    const gapView = useMemo(() => {
        if (!gapAnalysis || !matchResults || !selectedMatch) return gapAnalysis;

        if (!matchOnlyLayout) {
            return gapAnalysis;
        }

        // If we have API results, use them as the base to preserve LLM validation/recovery info
        const baseMatched = gapAnalysis.matched_columns || [];
        const baseGaps = gapAnalysis.gaps || [];
        const baseInvalidated = gapAnalysis.invalidated_matches || [];

        // Filter matched columns by layout
        const matchedLayoutColumns = baseMatched.filter((c) => (
            isLayoutYes(c.legacy_report_layout ?? c.report_layout)
        ));

        // Filter gaps by layout
        const gapsLayout = baseGaps.filter((g) => isLayoutYes(g.legacy_report_layout ?? g.report_layout));

        // Filter invalidated matches by layout
        const invalidatedLayout = baseInvalidated.filter((inv) => isLayoutYes(inv.legacy_report_layout ?? inv.report_layout));

        const totalLegacyLayout = dedupeByColumnKey(
            (matchResults.legacy_columns || []).filter((c) => isLayoutYes(c.report_layout))
        ).length;

        const overlapCount = matchedLayoutColumns.length;
        const overlapPercentage = totalLegacyLayout ? Math.round((overlapCount / totalLegacyLayout) * 1000) / 10 : 0;

        return {
            ...gapAnalysis,
            total_legacy_columns: totalLegacyLayout,
            matched_count: overlapCount,
            gap_count: gapsLayout.length,
            overlap_count: overlapCount,
            overlap_percentage: overlapPercentage,
            legacy_breakdown: gapAnalysis.legacy_breakdown_layout || gapAnalysis.legacy_breakdown,
            catalyst_breakdown: gapAnalysis.catalyst_breakdown_layout || gapAnalysis.catalyst_breakdown,
            matched_columns: matchedLayoutColumns,
            gaps: gapsLayout,
            invalidated_matches: invalidatedLayout,
            match_only_layout: true
        };
    }, [gapAnalysis, matchOnlyLayout, matchResults, selectedMatch]);

    const expressionFieldsView = useMemo(() => {
        if (!gapView) return [];

        const rawExpressionRows = Array.isArray(gapView.legacy_raw_expression_rows)
            ? gapView.legacy_raw_expression_rows
            : (Array.isArray(matchResults?.legacy_raw_expression_rows) ? matchResults.legacy_raw_expression_rows : null);

        if (!Array.isArray(rawExpressionRows)) {
            return [];
        }

        return rawExpressionRows.map((field, index) => ({
            key: `raw-expression-${field?.raw_index ?? index}-${index}`,
            logicalName: String(field?.logical_name || '').trim(),
            physicalName: String(field?.physical_name || '').trim(),
            type: String(field?.expression_type || '').trim() || getExpressionFieldType(field),
            expression: String(field?.expression || '').trim(),
            columnType: field?.column_type || '',
            usedInLayout: field?.used_in_layout ?? field?.report_layout ?? false,
        }));
    }, [gapView, matchResults]);

    const removedCommonPiiFieldsView = useMemo(() => {
        if (!gapView) return [];

        const normalizeRemovedField = (item, source, category, index) => {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
                const logicalName = String(item.logical_name || item.legacy_column || item.column_name || item.name || item.label || '').trim();
                const physicalName = String(item.physical_name || item.legacy_physical || item.physical || '').trim();
                return {
                    key: `${source}-${category}-${logicalName}-${physicalName}-${index}`,
                    source,
                    category,
                    logicalName: logicalName || physicalName || '-',
                    physicalName: physicalName || '-',
                    usedInLayout: item.used_in_layout ?? item.report_layout ?? false,
                    columnType: item.column_type || '',
                };
            }

            const rawValue = String(item || '').trim();
            return {
                key: `${source}-${category}-${rawValue}-${index}`,
                source,
                category,
                logicalName: rawValue || '-',
                physicalName: '-',
                usedInLayout: false,
                columnType: '',
            };
        };

        return [
            ...(gapView.legacy_removed_common || []).map((item, index) => normalizeRemovedField(item, 'Legacy', 'Common', index)),
            ...(gapView.legacy_removed_pii || []).map((item, index) => normalizeRemovedField(item, 'Legacy', 'PII', index)),
        ];
    }, [gapView]);

    const gapAnalysisLegacyReportPath = useMemo(() => {
        const selectedLegacyReportFromList = (classificationReports?.legacy || []).find(
            (item) => item?.report_name === gapView?.legacy_report
        );
        return selectedLegacyReport?.report_path || selectedLegacyReportFromList?.report_path || '';
    }, [classificationReports?.legacy, gapView?.legacy_report, selectedLegacyReport?.report_path]);

    const gapAnalysisLegacyReportMeta = useMemo(() => {
        const selectedLegacyReportFromList = (classificationReports?.legacy || []).find(
            (item) => item?.report_name === gapView?.legacy_report
        );
        return selectedLegacyReportFromList || selectedLegacyReport || null;
    }, [classificationReports?.legacy, gapView?.legacy_report, selectedLegacyReport]);

    const gapAnalysisCatalystReportPath = useMemo(() => {
        const selectedCatalystReportFromList = (classificationReports?.catalyst || []).find(
            (item) => item?.report_name === (gapView?.catalyst_report || selectedMatch?.catalyst_report_name)
        );
        return selectedMatch?.catalyst_report_path || selectedCatalystReportFromList?.report_path || '';
    }, [classificationReports?.catalyst, gapView?.catalyst_report, selectedMatch?.catalyst_report_name, selectedMatch?.catalyst_report_path]);

    const gapAnalysisCatalystReportMeta = useMemo(() => {
        const selectedCatalystReportFromList = (classificationReports?.catalyst || []).find(
            (item) => item?.report_name === (gapView?.catalyst_report || selectedMatch?.catalyst_report_name)
        );
        return selectedCatalystReportFromList || selectedMatch || null;
    }, [classificationReports?.catalyst, gapView?.catalyst_report, selectedMatch]);

    const gapAnalysisLegacyGlobalThemes = useMemo(
        () => (gapAnalysisLegacyReportMeta?.global_themes || matchResults?.legacy_global_themes || []).filter(Boolean),
        [gapAnalysisLegacyReportMeta, matchResults?.legacy_global_themes]
    );

    const gapAnalysisCatalystGlobalThemes = useMemo(
        () => (gapAnalysisCatalystReportMeta?.global_themes || gapAnalysisCatalystReportMeta?.catalyst_global_themes || []).filter(Boolean),
        [gapAnalysisCatalystReportMeta]
    );

    const gapAnalysisLegacyRawBreakdown = useMemo(
        () => gapAnalysis?.legacy_breakdown || gapView?.legacy_breakdown || null,
        [gapAnalysis?.legacy_breakdown, gapView?.legacy_breakdown]
    );

    const gapAnalysisCatalystRawBreakdown = useMemo(
        () => gapAnalysis?.catalyst_breakdown || gapView?.catalyst_breakdown || null,
        [gapAnalysis?.catalyst_breakdown, gapView?.catalyst_breakdown]
    );

    const gapAnalysisLegacyLayoutBreakdown = useMemo(
        () => gapAnalysis?.legacy_breakdown_layout || matchResults?.legacy_breakdown_layout || null,
        [gapAnalysis?.legacy_breakdown_layout, matchResults?.legacy_breakdown_layout]
    );

    const gapAnalysisCatalystLayoutBreakdown = useMemo(
        () => gapAnalysis?.catalyst_breakdown_layout || selectedMatch?.catalyst_breakdown_layout || null,
        [gapAnalysis?.catalyst_breakdown_layout, selectedMatch?.catalyst_breakdown_layout]
    );

    const gapAnalysisLegacyTotalFields = useMemo(() => {
        const dedupRows = Number(gapAnalysisLegacyRawBreakdown?.dedup_rows);
        if (Number.isFinite(dedupRows)) {
            return Math.max(
                dedupRows
                - Number(gapAnalysisLegacyRawBreakdown?.missing_logical ?? 0)
                - Number(gapAnalysisLegacyRawBreakdown?.ignored_common ?? 0)
                - Number(gapAnalysisLegacyRawBreakdown?.ignored_pii ?? 0),
                0
            );
        }

        return (
            gapAnalysisLegacyRawBreakdown?.unique_rows
            ?? gapAnalysisLegacyRawBreakdown?.raw_rows
            ?? gapView?.total_legacy_columns
            ?? 0
        );
    }, [
        gapAnalysisLegacyRawBreakdown?.dedup_rows,
        gapAnalysisLegacyRawBreakdown?.missing_logical,
        gapAnalysisLegacyRawBreakdown?.ignored_common,
        gapAnalysisLegacyRawBreakdown?.ignored_pii,
        gapAnalysisLegacyRawBreakdown?.unique_rows,
        gapAnalysisLegacyRawBreakdown?.raw_rows,
        gapView?.total_legacy_columns,
    ]);

    const gapAnalysisLegacyLayoutUsedCount = useMemo(() => {
        const layoutColumns = dedupeByColumnKey(
            (matchResults?.legacy_columns || []).filter((column) => isLayoutYes(column.report_layout))
        );
        return (
            layoutColumns.length
            || gapAnalysis?.layout_scored_columns_count
            || gapAnalysisLegacyLayoutBreakdown?.dedup_rows
            || gapAnalysisLegacyLayoutBreakdown?.raw_rows
            || 0
        );
    }, [
        matchResults?.legacy_columns,
        gapAnalysis?.layout_scored_columns_count,
        gapAnalysisLegacyLayoutBreakdown?.dedup_rows,
        gapAnalysisLegacyLayoutBreakdown?.raw_rows,
    ]);

    const gapAnalysisCatalystLayoutUsedCount = useMemo(() => {
        const layoutColumns = dedupeByColumnKey(
            (gapView?.all_catalyst_columns || []).filter((column) => isLayoutYes(column.report_layout)),
            { system: 'catalyst' }
        );
        return (
            layoutColumns.length
            || gapAnalysisCatalystLayoutBreakdown?.dedup_rows
            || gapAnalysisCatalystLayoutBreakdown?.raw_rows
            || 0
        );
    }, [
        gapView?.all_catalyst_columns,
        gapAnalysisCatalystLayoutBreakdown?.dedup_rows,
        gapAnalysisCatalystLayoutBreakdown?.raw_rows,
    ]);

    const gapAnalysisUsesFallbackScope = Boolean(
        gapAnalysis?.stage1_non_layout_fallback_used
        ?? matchResults?.stage1_non_layout_fallback_used
    );

    const removedCommonPiiCount = useMemo(
        () => (gapView?.legacy_removed_common?.length || 0) + (gapView?.legacy_removed_pii?.length || 0),
        [gapView?.legacy_removed_common, gapView?.legacy_removed_pii]
    );

    useEffect(() => {
        if (!gapAnalysis?.catalyst_report || !selectedMatch?.catalyst_report_name) {
            return;
        }

        setMatchResults((prev) => {
            const previousMatches = getAllTopMatches(prev);
            if (!previousMatches.length) return prev;

            const totalLegacyColumns = gapAnalysis.total_legacy_columns || prev.filtered_columns || prev.total_legacy_columns || 0;
            let didUpdate = false;
            const updatedCollections = updateMatchCollections(prev, (reportMatch) => {
                if (reportMatch.catalyst_report_name !== gapAnalysis.catalyst_report) {
                    return reportMatch;
                }

                didUpdate = true;
                const matchedColumns = gapAnalysis.matched_columns || [];
                return {
                    ...reportMatch,
                    ...buildReportMatchMetrics(reportMatch, matchedColumns, totalLegacyColumns),
                    invalidated_matches: gapAnalysis.invalidated_matches || [],
                    gaps: gapAnalysis.gaps || [],
                    recovered_count: gapAnalysis.recovered_count || 0,
                    stage2_verified: true,
                };
            });

            if (!didUpdate) return prev;

            return updatedCollections;
        });
    }, [gapAnalysis, selectedMatch?.catalyst_report_name]);

    useEffect(() => {
        const allMatches = getAllTopMatches(matchResults);
        if (!selectedMatch?.catalyst_report_name || !allMatches.length) {
            return;
        }
        const refreshedSelectedMatch = allMatches.find(
            (reportMatch) => reportMatch.catalyst_report_name === selectedMatch.catalyst_report_name
        );
        if (refreshedSelectedMatch && refreshedSelectedMatch !== selectedMatch) {
            setSelectedMatch(refreshedSelectedMatch);
        }
    }, [matchResults?.top_matches, matchResults?.standard_top_matches, matchResults?.custom_top_matches, matchResults?.standard_other_theme_top_matches, selectedMatch]);

    const getMatchResultsViewForPool = (baseMatches = []) => {
        if (!matchOnlyLayout) {
            return sortTopMatchesByMetrics(baseMatches);
        }

        const legacyColumns = matchResults?.legacy_columns || [];
        const totalLegacyLayout = dedupeByColumnKey(
            legacyColumns.filter((c) => isLayoutYes(c.report_layout))
        ).length;

        const layoutMatches = baseMatches.map((match) => {
            const layoutMatchedColumns = dedupeByColumnKey(
                (match.matched_columns || []).filter(
                    (c) => isLayoutYes(c.legacy_report_layout) && isLayoutYes(c.catalyst_report_layout)
                ),
                { keepBestScore: true }
            );
            const overlapCount = layoutMatchedColumns.length;
            const overlapPercentage = totalLegacyLayout
                ? Math.round((overlapCount / totalLegacyLayout) * 1000) / 10
                : 0;

            return {
                ...match,
                overlap_count: overlapCount,
                overlap_percentage: overlapPercentage,
                total_legacy_columns: totalLegacyLayout
            };
        });

        return layoutMatches.sort((a, b) => {
            if (b.overlap_percentage !== a.overlap_percentage) {
                return b.overlap_percentage - a.overlap_percentage;
            }
            if (b.overlap_count !== a.overlap_count) {
                return b.overlap_count - a.overlap_count;
            }
            return (a.catalyst_report_name || '').localeCompare(b.catalyst_report_name || '');
        });
    };

    const standardBaseMatches = useMemo(() => {
        if (!matchResults) return [];
        if (Array.isArray(matchResults.standard_top_matches)) {
            return matchResults.standard_top_matches;
        }
        return getAllTopMatches(matchResults).filter((match) => !isCustomCatalystReport(match));
    }, [matchResults]);

    const customBaseMatches = useMemo(() => {
        if (!matchResults) return [];
        if (Array.isArray(matchResults.custom_top_matches)) {
            return matchResults.custom_top_matches;
        }
        return getAllTopMatches(matchResults).filter((match) => isCustomCatalystReport(match));
    }, [matchResults]);
    const standardOtherThemeBaseMatches = useMemo(() => {
        if (!matchResults) return [];
        if (Array.isArray(matchResults.standard_other_theme_top_matches)) {
            return matchResults.standard_other_theme_top_matches;
        }
        return [];
    }, [matchResults]);

    const standardMatchResultsView = useMemo(
        () => getMatchResultsViewForPool(standardBaseMatches),
        [matchOnlyLayout, matchResults?.legacy_columns, standardBaseMatches]
    );
    const customMatchResultsView = useMemo(
        () => getMatchResultsViewForPool(customBaseMatches),
        [matchOnlyLayout, matchResults?.legacy_columns, customBaseMatches]
    );
    const standardOtherThemeMatchResultsView = useMemo(
        () => getMatchResultsViewForPool(standardOtherThemeBaseMatches),
        [matchOnlyLayout, matchResults?.legacy_columns, standardOtherThemeBaseMatches]
    );

    const filteredMatchResultsBySearch = (matches) => {
        const query = String(matchSectionSearch || '').trim().toLowerCase();
        if (!query) return matches;
        return (matches || []).filter((match) =>
            String(match?.catalyst_report_name || '').toLowerCase().includes(query)
        );
    };

    const standardMatchResultsFilteredView = useMemo(
        () => filteredMatchResultsBySearch(standardMatchResultsView),
        [matchSectionSearch, standardMatchResultsView]
    );
    const customMatchResultsFilteredView = useMemo(
        () => filteredMatchResultsBySearch(customMatchResultsView),
        [customMatchResultsView, matchSectionSearch]
    );
    const standardOtherThemeMatchResultsFilteredView = useMemo(
        () => filteredMatchResultsBySearch(standardOtherThemeMatchResultsView),
        [matchSectionSearch, standardOtherThemeMatchResultsView]
    );

    const getMatchSectionEmptyMessage = (defaultMessage) => (
        matchSectionSearch.trim()
            ? `No reports match "${matchSectionSearch.trim()}" in this section.`
            : defaultMessage
    );

    const recommendationTableOptions = useMemo(
        () => Array.from(new Set((recommendations?.recommendations || []).map(getRecommendationLegacyTableName).filter(Boolean)))
            .sort((left, right) => String(left).localeCompare(String(right))),
        [recommendations]
    );
    const recommendationColumnOptions = useMemo(
        () => Array.from(new Set((recommendations?.recommendations || []).map((rec) => rec?.legacy_column).filter(Boolean)))
            .sort((left, right) => String(left).localeCompare(String(right))),
        [recommendations]
    );
    const recommendationRowsView = useMemo(() => (
        (recommendations?.recommendations || []).map((rec, idx) => {
            const presentation = getRecommendationRowPresentation(rec, idx);
            return {
                rec: {
                    ...rec,
                    __recommendation_row_key: presentation.recommendationKey,
                },
                idx,
                presentation,
            };
        })
    ), [recommendations, recommendationStageSelections, recommendationSubstageSelections, recommendationVisibleCounts]);
    const filteredRecommendationRowsView = useMemo(() => (
        recommendationRowsView.filter(({ rec, presentation }) => {
            if (recommendationTableFilter && getRecommendationLegacyTableName(rec) !== recommendationTableFilter) return false;
            if (recommendationColumnFilter && rec?.legacy_column !== recommendationColumnFilter) return false;
            if (recommendationStatusFilter && presentation.overallStatus !== recommendationStatusFilter) return false;
            if (recommendationStageFilter) {
                const matchingStage = presentation.availableStages.find((stage) => stage.key === recommendationStageFilter);
                if (!matchingStage || matchingStage.count === 0) return false;
            }
            return true;
        })
    ), [recommendationRowsView, recommendationTableFilter, recommendationColumnFilter, recommendationStatusFilter, recommendationStageFilter]);

    // =========================================================================
    // RENDER
    // =========================================================================

    if (!workspaceId) {
        return (
            <Box sx={{ p: 4 }}>
                {/* Page Header */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom fontWeight={700} color="text.primary">
                            Workspaces
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Create and manage report matching workspaces for claim platform migrations.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowCreateDialog(true)}
                        size="large"
                    >
                        New Workspace
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    {workspaces.map((ws) => (
                        <Grid item xs={12} md={6} lg={4} key={ws.id}>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 32px rgba(0, 119, 200, 0.15)',
                                        borderColor: 'primary.main',
                                    },
                                }}
                                onClick={() => navigate(`/report-matching/workspaces/${ws.id}`)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <FolderIcon sx={{ color: 'primary.main' }} />
                                            <Typography variant="h6" component="h2" fontWeight={600} color="text.primary">
                                                {ws.name}
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setWorkspaceToDelete(ws);
                                                setShowDeleteDialog(true);
                                            }}
                                            sx={{
                                                color: 'text.secondary',
                                                '&:hover': { color: 'error.main' }
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    {ws.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {ws.description}
                                        </Typography>
                                    )}

                                    {/* Status chips */}
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                        <Chip
                                            label={`${ws.matching_count || 0} Saved`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                        {ws.is_classified && (
                                            <Chip
                                                label="Ready"
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                </CardContent>
                                <CardActions sx={{ px: 2, pb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Created: {new Date(ws.created_at).toLocaleDateString()}
                                    </Typography>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                    {workspaces.length === 0 && !loading && (
                        <Grid item xs={12}>
                            <Card sx={{ textAlign: 'center', py: 6 }}>
                                <FolderOpenIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    No workspaces yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Create your first workspace to start report matching.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setShowCreateDialog(true)}
                                >
                                    Create Workspace
                                </Button>
                            </Card>
                        </Grid>
                    )}
                </Grid>

                {/* Create Workspace Dialog */}
                <Dialog
                    open={showCreateDialog}
                    onClose={() => {
                        setShowCreateDialog(false);
                        setCreateWorkspaceMode('upload_later');
                        setSelectedExistingLineageFile(null);
                    }}
                    maxWidth="sm"
                    fullWidth
                    TransitionProps={{
                        onEnter: () => {
                            loadSchemaProjects();
                            loadExistingIndexedLineageFiles();
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 600 }}>Create New Workspace</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Workspace Name"
                            fullWidth
                            value={newWorkspaceName}
                            onChange={(e) => setNewWorkspaceName(e.target.value)}
                            placeholder="e.g., UDS to Catalyst Migration"
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            label="Description (optional)"
                            fullWidth
                            multiline
                            rows={3}
                            value={newWorkspaceDesc}
                            onChange={(e) => setNewWorkspaceDesc(e.target.value)}
                            placeholder="Brief description of what this workspace is for..."
                            sx={{ mb: 2 }}
                        />
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                Workspace Source
                            </Typography>
                            <ToggleButtonGroup
                                exclusive
                                fullWidth
                                value={createWorkspaceMode}
                                onChange={(event, value) => {
                                    if (!value) return;
                                    setCreateWorkspaceMode(value);
                                    if (value !== 'use_existing_index') {
                                        setSelectedExistingLineageFile(null);
                                    }
                                }}
                                size="small"
                            >
                                <ToggleButton value="upload_later">
                                    Upload Later
                                </ToggleButton>
                                <ToggleButton value="use_existing_index">
                                    Use Existing Index
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {createWorkspaceMode === 'use_existing_index' && (
                            <Box sx={{ mb: 2 }}>
                                <Autocomplete
                                    options={existingIndexedLineageFiles}
                                    getOptionLabel={(option) => option?.filename || ''}
                                    value={selectedExistingLineageFile}
                                    onChange={(event, value) => setSelectedExistingLineageFile(value)}
                                    isOptionEqualToValue={(option, value) => option?.file_id === value?.file_id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Existing Indexed Lineage"
                                            placeholder="Select a classified and indexed lineage file"
                                            helperText="This starts the workspace directly at report matching using an existing Catalyst index."
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={600} noWrap>
                                                    {option.filename}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Legacy {option.legacy_report_count || 0} • Catalyst {option.catalyst_report_count || 0} • Uploaded {formatDisplayDate(option.uploaded_at)}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    )}
                                />
                                {existingIndexedLineageFiles.length === 0 && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        No classified and indexed lineage files are available yet.
                                    </Alert>
                                )}
                            </Box>
                        )}
                        <Autocomplete
                            options={schemaMapperProjects}
                            getOptionLabel={(option) => option.name || ''}
                            value={selectedSchemaProject}
                            onChange={(e, value) => setSelectedSchemaProject(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Schema Mapper Project (optional)"
                                    placeholder="Select a project for future gap recommendations"
                                    helperText="Recommendations are deferred; this link will be used later"
                                />
                            )}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setShowCreateDialog(false)} color="inherit">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateWorkspace}
                            variant="contained"
                            disabled={
                                !newWorkspaceName.trim()
                                || loading
                                || (createWorkspaceMode === 'use_existing_index' && !selectedExistingLineageFile?.file_id)
                            }
                        >
                            {loading ? 'Creating...' : 'Create Workspace'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Workspace Dialog */}
                <Dialog
                    open={showDeleteDialog}
                    onClose={() => setShowDeleteDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ fontWeight: 600 }}>Delete Workspace</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete <strong>{workspaceToDelete?.name}</strong>?
                            This will permanently remove all matching history and uploaded files.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setShowDeleteDialog(false)} color="inherit">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteWorkspace}
                            variant="contained"
                            color="error"
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    if (!selectedWorkspace && loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!selectedWorkspace) return null;

    return (
        <Box sx={{ p: 1 }}>
            {activeStep === 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0, maxWidth: 760 }}>
                            <TextField
                                fullWidth
                                label="Workspace Name"
                                value={workspaceNameDraft}
                                onChange={(event) => setWorkspaceNameDraft(event.target.value)}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    sx: {
                                        fontSize: '1.75rem',
                                        fontWeight: 700,
                                        color: 'primary.main',
                                    },
                                }}
                            />
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                label="Description"
                                value={workspaceDescDraft}
                                onChange={(event) => setWorkspaceDescDraft(event.target.value)}
                                placeholder="No description provided."
                            />
                            {selectedWorkspace.lineage_filename && (
                                <Chip
                                    icon={<FileIcon />}
                                    label={selectedWorkspace.lineage_filename}
                                    size="small"
                                    sx={{ mt: 2, bgcolor: 'background.paper' }}
                                />
                            )}
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/report-matching/workspaces')}
                                variant="outlined"
                                size="small"
                            >
                                All Workspaces
                            </Button>
                        </Stack>
                    </Box>
                    <Card
                        variant="outlined"
                        sx={{
                            mt: 3,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            width: '100%',
                        }}
                    >
                        <CardContent sx={{ pb: '16px !important' }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} lg={5}>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        Workspace Settings
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Rename the workspace, update its description, and change the linked schema project at any time.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                        <Autocomplete
                                            fullWidth
                                            options={schemaMapperProjects}
                                            getOptionLabel={(option) => option.name || ''}
                                            value={schemaMapperProjects.find((project) => project.id === workspaceSchemaProjectId) || null}
                                            onChange={(e, value) => setWorkspaceSchemaProjectId(value?.id || '')}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Linked Schema Project"
                                                    placeholder="Select schema analyzer project"
                                                />
                                            )}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={savingWorkspaceSettings ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                            onClick={handleUpdateWorkspaceSchemaProject}
                                            disabled={savingWorkspaceSettings || !selectedWorkspace || !workspaceNameDraft.trim() || !hasWorkspaceSettingChanges}
                                            sx={{
                                                minWidth: 132,
                                                backgroundColor: 'primary.main',
                                                color: savingWorkspaceSettings || !selectedWorkspace || !workspaceNameDraft.trim() || !hasWorkspaceSettingChanges
                                                    ? 'grey.500'
                                                    : '#fff',
                                                borderRadius: 999,
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                '&:hover': {
                                                    backgroundColor: savingWorkspaceSettings || !selectedWorkspace || !workspaceNameDraft.trim() || !hasWorkspaceSettingChanges
                                                        ? 'grey.300'
                                                        : 'primary.dark',
                                                },
                                                '&.MuiButton-contained.Mui-disabled': {
                                                    backgroundColor: '#e0e0e0 !important',
                                                    color: '#9e9e9e !important',
                                                    boxShadow: 'none',
                                                },
                                                '& .MuiButton-startIcon': {
                                                    color: savingWorkspaceSettings || !selectedWorkspace || !workspaceNameDraft.trim() || !hasWorkspaceSettingChanges
                                                        ? 'grey.500'
                                                        : 'inherit',
                                                },
                                                '&.Mui-disabled .MuiButton-startIcon': {
                                                    color: '#9e9e9e !important',
                                                },
                                            }}
                                        >
                                            {savingWorkspaceSettings ? 'Saving...' : 'Save'}
                                        </Button>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, minWidth: { xl: 320 }, flexShrink: 0 }}>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                Workspace Processing Status
                                            </Typography>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}>
                                                    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                                                        <Typography variant="caption" color="text.secondary">Running</Typography>
                                                        <Typography variant="h6" fontWeight={800}>{workspaceQueueSummary.running}</Typography>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                                                        <Typography variant="caption" color="text.secondary">Queued</Typography>
                                                        <Typography variant="h6" fontWeight={800}>{workspaceQueueSummary.queued}</Typography>
                                                    </Paper>
                                                </Grid>
                                            </Grid>
                                            <Typography variant="caption" color="text.secondary">
                                                {workspaceQueueSummary.running > 0
                                                    ? 'A report is currently running. Additional requests stay queued.'
                                                    : 'No report is running right now.'}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Paper>
            )}

            {/* Stepper */}
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {steps.map((step, index) => (
                    <Step key={step.label}>
                        <StepLabel
                            onClick={() => {
                                if (index < activeStep) setActiveStep(index);
                                if (index === 1 && canViewClassification) setActiveStep(1);
                                if (index === 2 && canStartMatching) setActiveStep(2);
                            }}
                            sx={{
                                cursor: (
                                    index < activeStep
                                    || (index === 1 && canViewClassification)
                                    || (index === 2 && canStartMatching)
                                ) ? 'pointer' : 'default'
                            }}
                        >
                            {step.label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Step 0: Upload & Classify */}
            {activeStep === 0 && (
                <Fade in>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    height: '100%',
                                    border: '2px dashed',
                                    borderColor: selectedWorkspace.lineage_file_id ? 'success.light' : 'primary.light',
                                    bgcolor: alpha(selectedWorkspace.lineage_file_id ? '#2e7d32' : '#0077C8', 0.02)
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                    <UploadIcon sx={{ fontSize: 60, color: selectedWorkspace.lineage_file_id ? 'success.main' : 'primary.main', mb: 2 }} />
                                    <Typography variant="h5" fontWeight={600} gutterBottom>
                                        {selectedWorkspace.lineage_file_id ? 'Lineage Uploaded' : 'Upload Lineage'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" mb={3} sx={{ maxWidth: 300, mx: 'auto' }}>
                                        Upload the Excel data dictionary containing Legacy and Catalyst reports.
                                    </Typography>
                                    <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                        {selectedWorkspace.lineage_file_id && (
                                            <FormControlLabel
                                                control={(
                                                    <Switch
                                                        checked={incrementalUploadEnabled}
                                                        onChange={(event) => setIncrementalUploadEnabled(event.target.checked)}
                                                        color="primary"
                                                    />
                                                )}
                                                label="Incremental upload"
                                                sx={{
                                                    m: 0,
                                                    justifyContent: 'center',
                                                    '.MuiFormControlLabel-label': {
                                                        fontWeight: 500,
                                                    },
                                                }}
                                            />
                                        )}
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1.5}
                                            alignItems="center"
                                            justifyContent="center"
                                            useFlexGap
                                            sx={{ width: '100%' }}
                                        >
                                            <Button
                                                variant={selectedWorkspace.lineage_file_id ? "outlined" : "contained"}
                                                component="label"
                                                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                                                disabled={uploading}
                                                sx={{ borderRadius: 2, px: 4, minWidth: 190 }}
                                            >
                                                {uploading ? 'Uploading...' : selectedWorkspace.lineage_file_id ? (incrementalUploadEnabled ? 'Add Reports' : 'Replace File') : 'Choose File'}
                                                <input type="file" hidden accept=".xlsx,.xls" onChange={handleFileUpload} />
                                            </Button>
                                            {selectedWorkspace.lineage_file_id && (
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={downloadingLineage ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
                                                    onClick={handleDownloadWorkspaceLineage}
                                                    disabled={downloadingLineage}
                                                    sx={{ borderRadius: 2, px: 4, minWidth: 220 }}
                                                >
                                                    {downloadingLineage ? 'Preparing Download...' : 'Download Cleaned Lineage'}
                                                </Button>
                                            )}
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: alpha('#AutoAwesome', 0.02)
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 60, color: selectedWorkspace.is_classified ? 'success.main' : 'secondary.main', mb: 2 }} />
                                    <Typography variant="h5" fontWeight={600} gutterBottom>
                                        {selectedWorkspace.is_classified ? 'Classified' : 'Classify Reports'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" mb={3} sx={{ maxWidth: 300, mx: 'auto' }}>
                                        Automatically identify report themes based on column keywords and filter logic.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={classifying ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                                        onClick={handleClassify}
                                        disabled={!selectedWorkspace.lineage_file_id || classifying || selectedWorkspace.is_classified}
                                        sx={{ borderRadius: 2, px: 4 }}
                                    >
                                        {classifying ? 'Classifying...' : selectedWorkspace.is_classified ? 'Classified' : 'Run Classification'}
                                    </Button>
                                    {lineageClassificationState && lineageClassificationState.status !== 'not_started' && (
                                        <Alert
                                            severity={
                                                lineageClassificationState.status === 'completed'
                                                    ? 'success'
                                                    : lineageClassificationState.status === 'failed'
                                                        ? 'error'
                                                        : 'info'
                                            }
                                            variant="outlined"
                                            sx={{ mt: 3, textAlign: 'left' }}
                                        >
                                            <Stack spacing={1.5}>
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight={700}>
                                                        {lineageClassificationState.message || 'Classification in progress'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {lineageClassificationState.total_reports > 0
                                                            ? `${lineageClassificationState.completed_reports || 0} of ${lineageClassificationState.total_reports} reports processed`
                                                            : 'Preparing reports for classification'}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.max(0, Math.min(100, lineageClassificationState.progress || 0))}
                                                    sx={{ height: 8, borderRadius: 999 }}
                                                />
                                                <Stack direction="row" spacing={2} justifyContent="space-between" flexWrap="wrap" useFlexGap>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Progress: {Math.round(lineageClassificationState.progress || 0)}%
                                                    </Typography>
                                                    {lineageClassificationState.current_system && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Current system: {lineageClassificationState.current_system}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                                {lineageClassificationState.error && (
                                                    <Typography variant="caption" color="error.main">
                                                        {lineageClassificationState.error}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {selectedWorkspace.is_classified && (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => setActiveStep(1)}
                                        endIcon={<NextIcon />}
                                        sx={{ borderRadius: 2, px: 6, color: '#fff' }}
                                    >
                                        View Classification Details
                                    </Button>
                                    {canStartMatching && (
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setActiveStep(2)}
                                            endIcon={<NextIcon />}
                                            sx={{ borderRadius: 2, px: 6, color: '#fff' }}
                                        >
                                            Start Matching Reports
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        )}

                        {selectedWorkspace.lineage_file_id && (
                            <Grid item xs={12}>
                                <Card sx={{ borderRadius: 3 }}>
                                    <CardContent>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            Cleaning Summary
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" mb={3}>
                                            Before and after counts per report (deduplication, physical name, and layout filters).
                                        </Typography>
                                        {lineageMeta && (
                                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                                <Grid item xs={12} md={4}>
                                                    {renderTotalsCard(
                                                        'Legacy Rows (Cleaned)',
                                                        lineageMeta.legacy_row_count,
                                                        lineageMeta.legacy_raw_row_count,
                                                        'Raw'
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    {renderTotalsCard(
                                                        'Catalyst Rows (Cleaned)',
                                                        lineageMeta.catalyst_row_count,
                                                        lineageMeta.catalyst_raw_row_count,
                                                        'Raw'
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    {(() => {
                                                        const expectedDocs = lineageMeta.catalyst_index_total_count ?? lineageMeta.catalyst_index_row_count;
                                                        const uniqueDocs = lineageDiagnostics?.unique_doc_ids;
                                                        const logical = lineageMeta.catalyst_index_logical_count;
                                                        const physical = lineageMeta.catalyst_index_physical_count;
                                                        const excluded = lineageDiagnostics?.missing_logical_rows ?? lineageMeta.catalyst_missing_logical_total;
                                                        const duplicateIds = lineageDiagnostics?.duplicate_doc_ids;
                                                        const lines = [];
                                                        const addLine = (label, value) => {
                                                            if (value === null || value === undefined) return;
                                                            lines.push(`${label}: ${formatCount(value)}`);
                                                        };
                                                        addLine('Will index (unique docs)', uniqueDocs);
                                                        addLine('Logical names', logical);
                                                        addLine('Physical names', physical);
                                                        addLine('Expected docs (logical + physical)', expectedDocs);
                                                        addLine('Duplicate IDs (overwrites)', duplicateIds);
                                                        addLine('Excluded rows without logical names', excluded);
                                                        const headlineValue = uniqueDocs ?? expectedDocs;
                                                        return renderIndexCard(
                                                            'Catalyst Rows to Index',
                                                            headlineValue,
                                                            lines
                                                        );
                                                    })()}
                                                </Grid>
                                            </Grid>
                                        )}
                                        {lineageMeta?.cleaning_summary ? (
                                            <>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        {renderCleaningSummaryTable('Legacy', lineageMeta.cleaning_summary.legacy || [])}
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        {renderCleaningSummaryTable('Catalyst', lineageMeta.cleaning_summary.catalyst || [])}
                                                    </Grid>
                                                </Grid>
                                                {renderMissingLogicalTable(
                                                    missingLogicalSystem === 'legacy'
                                                        ? (lineageMeta.legacy_missing_logical || [])
                                                        : (lineageMeta.catalyst_missing_logical || []),
                                                    missingLogicalSystem
                                                )}
                                            </>
                                        ) : (
                                            <Alert severity="info">
                                                Cleaning summary not available. Re-upload the lineage file to generate it.
                                            </Alert>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Fade>
            )}

            {/* Step 1: Classification Details */}
            {activeStep === 1 && (
                <Fade in>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="h5" fontWeight={700}>Classification Details</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Review theme assignments for Legacy and Catalyst reports.
                                </Typography>
                            </Box>
                            <ToggleButtonGroup
                                value={classificationMode}
                                exclusive
                                onChange={(event, value) => {
                                    if (value) setClassificationMode(value);
                                }}
                                size="small"
                            >
                                <ToggleButton value="local">Local Themes</ToggleButton>
                                <ToggleButton value="global">Global Themes</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {classificationLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    {renderThemeStatsCard('Legacy', classificationStats.legacy, 'legacy')}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    {renderThemeStatsCard('Catalyst', classificationStats.catalyst, 'catalyst')}
                                </Grid>
                            </Grid>
                        )}

                        <Card sx={{ borderRadius: 3, mt: 3 }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap">
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>Index Catalyst Columns</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Indexing is required before matching can run.
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip
                                            label={
                                                lineageMeta?.is_indexed
                                                    ? 'Indexed'
                                                    : lineageIndexState?.status === 'running'
                                                        ? 'Indexing'
                                                        : (lineageIndexState?.status === 'failed' || lineageIndexState?.status === 'interrupted')
                                                            ? 'Resume Ready'
                                                            : 'Not Indexed'
                                            }
                                            color={
                                                lineageMeta?.is_indexed
                                                    ? 'success'
                                                    : lineageIndexState?.status === 'failed'
                                                        ? 'error'
                                                        : lineageIndexState?.status === 'interrupted'
                                                            ? 'warning'
                                                            : lineageIndexState?.status === 'running'
                                                                ? 'info'
                                                                : 'warning'
                                            }
                                            variant="outlined"
                                            size="small"
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleIndex}
                                            startIcon={indexing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                                            disabled={indexing || !selectedWorkspace?.is_classified || lineageMeta?.is_indexed}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            {
                                                indexing
                                                    ? 'Indexing...'
                                                    : lineageMeta?.is_indexed
                                                        ? 'Indexed'
                                                        : (lineageIndexState?.status === 'failed' || lineageIndexState?.status === 'interrupted')
                                                            ? ((lineageIndexState?.resume_from_batch || 1) > 1 ? 'Resume Indexing' : 'Retry Indexing')
                                                            : 'Run Indexing'
                                            }
                                        </Button>
                                    </Stack>
                                </Stack>
                                {lineageIndexState && lineageIndexState.status !== 'not_started' && (
                                    <Alert
                                        severity={
                                            lineageIndexState.status === 'completed'
                                                ? 'success'
                                                : lineageIndexState.status === 'failed'
                                                    ? 'error'
                                                    : lineageIndexState.status === 'interrupted'
                                                        ? 'warning'
                                                        : 'info'
                                        }
                                        variant="outlined"
                                        sx={{ mt: 3, alignItems: 'stretch' }}
                                    >
                                        <Stack spacing={1.5}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700}>
                                                    {lineageIndexState.message || 'Lineage indexing in progress'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {lineageIndexState.total_docs > 0
                                                        ? `${lineageIndexState.completed_docs || 0} of ${lineageIndexState.total_docs} catalyst documents processed`
                                                        : 'Preparing catalyst documents for indexing'}
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.max(0, Math.min(100, lineageIndexState.progress || 0))}
                                                sx={{ height: 8, borderRadius: 999 }}
                                            />
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                justifyContent="space-between"
                                                flexWrap="wrap"
                                                useFlexGap
                                            >
                                                <Typography variant="caption" color="text.secondary">
                                                    Progress: {Math.round(lineageIndexState.progress || 0)}%
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Batch {lineageIndexState.current_batch || 0}
                                                    {lineageIndexState.total_batches ? ` / ${lineageIndexState.total_batches}` : ''}
                                                </Typography>
                                                {!lineageMeta?.is_indexed && (lineageIndexState.resume_from_batch || 1) > 1 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Resume point: batch {lineageIndexState.resume_from_batch}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            {lineageIndexState.error && (
                                                <Typography variant="caption" color="error.main">
                                                    {lineageIndexState.error}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Alert>
                                )}
                                {lineageMeta?.is_indexed && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setActiveStep(2)}
                                            endIcon={<NextIcon />}
                                            sx={{ borderRadius: 2, px: 6 }}
                                        >
                                            Continue to Matching
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                    </Box>
                </Fade>
            )}

            {/* Step 2: Queue Reports */}
            {activeStep === 2 && (
                <Fade in>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Card
                                sx={{
                                    minHeight: 560,
                                    maxHeight: 760,
                                    borderRadius: 3,
                                    boxShadow: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <CardContent
                                    sx={{
                                        p: 4,
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        spacing={2}
                                        sx={{ mb: 3 }}
                                    >
                                        <Typography variant="h5" fontWeight={700}>
                                            Identify Matches
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<CompareArrowsIcon />}
                                            onClick={openCompareDialog}
                                            sx={{ color: '#fff' }}
                                        >
                                            Compare Reports
                                        </Button>
                                    </Stack>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Step 2: Queue one or more legacy reports. They will run sequentially in the order shown below.
                                    </Typography>
                                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        <Autocomplete
                                            multiple
                                            disableCloseOnSelect
                                            filterSelectedOptions
                                            limitTags={3}
                                            options={legacyReports}
                                            getOptionLabel={(opt) => opt?.report_name || ''}
                                            value={queuedLegacyReports}
                                            onChange={(e, value) => {
                                                const deduped = [];
                                                const seen = new Set();
                                                value.forEach((item) => {
                                                    const reportName = item?.report_name || '';
                                                    if (!reportName || seen.has(reportName)) return;
                                                    seen.add(reportName);
                                                    deduped.push(item);
                                                });
                                                setQueuedLegacyReports(deduped);
                                                if (!selectedLegacyReport && deduped[0]) {
                                                    setSelectedLegacyReport(deduped[0]);
                                                }
                                            }}
                                            isOptionEqualToValue={(option, value) => option?.report_name === value?.report_name}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder="Search and select legacy reports..." fullWidth />
                                            )}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props} key={option?.report_name}>
                                                    <Box>
                                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                            <Typography variant="body1" fontWeight={600}>{option?.report_name}</Typography>
                                                            {savedMatchingByReport.get(option?.report_name) && (
                                                                <Chip label="Output Ready" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                            {latestWorkspaceJobByReport.get(option?.report_name)?.status === 'running' && (
                                                                <Chip label="Running" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                            {latestWorkspaceJobByReport.get(option?.report_name)?.status === 'queued' && (
                                                                <Chip label="Queued" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                        </Stack>
                                                        <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" useFlexGap>
                                                            <Chip label={`${option?.column_count || 0} cols`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                            {(globalThemeOnly ? option?.global_themes : option?.themes)?.map(t => (
                                                                <Chip
                                                                    key={t}
                                                                    label={t}
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                </Box>
                                            )}
                                            sx={{
                                                mb: queuedLegacyReports.length > 0 ? 2 : 0,
                                                mt: 1,
                                                flexShrink: 0,
                                                '& .MuiAutocomplete-inputRoot': {
                                                    minHeight: 52,
                                                    pr: 4,
                                                    alignItems: 'center',
                                                },
                                            }}
                                        />

                                        {queuedLegacyReports.length > 0 && (
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    flex: 1,
                                                    minHeight: 180,
                                                    maxHeight: 380,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0 }}>
                                                    <Typography variant="subtitle2" fontWeight={700}>
                                                        Queue Order
                                                    </Typography>
                                                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
                                                        {queuedLegacyReports.map((report, index) => (
                                                            <Stack
                                                                key={report.report_name}
                                                                direction="row"
                                                                spacing={1}
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                sx={{ py: 0.75, borderBottom: index === queuedLegacyReports.length - 1 ? 'none' : '1px solid', borderColor: 'divider' }}
                                                            >
                                                                <Box sx={{ minWidth: 0 }}>
                                                                    <Typography variant="body2" fontWeight={600} noWrap>
                                                                        {index + 1}. {report.report_name}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {report.column_count || 0} columns
                                                                    </Typography>
                                                                </Box>
                                                                <Stack direction="row" spacing={0.5}>
                                                                    {isReportOutputReady(report.report_name) ? (
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onClick={() => handleViewQueuedReportMatches(report)}
                                                                            sx={{ textTransform: 'none', minWidth: 108, px: 1, py: 0.35, fontSize: '0.78rem' }}
                                                                        >
                                                                            View Matches
                                                                        </Button>
                                                                    ) : null}
                                                                    <IconButton size="small" onClick={() => moveQueuedLegacyReport(index, -1)} disabled={index === 0}>
                                                                        <MoveUpIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton size="small" onClick={() => moveQueuedLegacyReport(index, 1)} disabled={index === queuedLegacyReports.length - 1}>
                                                                        <MoveDownIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => setQueuedLegacyReports((prev) => prev.filter((item) => item.report_name !== report.report_name))}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Stack>
                                                            </Stack>
                                                        ))}
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        )}
                                    </Box>

                                    <Box sx={{ mt: 2, mb: 1, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 2, flexShrink: 0 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary">Matching Configuration</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                    Std {topN} • Custom {customTopN} • Other {standardOtherThemeTopN} • Min {finalMatchThreshold}% • {globalThemeOnly ? 'Global Themes' : (sameThemeOnly ? 'Themes' : 'All')} • {ignoreCommon ? 'Ignore Common' : 'All Columns'} • AI Verified
                                                  </Typography>
                                              </Box>
                                            <Button
                                                variant="contained"
                                                startIcon={<SettingsIcon />}
                                                onClick={() => setIsConfigOpen(true)}
                                                sx={{ borderRadius: 2, color: '#fff' }}
                                            >
                                                Matching Options
                                            </Button>
                                        </Stack>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        startIcon={queueSubmitting ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                        onClick={handleFindMatches}
                                        disabled={pendingQueueReports.length === 0 || queueSubmitting}
                                        sx={{ borderRadius: 2, py: 1.5, fontSize: '1.05rem', boxShadow: '0 4px 14px 0 rgba(0,119,200,0.39)', flexShrink: 0 }}
                                    >
                                        {queueSubmitting
                                            ? 'Submitting Queue...'
                                            : `Queue ${pendingQueueReports.length || ''} Report${pendingQueueReports.length === 1 ? '' : 's'}`.trim()}
                                    </Button>

                                    {activeMatchJob && isMatchJobActive(activeMatchJob) && (
                                        <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, maxHeight: 160, overflowY: 'auto', flexShrink: 0 }}>
                                            <Stack spacing={1}>
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="subtitle2" fontWeight={700}>
                                                            Current Queue Status
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                            {activeMatchJob.legacy_report_name}
                                                        </Typography>
                                                    </Box>
                                                    {isMatchJobCancelable(activeMatchJob) && (
                                                        <Button
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                            startIcon={stoppingMatchJobIds[activeMatchJob.id] ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
                                                            onClick={() => handleCancelMatchJob(activeMatchJob)}
                                                            disabled={Boolean(stoppingMatchJobIds[activeMatchJob.id])}
                                                        >
                                                            Stop
                                                        </Button>
                                                    )}
                                                    {isMatchJobDeletable(activeMatchJob) && (
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={() => handleDeleteMatchJob(activeMatchJob)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    )}
                                                </Stack>
                                                <Typography variant="body2" color="text.secondary">
                                                    {activeMatchJob.progress_message || 'Waiting for status...'}
                                                </Typography>
                                                {renderMatchJobProgress(activeMatchJob)}
                                                {shouldShowMatchJobReason(activeMatchJob) && (
                                                    <Typography variant="caption" color={getMatchJobReasonColor(activeMatchJob.status)}>
                                                        {getMatchJobReason(activeMatchJob)}
                                                    </Typography>
                                                )}
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    <Chip
                                                        size="small"
                                                        label={activeMatchJob.status}
                                                        color={getMatchJobStatusColor(activeMatchJob.status)}
                                                    />
                                                    {activeMatchJob.result_summary?.top_match_count !== undefined && (
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label={`${activeMatchJob.result_summary.top_match_count} matches`}
                                                        />
                                                    )}
                                                </Stack>
                                                {(activeMatchJob.logs || []).slice(-3).map((entry, index) => (
                                                    <Typography key={`${entry.timestamp}-${index}`} variant="caption" color="text.secondary">
                                                        [{parseApiDate(entry.timestamp)?.toLocaleTimeString() || '-'}] {entry.message}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </Paper>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Queue and History */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ minHeight: 560, borderRadius: 3 }}>
                                <CardContent
                                    sx={{
                                        p: 4,
                                        height: 560,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={3}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <HistoryIcon color="primary" />
                                            <Typography variant="h6" fontWeight={700}>Queue and History</Typography>
                                        </Stack>
                                    </Stack>
                                    <Box sx={{ mb: 3, flexShrink: 0 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                Latest Queue State
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => openHistoryView('history')}
                                                sx={{
                                                    color: '#fff',
                                                    flexShrink: 0,
                                                    minWidth: 92,
                                                    px: 1.65,
                                                    py: 0.42,
                                                    fontSize: '0.79rem',
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                View More
                                            </Button>
                                        </Stack>
                                        {latestWorkspaceJobs.length === 0 ? (
                                            <Box sx={{ py: 2 }}>
                                                <Typography variant="body2" color="text.secondary">No queue activity yet.</Typography>
                                            </Box>
                                        ) : (
                                            <List disablePadding sx={{ maxHeight: 180, overflow: 'auto', pr: 1 }}>
                                                {latestWorkspaceJobs.map((job, index) => (
                                                    <Paper key={`${job.id}-${index}`} variant="outlined" sx={{ mb: 1, borderRadius: 2, p: 1.5 }}>
                                                        <Stack spacing={0.75}>
                                                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                                                <Typography variant="body2" fontWeight={600} noWrap>
                                                                    {job.legacy_report_name}
                                                                </Typography>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Chip
                                                                        size="small"
                                                                        label={job.status}
                                                                        color={getMatchJobStatusColor(job.status)}
                                                                    />
                                                                    {isMatchJobCancelable(job) && (
                                                                        <IconButton
                                                                            size="small"
                                                                            color="warning"
                                                                            onClick={() => handleCancelMatchJob(job)}
                                                                            disabled={Boolean(stoppingMatchJobIds[job.id])}
                                                                        >
                                                                            {stoppingMatchJobIds[job.id]
                                                                                ? <CircularProgress size={16} color="inherit" />
                                                                                : <StopIcon fontSize="small" />}
                                                                        </IconButton>
                                                                    )}
                                                                    {isMatchJobDeletable(job) && (
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleDeleteMatchJob(job)}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    )}
                                                                </Stack>
                                                            </Stack>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {job.progress_message || 'Waiting for status...'}
                                                            </Typography>
                                                            {renderMatchJobProgress(job, { compact: true })}
                                                            {shouldShowMatchJobReason(job) && (
                                                                <Typography variant="caption" color={getMatchJobReasonColor(job.status)}>
                                                                    {getMatchJobReason(job)}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Paper>
                                                ))}
                                            </List>
                                        )}
                                    </Box>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5, flexShrink: 0 }}>
                                        <Typography variant="subtitle2" fontWeight={700}>
                                            Output History
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => openHistoryView('outputs')}
                                            sx={{
                                                color: '#fff',
                                                flexShrink: 0,
                                                minWidth: 92,
                                                px: 1.65,
                                                py: 0.42,
                                                fontSize: '0.79rem',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            View More
                                        </Button>
                                    </Stack>
                                    {savedMatchings.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 4, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">No reports matched yet.</Typography>
                                        </Box>
                                    ) : (
                                        <List disablePadding sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 1 }}>
                                            {savedMatchings.map((m, i) => (
                                                <Paper
                                                    key={i}
                                                    variant="outlined"
                                                    sx={{
                                                        mb: 1.5,
                                                        borderRadius: 2,
                                                        overflow: 'hidden',
                                                        transition: 'background-color 160ms ease, border-color 160ms ease',
                                                        '&:hover': {
                                                            bgcolor: 'action.hover',
                                                            borderColor: 'primary.main',
                                                        },
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="center" sx={{ pr: 1 }}>
                                                        <ListItemButton
                                                            onClick={() => handleLoadSavedMatching(m.legacy_report_name)}
                                                            sx={{
                                                                flex: 1,
                                                                minWidth: 0,
                                                                '&:hover': { bgcolor: 'transparent' },
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={m.legacy_report_name}
                                                                secondary={`${m.match_count} matches found`}
                                                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true }}
                                                                secondaryTypographyProps={{ variant: 'caption' }}
                                                            />
                                                        </ListItemButton>
                                                        <Chip
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                            label="Output"
                                                            sx={{ mr: 0.5 }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleDeleteMatching(e, m.legacy_report_name)}
                                                            sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.light', color: 'white' } }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                </Paper>
                                            ))}
                                        </List>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Fade>
            )}

            {/* Step 3: Match Results */}
            {activeStep === 3 && matchResults && (
                <Fade in>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={() => setActiveStep(2)} sx={{ mr: 2 }}><ArrowBackIcon /></IconButton>
                                <Box>
                                    <Typography variant="h5" fontWeight={700}>Matching Reports</Typography>
                                    <Typography variant="body2" color="text.secondary">For legacy report: {matchResults.legacy_report}</Typography>
                                </Box>
                            </Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={matchOnlyLayout}
                                        onChange={(event) => setMatchOnlyLayout(event.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Match Only Used Headers"
                            />
                        </Box>

                        {matchResults.filtered_columns === 0 && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                No columns remain after applying Common/PII filters. Try disabling one or both filters and run matching again.
                            </Alert>
                        )}
                        <Stack
                            direction={{ xs: 'column', lg: 'row' }}
                            spacing={1.5}
                            sx={{ mb: 3 }}
                            alignItems={{ xs: 'stretch', lg: 'center' }}
                            justifyContent="space-between"
                        >
                            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="contained"
                                    startIcon={<InsightsIcon />}
                                    onClick={() => navigate(
                                        `/report-matching/workspaces/${selectedWorkspace.id}/telemetry?report=${encodeURIComponent(matchResults.legacy_report)}`,
                                        {
                                            state: {
                                                legacyReportName: matchResults.legacy_report,
                                                telemetry: matchResults.telemetry || null,
                                                telemetryDetail: matchResults.telemetry_detail || null,
                                                matchResults,
                                            },
                                        }
                                    )}
                                    sx={{ color: '#fff' }}
                                >
                                    View Match Telemetry
                                </Button>
                            </Stack>
                            <TextField
                                size="small"
                                placeholder="Search reports..."
                                value={matchSectionSearch}
                                onChange={(event) => setMatchSectionSearch(event.target.value)}
                                sx={{ width: { xs: '100%', lg: 320 } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Stack>

                        {renderMatchCardSection(
                            'Standard Matches',
                            standardMatchResultsFilteredView,
                            getMatchSectionEmptyMessage('No standard reports matched this legacy report.'),
                            'standard'
                        )}

                        {(includeCustom || customMatchResultsView.length > 0) && renderMatchCardSection(
                            'Custom Matches',
                            customMatchResultsFilteredView,
                            getMatchSectionEmptyMessage('No custom reports matched this legacy report.'),
                            'custom'
                        )}

                        {((matchResults?.standard_other_theme_pool_label || standardOtherThemeMatchResultsView.length > 0) && renderMatchCardSection(
                            `Standard (${matchResults?.standard_other_theme_pool_label || 'Other Themes'})`,
                            standardOtherThemeMatchResultsFilteredView,
                            getMatchSectionEmptyMessage('No standard reports matched in the other-theme scope.'),
                            'standard-other-theme'
                        ))}
                    </Box>
                </Fade>
            )}

            {/* Step 4: Gap Analysis */}
            {activeStep === 4 && gapView && (
                <Fade in>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={() => setActiveStep(3)} sx={{ mr: 2 }}><ArrowBackIcon /></IconButton>
                                <Box>
                                    <Typography variant="h5" fontWeight={700}>Gap Analysis</Typography>
                                    <Stack direction="row" alignItems="center" spacing={0.5} useFlexGap flexWrap="wrap">
                                        <Typography variant="body2" color="text.secondary">
                                            Comparing {selectedLegacyReport.report_name} vs {selectedMatch.catalyst_report_name}
                                        </Typography>
                                        <Tooltip title="View report paths and global themes">
                                            <IconButton
                                                size="small"
                                                onClick={() => setGapAnalysisInfoDialogOpen(true)}
                                                sx={{
                                                    color: 'primary.main',
                                                    p: 0,
                                                    ml: 0.25,
                                                    alignSelf: 'center',
                                                    '&:hover': {
                                                        bgcolor: 'transparent',
                                                        color: 'primary.dark',
                                                    },
                                                }}
                                            >
                                                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={hasCompletedRecommendationForSelectedMatch ? handleOpenRecommendations : handleGetRecommendations}
                                    disabled={recommending || (!hasCompletedRecommendationForSelectedMatch && gapView.gap_count === 0)}
                                    startIcon={recommending ? <CircularProgress size={18} color="inherit" /> : null}
                                    sx={{ color: '#fff' }}
                                >
                                    {recommending
                                        ? 'Preparing...'
                                        : hasCompletedRecommendationForSelectedMatch
                                            ? 'View Recommendations'
                                            : 'Get Recommendations'}
                                </Button>
                                {saving && (
                                    <Typography variant="body2" color="text.secondary">
                                        Saving changes...
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {renderTelemetryPanel(gapView.telemetry, 'Gap Analysis Telemetry')}

                        {activeRecommendationJob
                            && selectedLegacyReport?.report_name === activeRecommendationJob.legacy_report_name
                            && selectedMatch?.catalyst_report_name === activeRecommendationJob.catalyst_report_name && (
                                <Paper variant="outlined" sx={{ mb: 3, p: 2, borderRadius: 2 }}>
                                      <Stack spacing={1}>
                                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                              <Typography variant="subtitle2" fontWeight={700}>
                                                  Background Recommendation Status
                                            </Typography>
                                            {isRecommendationJobCancelable(activeRecommendationJob) && (
                                                <Button
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    startIcon={stoppingRecommendationJobIds[activeRecommendationJob.id] ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
                                                    disabled={Boolean(stoppingRecommendationJobIds[activeRecommendationJob.id])}
                                                    onClick={() => handleCancelRecommendationJob(activeRecommendationJob)}
                                                >
                                                    Stop
                                                  </Button>
                                              )}
                                          </Stack>
                                          <Stepper
                                              activeStep={getRecommendationJobStageIndex(activeRecommendationJob)}
                                              alternativeLabel
                                              sx={{
                                                  pt: 1,
                                                '& .MuiStepLabel-label': {
                                                    typography: 'caption',
                                                },
                                            }}
                                        >
                                              {RECOMMENDATION_JOB_STAGE_ORDER.map((stage) => (
                                                  <Step key={stage.key}>
                                                      <StepLabel>{stage.label}</StepLabel>
                                                  </Step>
                                              ))}
                                          </Stepper>
                                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                              {getRecommendationStartedAt(activeRecommendationJob, recommendations) && (
                                                  <Chip
                                                      size="small"
                                                      variant="outlined"
                                                      label={`Started: ${formatRecommendationJobTimestamp(getRecommendationStartedAt(activeRecommendationJob, recommendations))}`}
                                                  />
                                              )}
                                              {getRecommendationCompletedAt(activeRecommendationJob, recommendations) && (
                                                  <Chip
                                                      size="small"
                                                      variant="outlined"
                                                      label={`Completed: ${formatRecommendationJobTimestamp(getRecommendationCompletedAt(activeRecommendationJob, recommendations))}`}
                                                  />
                                              )}
                                              {activeRecommendationJob.status === 'queued' && Number(activeRecommendationJob.queue_ahead_count || 0) > 0 && (
                                                  <Chip
                                                      size="small"
                                                      variant="outlined"
                                                      color="warning"
                                                      label={`Queue Ahead: ${activeRecommendationJob.queue_ahead_count}`}
                                                  />
                                              )}
                                              {activeRecommendationJob.status === 'queued' && Number(activeRecommendationJob.active_queue_count || 0) > 0 && (
                                                  <Chip
                                                      size="small"
                                                      variant="outlined"
                                                      label={`Queue Position: ${activeRecommendationJob.queue_position || 1}/${activeRecommendationJob.active_queue_count}`}
                                                  />
                                              )}
                                          </Stack>
                                      </Stack>
                                  </Paper>
                              )}

                        <Grid container spacing={3} mb={4} columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                    <Typography variant="h3" fontWeight={800} color="info.main">{formatCount(gapAnalysisLegacyTotalFields)}</Typography>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Total Legacy Fields</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                    <Typography variant="h3" fontWeight={800} color="primary.main">{formatCount(gapView.total_legacy_columns)}</Typography>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Legacy Layout Fields</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                    <Typography variant="h3" fontWeight={800} color="success.main">{formatCount(gapView.matched_count)}</Typography>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Matched Fields</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                    <Typography variant="h3" fontWeight={800} color="warning.main">{formatCount(gapView.gap_count)}</Typography>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Detected Gaps</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {(gapView.legacy_breakdown || gapView.catalyst_breakdown) && (
                            <Accordion
                                expanded={breakdownExpanded}
                                onChange={(_, nextExpanded) => setBreakdownExpanded(nextExpanded)}
                                sx={{
                                    mb: 4,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 3,
                                    '&:before': { display: 'none' },
                                    overflow: 'hidden',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={breakdownExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    sx={{ px: 3, py: 0.5 }}
                                >
                                    <Stack spacing={0.25}>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            Breakdown Summaries
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Legacy and Catalyst row summaries used to derive the gap analysis.
                                        </Typography>
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Legacy Breakdown</Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <Stack spacing={1}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Raw Rows</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisLegacyRawBreakdown?.raw_rows)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">After Dedup</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisLegacyRawBreakdown?.dedup_rows)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Missing Logical</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisLegacyRawBreakdown?.missing_logical)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Ignored Common</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisLegacyRawBreakdown?.ignored_common)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Ignored PII</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisLegacyRawBreakdown?.ignored_pii)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Unique Rows</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisLegacyRawBreakdown?.after_ignore_rows)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Used in Report Layout</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisLegacyLayoutUsedCount)}</Typography>
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Catalyst Breakdown</Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <Stack spacing={1}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Raw Rows</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisCatalystRawBreakdown?.raw_rows)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">After Dedup</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisCatalystRawBreakdown?.dedup_rows)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Missing Logical</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisCatalystRawBreakdown?.missing_logical)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Ignored Common</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisCatalystRawBreakdown?.ignored_common)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Ignored PII</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisCatalystRawBreakdown?.ignored_pii)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Unique Rows</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisCatalystRawBreakdown?.after_ignore_rows)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Used in Report Layout</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{formatCount(gapAnalysisCatalystLayoutUsedCount)}</Typography>
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Overlap: <strong>{gapView.overlap_count ?? gapView.matched_count}</strong> of{' '}
                                                    <strong>{gapView.total_legacy_columns}</strong>{' '}
                                                    {gapAnalysisUsesFallbackScope ? 'legacy match-scope fields' : 'legacy matchable fields'} ({gapView.overlap_percentage ?? 0}%)
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        <Grid container spacing={4}>
                            <Grid item xs={12}>
                                <Typography variant="h6" fontWeight={700} color="success.main" mb={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CheckCircleIcon sx={{ mr: 1 }} /> LLM-Validated Matches ({gapView.matched_count})
                                </Typography>
                                {gapView.use_llm && (
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        All matches have been validated by LLM. Only columns confirmed as equivalent are shown.
                                    </Alert>
                                )}
                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxHeight: 400, overflow: 'auto' }}>
                                    <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, width: 200 }}>Legacy Logical Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: 130 }}>Legacy Physical</TableCell>
                                                <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 110 }}>Used in Layout</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: 220 }}>Catalyst Logical Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: 230 }}>Catalyst Physical</TableCell>
                                                <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 100 }}>Confidence</TableCell>
                                                <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 110 }}>Match Type</TableCell>
                                                <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 120 }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {gapView.matched_columns?.map((c, i) => {
                                                const resolvedCandidates = mergeMatchCandidates(c);
                                                const acceptedCandidateCount = resolvedCandidates.filter(
                                                    (candidate) => String(candidate?.preference_status || '').toLowerCase() === 'accepted'
                                                ).length;
                                                const matchedDecision = acceptedCandidateCount > 0
                                                    ? 'accepted'
                                                    : c.preference_status === 'rejected'
                                                        ? 'rejected'
                                                        : '';
                                                const matchedRowBg = matchedDecision === 'accepted'
                                                    ? alpha(theme.palette.success.main, 0.08)
                                                    : matchedDecision === 'rejected'
                                                        ? alpha(theme.palette.error.main, 0.08)
                                                        : getMatchTypeCategory(c.match_type) === 'llm_recovery'
                                                            ? alpha(theme.palette.success.main, 0.05)
                                                            : (i % 2 === 0 ? undefined : theme.palette.grey[50]);
                                                return (
                                                <TableRow key={i} sx={{ bgcolor: matchedRowBg }}>
                                                    {(() => {
                                                        const selectedCandidateKey = matchSelections[c.legacy_key]
                                                            || getMatchCandidateKey(
                                                                selectPreferredMatchCandidate(resolvedCandidates)
                                                                || {
                                                                    catalyst_logical: c.catalyst_column,
                                                                    catalyst_physical: c.catalyst_physical,
                                                                    catalyst_table: c.catalyst_table,
                                                                }
                                                            );
                                                        const selectedCandidate = resolvedCandidates.find(
                                                            (cand) => getMatchCandidateKey(cand) === selectedCandidateKey
                                                        ) || null;
                                                        const resolvedLogical = selectedCandidate?.catalyst_logical || c.catalyst_column || '-';
                                                        const resolvedPhysical = getMatchCandidatePhysicalLabel(selectedCandidate)
                                                            || formatPhysicalNameWithTable(c.catalyst_physical, c.catalyst_table)
                                                            || '-';
                                                        const resolvedConfidence = selectedCandidate?.score ?? c.llm_confidence ?? c.score ?? 0;
                                                        const resolvedMatchType = selectedCandidate?.match_type || c.match_type;
                                                        const resolvedPreferenceStatus = selectedCandidate?.preference_status || c.preference_status;
                                                        const resolvedPreferenceId = selectedCandidate?.preference_id || c.preference_id;
                                                        const resolvedPreferenceSource = selectedCandidate?.preference_source || c.preference_source;
                                                        const resolvedPreferenceNotes = selectedCandidate?.preference_notes || c.preference_notes;
                                                        const resolvedReasoning = selectedCandidate?.llm_reasoning || c.llm_reasoning || '';
                                                        const effectiveMatch = {
                                                            ...c,
                                                            catalyst_column: selectedCandidate?.catalyst_logical || c.catalyst_column,
                                                            catalyst_physical: selectedCandidate?.catalyst_physical || c.catalyst_physical,
                                                            catalyst_table: selectedCandidate?.catalyst_table || c.catalyst_table,
                                                            score: resolvedConfidence,
                                                            match_type: resolvedMatchType,
                                                            preference_status: resolvedPreferenceStatus,
                                                            preference_id: resolvedPreferenceId,
                                                            preference_source: resolvedPreferenceSource,
                                                            preference_notes: resolvedPreferenceNotes,
                                                        };
                                                        return (
                                                            <>
                                                                <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        {getMatchTypeCategory(c.match_type) === 'llm_recovery' && (
                                                                            <AutoAwesomeIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                                                        )}
                                                                        {renderLogicalNameWithTypeTooltip(
                                                                            c.legacy_column,
                                                                            c.column_type,
                                                                            <Typography variant="body2" sx={tableCellWrapSx}>
                                                                                {getLegacyLogicalDisplayName(c.legacy_column, c.legacy_physical)}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', ...tableCellWrapSx }}>
                                                                        {formatPhysicalNameWithTable(c.legacy_physical, c.legacy_table) || '-'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                                                                    {renderUsedInLayoutChip(c.legacy_report_layout ?? c.report_layout)}
                                                                </TableCell>
                                                                <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                                    <Box sx={{ minWidth: 200 }}>
                                                                        {renderLogicalNameWithTypeTooltip(
                                                                            resolvedLogical,
                                                                            selectedCandidate?.column_type || c.catalyst_column_type || c.column_type,
                                                                            <Chip
                                                                                label={resolvedLogical}
                                                                                size="small"
                                                                                color="success"
                                                                                variant="outlined"
                                                                                sx={chipTruncateSx}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                                    {resolvedCandidates.length > 1 ? (
                                                                        <Autocomplete
                                                                            size="small"
                                                                            options={resolvedCandidates}
                                                                            filterOptions={filterMatchCandidateOptions}
                                                                            selectOnFocus
                                                                            getOptionLabel={(option) => (
                                                                                typeof option === 'string'
                                                                                    ? option
                                                                                    : getMatchCandidatePhysicalLabel(option) || option.catalyst_logical || ''
                                                                            )}
                                                                            isOptionEqualToValue={(option, value) => (
                                                                                getMatchCandidateKey(option) === getMatchCandidateKey(value)
                                                                            )}
                                                                            value={resolvedCandidates.find(cand => getMatchCandidateKey(cand) === selectedCandidateKey) || null}
                                                                            onChange={(_, newValue) => {
                                                                                if (newValue) {
                                                                                    autosaveLastFailedSnapshotRef.current = '';
                                                                                    setWorkspaceDecisionDirty(true);
                                                                                    setMatchSelections(prev => ({
                                                                                        ...prev,
                                                                                        [c.legacy_key]: getMatchCandidateKey(newValue)
                                                                                    }));
                                                                                }
                                                                            }}
                                                                            renderOption={(props, option) => (
                                                                                <Box component="li" {...props} key={getMatchCandidateKey(option)}>
                                                                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                                                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                                            {getMatchCandidatePhysicalLabel(option) || option.catalyst_logical || '-'}
                                                                                        </Typography>
                                                                                        {String(option?.preference_status || '').trim() && (
                                                                                            <Chip
                                                                                                size="small"
                                                                                                label={String(option.preference_status).toLowerCase() === 'accepted' ? 'Accepted' : 'Rejected'}
                                                                                                color={String(option.preference_status).toLowerCase() === 'accepted' ? 'success' : 'error'}
                                                                                                variant="outlined"
                                                                                                sx={{ height: 20, fontSize: '0.65rem' }}
                                                                                            />
                                                                                        )}
                                                                                    </Stack>
                                                                                </Box>
                                                                            )}
                                                                            renderInput={(params) => <TextField {...params} variant="standard" />}
                                                                        />
                                                                    ) : (
                                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', ...tableCellWrapSx }}>
                                                                            {resolvedPhysical}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                                                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                                                        <Chip
                                                                            label={`${Math.round((resolvedConfidence || 0) * 100)}%`}
                                                                            size="small"
                                                                            color={resolvedConfidence >= 0.9 ? 'success' : resolvedConfidence >= 0.75 ? 'primary' : 'warning'}
                                                                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                                                        />
                                                                        {resolvedReasoning && (
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={(event) => openLlmReasoningPopover(event, resolvedReasoning)}
                                                                                aria-label="View LLM reasoning"
                                                                                sx={{ p: 0.25 }}
                                                                            >
                                                                                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                                            </IconButton>
                                                                        )}
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                                                                    <Chip
                                                                        label={getMatchTypeLabel(resolvedMatchType)}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color={getMatchTypeColor(resolvedMatchType)}
                                                                        sx={{ fontSize: '0.65rem' }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ py: 1, textAlign: 'center' }}>
                                                                    <Stack spacing={0.75} alignItems="center">
                                                                        {renderDecisionStatusChip(resolvedPreferenceStatus)}
                                                                        {acceptedCandidateCount > 1 && (
                                                                            <Chip
                                                                                size="small"
                                                                                label={`${acceptedCandidateCount} accepted`}
                                                                                color="success"
                                                                                variant="outlined"
                                                                                sx={{ height: 22, fontSize: '0.65rem' }}
                                                                            />
                                                                        )}
                                                                        <Stack direction="row" spacing={0.5} justifyContent="center">
                                                                            <IconButton
                                                                                size="small"
                                                                                color="success"
                                                                                onClick={() => handleAcceptValidatedMatch(effectiveMatch)}
                                                                                title="Accept and store as user preference"
                                                                                sx={resolvedPreferenceStatus === 'accepted' ? { bgcolor: alpha(theme.palette.success.main, 0.12) } : undefined}
                                                                            >
                                                                                <AcceptIcon fontSize="small" />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleRejectMatch(effectiveMatch)}
                                                                                title="Reject and move to Gaps"
                                                                            >
                                                                                <RejectIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Stack>
                                                                    </Stack>
                                                                </TableCell>
                                                            </>
                                                        );
                                                    })()}
                                                </TableRow>
                                            )})}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>

                            {/* LLM Invalidated Matches section removed as it's now merged into Gaps for unified interaction */}

                            <Grid item xs={12}>
                                <Typography variant="h6" fontWeight={700} color="warning.dark" mb={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <WarningIcon sx={{ mr: 1 }} /> Gap Columns - Action Required ({gapView.gap_count ?? gapView.gaps?.length ?? 0})

                                </Typography>
                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxHeight: 500, overflow: 'auto' }}>
                                    <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, width: '30%' }}>Logical Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: '38%' }}>Physical Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: '14%' }}>Used in Layout</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: '18%' }}>User Preference</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {gapView.gaps?.map((g, i) => {
                                                const rowKey = g.legacy_key || `gap-row-${i}`;
                                                const decision = decidedGaps[rowKey];
                                                const rowBg = decision === 'accepted' || decision === 'corrected'
                                                    ? alpha(theme.palette.success.main, 0.08)
                                                    : decision === 'rejected'
                                                        ? alpha(theme.palette.error.main, 0.08)
                                                        : (i % 2 !== 0 ? theme.palette.grey[50] : 'white');

                                                return (
                                                    <TableRow
                                                        key={rowKey}
                                                        sx={{
                                                            bgcolor: rowBg,
                                                            transition: 'background-color 0.3s ease'
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                            {renderLogicalNameWithTypeTooltip(
                                                                g.legacy_column,
                                                                g.column_type,
                                                                <Typography variant="body2" fontWeight={600} sx={tableCellWrapSx}>
                                                                    {getLegacyLogicalDisplayName(g.legacy_column, g.legacy_physical)}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', ...tableCellWrapSx }}>
                                                                {getGapPhysicalName(g)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                                                            {renderUsedInLayoutChip(g.legacy_report_layout ?? g.report_layout)}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                            <Typography variant="body2" sx={tableCellWrapSx}>
                                                                {getGapUserPreferenceLabel(g, decision)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            {(!gapView.gaps || gapView.gaps.length === 0) && (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                                        <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1, display: 'block', mx: 'auto' }} />
                                                        <Typography>No functional gaps detected!</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" fontWeight={700} color="primary.dark" mb={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <InfoOutlinedIcon sx={{ mr: 1 }} /> Filter, Join, Calculation and SQL Expressions ({expressionFieldsView.length})
                                </Typography>
                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxHeight: 500, overflow: 'auto' }}>
                                    <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, width: '22%' }}>Logical Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: '22%' }}>Physical Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: '12%' }}>Used in Layout</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: '12%' }}>Type</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: '32%' }}>Expression</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {expressionFieldsView.map((field, i) => (
                                                <TableRow
                                                    key={field.key}
                                                    sx={{ bgcolor: i % 2 !== 0 ? theme.palette.grey[50] : 'white' }}
                                                >
                                                    <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                        {renderLogicalNameWithTypeTooltip(
                                                            field.logicalName,
                                                            field.columnType,
                                                            <Typography variant="body2" fontWeight={600} sx={tableCellWrapSx}>
                                                                {field.logicalName || '-'}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', ...tableCellWrapSx }}>
                                                            {field.physicalName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                                                        {renderUsedInLayoutChip(field.usedInLayout)}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                            <Chip
                                                                label={field.type}
                                                                size="small"
                                                                color={field.type === 'Calculation' ? 'secondary' : field.type === 'Join' ? 'info' : field.type === 'SQL' ? 'default' : 'warning'}
                                                                variant="outlined"
                                                                sx={{ fontWeight: 700 }}
                                                            />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', ...tableCellWrapSx }}>
                                                            {field.expression}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {expressionFieldsView.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                        <Typography>No filter, join, calculation, or SQL expressions found.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" fontWeight={700} color="secondary.main" mb={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <InfoOutlinedIcon sx={{ mr: 1 }} /> Common and PII Fields Removed ({formatCount(removedCommonPiiCount)})
                                </Typography>
                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, maxHeight: 500, overflow: 'auto' }}>
                                    <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, width: '12%' }}>Source</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: '12%' }}>Category</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: '28%' }}>Logical Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: '30%' }}>Physical Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: '18%' }}>Used in Layout</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {removedCommonPiiFieldsView.map((field, i) => (
                                                <TableRow
                                                    key={field.key}
                                                    sx={{ bgcolor: i % 2 !== 0 ? theme.palette.grey[50] : 'white' }}
                                                >
                                                    <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                        <Typography variant="body2" fontWeight={600} sx={tableCellWrapSx}>
                                                            {field.source}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                        <Chip
                                                            label={field.category}
                                                            size="small"
                                                            color={field.category === 'PII' ? 'warning' : 'default'}
                                                            variant="outlined"
                                                            sx={{ fontWeight: 700 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                        {renderLogicalNameWithTypeTooltip(
                                                            field.logicalName,
                                                            field.columnType,
                                                            <Typography variant="body2" fontWeight={600} sx={tableCellWrapSx}>
                                                                {field.logicalName}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, ...tableCellWrapSx }}>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', ...tableCellWrapSx }}>
                                                            {field.physicalName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                                                        {renderUsedInLayoutChip(field.usedInLayout)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {removedCommonPiiFieldsView.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                        <Typography>No common or PII fields were removed.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        </Grid>
                    </Box>
                </Fade>
            )}

            {/* Step 5: Recommendations */}
            {activeStep === 5 && recommendations && (
                <Fade in>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={() => setActiveStep(4)} sx={{ mr: 2 }}><ArrowBackIcon /></IconButton>
                                <Box>
                                    <Typography variant="h5" fontWeight={700}>Recommendations</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        For {recommendations.legacy_report} vs {recommendations.catalyst_report}
                                    </Typography>
                                </Box>
                            </Box>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                  <Button
                                      variant="outlined"
                                      color="primary"
                                      startIcon={recommending ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                                      onClick={handleRefreshRecommendations}
                                      disabled={recommending || !recommendations || !gapView}
                                      sx={{ borderRadius: 3, fontWeight: 700 }}
                                  >
                                      {recommending ? 'Refreshing...' : 'Refresh Recommendations'}
                                  </Button>
                                  <Button
                                      variant="outlined"
                                      color="error"
                                      startIcon={<DeleteIcon />}
                                      onClick={handleDeleteRecommendations}
                                      disabled={!recommendations}
                                      sx={{ borderRadius: 3, fontWeight: 700 }}
                                  >
                                      Delete Recommendations
                                  </Button>
                                  <Button
                                      variant="outlined"
                                      color="success"
                                      startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
                                    onClick={handleExportRecommendations}
                                    disabled={exporting || !recommendations}
                                    sx={{ borderRadius: 3, fontWeight: 700 }}
                                >
                                    {exporting ? 'Exporting...' : 'Download Recommendations'}
                                </Button>
                            </Box>
                        </Box>

                        {renderTelemetryPanel(recommendations.telemetry, 'Recommendation Telemetry')}

                        <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {getRecommendationStartedAt(activeRecommendationJob, recommendations) && (
                                    <Chip label={`Started: ${formatRecommendationJobTimestamp(getRecommendationStartedAt(activeRecommendationJob, recommendations))}`} variant="outlined" />
                                )}
                                {getRecommendationCompletedAt(activeRecommendationJob, recommendations) && (
                                    <Chip label={`Completed: ${formatRecommendationJobTimestamp(getRecommendationCompletedAt(activeRecommendationJob, recommendations))}`} variant="outlined" />
                                )}
                            </Stack>
                        </Paper>

                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={2.5}>
                                        <Autocomplete
                                            fullWidth
                                            size="small"
                                            openOnFocus
                                            options={recommendationTableOptions}
                                            value={recommendationTableFilter || null}
                                            onChange={(event, newValue) => setRecommendationTableFilter(newValue || '')}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            renderInput={(params) => <TextField {...params} label="Table" placeholder="Select table..." />}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2.5}>
                                        <Autocomplete
                                            fullWidth
                                            size="small"
                                            openOnFocus
                                            options={recommendationColumnOptions}
                                            value={recommendationColumnFilter || null}
                                            onChange={(event, newValue) => setRecommendationColumnFilter(newValue || '')}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            renderInput={(params) => <TextField {...params} label="Column" placeholder="Select column..." />}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                value={recommendationStatusFilter}
                                                onChange={(event) => setRecommendationStatusFilter(event.target.value)}
                                                label="Status"
                                            >
                                                <MenuItem value="">All Status</MenuItem>
                                                <MenuItem value="approved">Approved</MenuItem>
                                                <MenuItem value="review">Pending Review</MenuItem>
                                                <MenuItem value="none">No Match</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={2.5}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Stage</InputLabel>
                                            <Select
                                                value={recommendationStageFilter}
                                                onChange={(event) => setRecommendationStageFilter(event.target.value)}
                                                label="Stage"
                                            >
                                                <MenuItem value="">All Stages</MenuItem>
                                                {RECOMMENDATION_STAGE_ORDER.map((stage) => (
                                                    <MenuItem key={stage.key} value={stage.key}>{stage.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={2.5} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {filteredRecommendationRowsView.length} rows shown
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell sx={{ fontWeight: 700, width: '24%' }}>Logical Name</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: '18%' }}>Physical Name</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: '58%' }}>Target Column</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRecommendationRowsView.map(({ rec, idx, presentation }) => {
                                        const physicalName = getRecommendationLegacyPhysicalDisplayName(rec);
                                        return (
                                            <TableRow key={`${rec.legacy_key || rec.legacy_column}-${idx}`} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                                <TableCell sx={{ borderRight: '1px solid #eee', py: 2 }}>
                                                    <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ wordBreak: 'break-all' }}>
                                                        {getLegacyLogicalDisplayName(rec.legacy_column, rec.legacy_physical)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #eee', py: 2 }}>
                                                    <Typography variant="body2" color={physicalName === 'No physical name' ? 'text.secondary' : 'text.primary'} sx={{ wordBreak: 'break-all' }}>
                                                        {physicalName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #eee', py: 1 }}>
                                                    {renderRecommendationTargetCell(rec, presentation)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {filteredRecommendationRowsView.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No recommendation rows match the current filters.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Fade>
            )}
            {/* LLM Processing Overlay */}
            {runningLlm && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        color: 'white'
                    }}
                >
                    <Paper
                        elevation={6}
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            bgcolor: 'background.paper'
                        }}
                    >
                        <CircularProgress size={60} thickness={4} color="primary" />
                        <Typography variant="h6" fontWeight={600} color="text.primary">
                            AI Validation in Progress
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Automatically validating matches & recovering gaps.<br />
                            This may take 30-60 seconds...
                        </Typography>
                    </Paper>
                </Box>
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Gap Analysis Details Dialog */}
            <Dialog
                open={gapAnalysisInfoDialogOpen}
                onClose={() => setGapAnalysisInfoDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Gap Analysis Details</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={700}>Legacy Report</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {gapView?.legacy_report || selectedLegacyReport?.report_name || '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                            Path
                                        </Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {gapAnalysisLegacyReportPath || '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                            Global Themes
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            {renderCompareThemeChips(gapAnalysisLegacyGlobalThemes, 'secondary')}
                                        </Box>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={700}>Catalyst Report</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {gapView?.catalyst_report || selectedMatch?.catalyst_report_name || '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                            Path
                                        </Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {gapAnalysisCatalystReportPath || '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                            Global Themes
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            {renderCompareThemeChips(gapAnalysisCatalystGlobalThemes, 'info')}
                                        </Box>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGapAnalysisInfoDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={manualOverrideDialog.open}
                onClose={manualOverrideDialog.saving ? undefined : closeRecommendationManualOverrideDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Manual Override</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.25} sx={{ mt: 0.5 }}>
                        <Box>
                            <Typography variant="body2" fontWeight={700}>
                                {manualOverrideDialog.gap?.legacy_column || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {formatPhysicalNameWithTable(
                                    manualOverrideDialog.gap?.legacy_physical,
                                    manualOverrideDialog.gap?.legacy_table,
                                ) || 'No physical name'}
                            </Typography>
                        </Box>

                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={manualOverrideDialog.layer}
                            onChange={(event, nextLayer) => {
                                if (nextLayer && nextLayer !== manualOverrideDialog.layer) {
                                    handleManualOverrideLayerChange(nextLayer);
                                }
                            }}
                        >
                            <ToggleButton value="L2">L2</ToggleButton>
                            <ToggleButton value="L3">L3</ToggleButton>
                        </ToggleButtonGroup>

                        {manualOverrideDialog.error && (
                            <Alert severity="error" sx={{ py: 0.75 }}>
                                {manualOverrideDialog.error}
                            </Alert>
                        )}

                        <Autocomplete
                            fullWidth
                            size="small"
                            loading={manualOverrideDialog.loadingTables}
                            options={manualOverrideDialog.tables}
                            value={manualOverrideDialog.table}
                            onChange={(event, value) => handleManualOverrideTableChange(value)}
                            getOptionLabel={(option) => option?.table_name || ''}
                            isOptionEqualToValue={(option, value) => option?.table_name === value?.table_name}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Table"
                                    placeholder="Search table..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {manualOverrideDialog.loadingTables ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <Autocomplete
                            fullWidth
                            size="small"
                            loading={manualOverrideDialog.loadingColumns}
                            options={manualOverrideDialog.columns}
                            value={manualOverrideDialog.column}
                            onChange={(event, value) => setManualOverrideDialog((prev) => ({ ...prev, column: value }))}
                            getOptionLabel={(option) => {
                                const physical = option?.column_name || '';
                                const logical = option?.attribute_name || '';
                                return logical && logical !== physical ? `${physical} - ${logical}` : physical;
                            }}
                            isOptionEqualToValue={(option, value) => (
                                option?.table_name === value?.table_name && option?.column_name === value?.column_name
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Column"
                                    placeholder={manualOverrideDialog.table ? 'Search column...' : 'Select a table first'}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {manualOverrideDialog.loadingColumns ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            disabled={!manualOverrideDialog.table}
                        />

                        {manualOverrideDialog.column && (
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                <Typography variant="body2" fontWeight={700}>
                                    {manualOverrideDialog.table?.table_name}.{manualOverrideDialog.column?.column_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Attribute: {manualOverrideDialog.column?.attribute_name || '-'} | Data Type: {manualOverrideDialog.column?.data_type || '-'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Definition: {manualOverrideDialog.column?.definition || '-'}
                                </Typography>
                            </Paper>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRecommendationManualOverrideDialog} disabled={manualOverrideDialog.saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={manualOverrideDialog.saving ? <CircularProgress size={16} color="inherit" /> : <AcceptIcon />}
                        onClick={handleSaveRecommendationManualOverride}
                        disabled={manualOverrideDialog.saving || !manualOverrideDialog.table || !manualOverrideDialog.column}
                    >
                        Accept Override
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(selectedRecommendation)}
                onClose={() => setSelectedRecommendation(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Recommendation Details</DialogTitle>
                <DialogContent dividers>
                    {selectedRecommendation && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Legacy Column</Typography>
                            <Typography variant="body2">{selectedRecommendation.gap.legacy_column}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Physical: {formatPhysicalNameWithTable(selectedRecommendation.gap.legacy_physical, selectedRecommendation.gap.legacy_table) || '-'} | Type: {selectedRecommendation.gap.column_type || 'base'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                Expression: {selectedRecommendation.gap.legacy_expression || '-'}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Catalyst Candidate</Typography>
                            {getRecommendationCandidateReportLabel(selectedRecommendation.candidate) && (
                                <Typography variant="body2" fontWeight={700}>
                                    {getRecommendationCandidateReportLabel(selectedRecommendation.candidate)}
                                </Typography>
                            )}
                            <Typography variant="body2">{getRecommendationCandidateFieldLabel(selectedRecommendation.candidate)}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Table: {selectedRecommendation.candidate.catalyst_table || '-'} | Physical: {selectedRecommendation.candidate.catalyst_physical || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Attribute: {selectedRecommendation.candidate.catalyst_attribute || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Data Type: {selectedRecommendation.candidate.catalyst_data_type || '-'} | Layer: {selectedRecommendation.candidate.catalyst_layer || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                Definition: {selectedRecommendation.candidate.catalyst_definition || '-'}
                            </Typography>
                            {isSelectedTargetReportRecommendationCandidate(selectedRecommendation.candidate) && (
                                <Chip label="Selected Target Report" size="small" color="primary" variant="outlined" sx={{ mb: 1, mr: 1 }} />
                            )}
                            {isRecommendationFallbackCandidate(selectedRecommendation.candidate) && (
                                <Chip label="Fallback" size="small" color="warning" variant="outlined" sx={{ mb: 2 }} />
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>LLM Validation</Typography>
                            <Typography variant="body2">
                                {selectedRecommendation.candidate.llm_validated
                                    ? (selectedRecommendation.candidate.llm_is_equivalent ? 'Equivalent' : 'Not Equivalent')
                                    : 'Not Validated'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Confidence: {Math.round((selectedRecommendation.candidate.llm_confidence || 0) * 100)}%
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {selectedRecommendation.candidate.llm_reasoning || 'No reasoning provided.'}
                            </Typography>
                            {selectedRecommendation.candidate.llm_concerns?.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">Concerns:</Typography>
                                    <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                                        {selectedRecommendation.candidate.llm_concerns.map((c, i) => (
                                            <li key={i}>
                                                <Typography variant="caption" color="text.secondary">{c}</Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedRecommendation(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Unclassified Report Details Dialog */}
            <Dialog
                open={classificationDialogOpen}
                onClose={() => setClassificationDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    Unclassified Report
                    {classificationDialogReport ? `: ${classificationDialogReport}` : ''}
                </DialogTitle>
                <DialogContent dividers>
                    {classificationDialogLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                    Available Columns ({formatCount(classificationDialogColumns.length)})
                                </Typography>
                                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Logical Name</TableCell>
                                                <TableCell>Physical Name</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {classificationDialogColumns.map((col, idx) => (
                                                <TableRow key={`${col.logical}-${col.physical}-${idx}`}>
                                                    <TableCell sx={tableCellWrapSx}>{col.logical || '-'}</TableCell>
                                                    <TableCell sx={tableCellWrapSx}>{col.physical || '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                            {classificationDialogColumns.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                                                        <Typography variant="body2" color="text.secondary">No columns found.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                    Expected Theme Keywords ({classificationMode === 'global' ? 'Global' : 'Local'})
                                </Typography>
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                    <InputLabel id="theme-keyword-select-label">Theme</InputLabel>
                                    <Select
                                        labelId="theme-keyword-select-label"
                                        value={selectedKeywordTheme}
                                        label="Theme"
                                        onChange={(event) => setSelectedKeywordTheme(event.target.value)}
                                    >
                                        {Object.keys(getKeywordMapForMode())
                                            .sort((a, b) => a.localeCompare(b))
                                            .map((themeName) => (
                                            <MenuItem key={themeName} value={themeName}>{themeName}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {selectedKeywordTheme ? (
                                    <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto', p: 2 }}>
                                        <Typography variant="body2" fontWeight={600} gutterBottom>
                                            {selectedKeywordTheme} Keywords
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {(getKeywordMapForMode()[selectedKeywordTheme] || []).map((kw) => (
                                                <Chip key={kw} label={kw} size="small" />
                                            ))}
                                            {(getKeywordMapForMode()[selectedKeywordTheme] || []).length === 0 && (
                                                <Typography variant="body2" color="text.secondary">No keywords configured.</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                ) : (
                                    <Alert severity="info">No themes available.</Alert>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClassificationDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={compareDialogOpen}
                onClose={closeCompareDialog}
                fullScreen
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, pr: 6 }}>
                    <Button
                        onClick={closeCompareDialog}
                        startIcon={<ArrowBackIcon />}
                        variant="text"
                        size="small"
                        sx={{ minWidth: 'auto', px: 0.5 }}
                    >
                        Back
                    </Button>
                    <Typography variant="h6" component="span" fontWeight={700}>
                        Compare Legacy vs Catalyst Report
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3}>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} md={5}>
                                <Autocomplete
                                    options={compareLegacyOptions}
                                    getOptionLabel={(opt) => opt?.report_name || ''}
                                    value={compareLegacyReport}
                                    onChange={(event, value) => {
                                        setCompareLegacyReport(value);
                                        setCompareReportData(null);
                                    }}
                                    isOptionEqualToValue={(option, value) => option?.report_name === value?.report_name}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Legacy Report"
                                            placeholder="Search legacy reports..."
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Autocomplete
                                    options={compareCatalystOptions}
                                    getOptionLabel={(opt) => opt?.report_name || ''}
                                    value={compareCatalystReport}
                                    onChange={(event, value) => {
                                        setCompareCatalystReport(value);
                                        setCompareReportData(null);
                                    }}
                                    isOptionEqualToValue={(option, value) => option?.report_name === value?.report_name}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Catalyst Report"
                                            placeholder="Search catalyst reports..."
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={<CompareArrowsIcon />}
                                    sx={{ height: 56 }}
                                    disabled={!selectedWorkspace?.id || !compareLegacyReport?.report_name || !compareCatalystReport?.report_name || compareReportLoading}
                                    onClick={() => handleLoadReportComparison()}
                                >
                                    Compare
                                </Button>
                            </Grid>
                        </Grid>

                        {compareReportLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        {!compareReportLoading && !compareReportData && (
                            <Alert severity="info">
                                Select one legacy report and one Catalyst report, then click Compare.
                            </Alert>
                        )}

                        {!compareReportLoading && compareReportData && (
                            <Stack spacing={2}>
                                <Tabs
                                    value={compareViewTab}
                                    onChange={(event, value) => setCompareViewTab(value)}
                                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                                >
                                    <Tab value="inventory" label="Inventory" />
                                    <Tab value="diagnostics" label="Matching Diagnostics" />
                                </Tabs>

                                {compareViewTab === 'inventory' ? (
                                    <Grid container spacing={3}>
                                        {renderCompareReportPanel(compareReportData.legacy_report, 'Legacy Report')}
                                        {renderCompareReportPanel(compareReportData.catalyst_report, 'Catalyst Report')}
                                    </Grid>
                                ) : renderCompareDiagnostics()}
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions />
            </Dialog>

            {/* Matching Configuration Dialog */}
            <MatchingOptionsDialog
                open={isConfigOpen}
                onClose={() => setIsConfigOpen(false)}
                config={{
                    topN,
                    customTopN,
                    standardOtherThemeTopN,
                    finalMatchThreshold,
                    sameThemeOnly,
                    globalThemeOnly,
                    ignoreCommon,
                    ignorePii,
                    includeCustom,
                }}
                onChange={(key, value) => {
                    if (key === 'topN') setTopN(value);
                    else if (key === 'customTopN') setCustomTopN(value);
                    else if (key === 'standardOtherThemeTopN') setStandardOtherThemeTopN(value);
                    else if (key === 'finalMatchThreshold') setFinalMatchThreshold(value);
                    else if (key === 'sameThemeOnly') {
                        setSameThemeOnly(value);
                        if (value && globalThemeOnly) {
                            showNotification('Both theme filters enabled. Global themes will be used.', 'warning');
                        }
                    } else if (key === 'globalThemeOnly') {
                        setGlobalThemeOnly(value);
                        if (value && sameThemeOnly) {
                            showNotification('Both theme filters enabled. Global themes will be used.', 'warning');
                        }
                    }
                    else if (key === 'ignoreCommon') setIgnoreCommon(value);
                    else if (key === 'ignorePii') setIgnorePii(value);
                    else if (key === 'includeCustom') setIncludeCustom(value);
                }}
            />

            <Popover
                open={Boolean(llmReasoningPopover.anchorEl)}
                anchorEl={llmReasoningPopover.anchorEl}
                onClose={closeLlmReasoningPopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        p: 2,
                        maxWidth: 420,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    },
                }}
            >
                <Typography variant="body2">
                    {llmReasoningPopover.content}
                </Typography>
            </Popover>
        </Box>
    );
}

export default ReportMatchingPage;
